import arcToCurve from 'svg-arc-to-cubic-bezier';

function injectInspector(ctor, def) {
  if (def === void 0) def = [];

  if (process.env.NODE_ENV === "production") {
    return;
  }

  var _InspectorImpl = /*@__PURE__*/function () {
    function InspectorImpl(obj) {
      Object.defineProperty(this, "__obj", {
        value: obj
      });

      if (typeof obj !== "object") {
        return this;
      }

      return new Proxy(this, {
        get: function get(target, key) {
          var value = Reflect.has(target, key) ? Reflect.get(target, key) : obj[key];
          return inspect(value);
        },
        ownKeys: function ownKeys() {
          return Object.getOwnPropertyNames(obj).filter(function (key) {
            return !InspectorImpl.blackList.has(key) && !key.startsWith("_") && typeof obj[key] !== "function";
          }).concat("__obj");
        },
        getOwnPropertyDescriptor: function getOwnPropertyDescriptor(target, key) {
          if (key === "__obj") {
            return Reflect.getOwnPropertyDescriptor(target, key);
          }

          return Object.assign({}, Reflect.getOwnPropertyDescriptor(obj, key), {
            configurable: true
          });
        }
      });
    }

    return InspectorImpl;
  }();

  var InspectorImpl = _InspectorImpl;
  InspectorImpl.blackList = /* @__PURE__ */new Set();
  var ParentInspector = getInspector(ctor.prototype);

  if (ParentInspector) {
    Object.defineProperties(InspectorImpl.prototype, Object.getOwnPropertyDescriptors(ParentInspector.prototype));
    InspectorImpl.prototype.constructor = InspectorImpl;
    InspectorImpl.blackList = new Set(ParentInspector.blackList);
  }

  def.forEach(function (rule) {
    var dest = Array.isArray(rule) ? rule[0] : rule;
    var src = Array.isArray(rule) ? rule[1] : rule;

    if (src === false) {
      InspectorImpl.blackList.add(dest);
      return;
    }

    Object.defineProperty(InspectorImpl.prototype, dest, {
      configurable: true,
      enumerable: true,
      get: function get() {
        return typeof src === "function" ? src.call(this.__obj, this.__obj) : this.__obj[src];
      }
    });
  });
  ctor.Inspector = InspectorImpl;
}
function inspect(obj) {
  if (Array.isArray(obj)) {
    return obj.map(inspect);
  }

  var Inspector = getInspector(obj);

  if (Inspector) {
    return new Inspector(obj);
  }

  return obj;
}

function getInspector(obj) {
  if (obj === void 0 || obj === null || obj === Object.prototype) {
    return null;
  }

  if (hasInspector(obj)) {
    return obj.Inspector;
  }

  var ctor = obj.constructor;

  if (hasInspector(ctor)) {
    return ctor.Inspector;
  }

  return getInspector(Object.getPrototypeOf(obj));
}

function hasInspector(obj) {
  return obj !== void 0 && obj !== null && typeof obj.Inspector === "function";
}

var Versioning = /*@__PURE__*/function () {
  function Versioning() {
    this._id = 1;
    this._updateListeners = [];
    this._updated = this._updated.bind(this);
  }

  Versioning.prototype._addUpdateListener = function _addUpdateListener(listener) {
    this._updateListeners.push(listener);
  };

  Versioning.prototype._removeUpdateListener = function _removeUpdateListener(listener) {
    var index = this._updateListeners.indexOf(listener);

    if (index >= 0) {
      this._updateListeners.splice(index, 1);
    }
  };

  Versioning.prototype._updated = function _updated() {
    this._id += 1;

    for (var i = 0, list = this._updateListeners; i < list.length; i += 1) {
      var listener = list[i];
      listener();
    }
  };

  return Versioning;
}();
function dep(target, key) {
  var storageKey = "_" + key;
  return defineProperty(target, key, {
    enumerable: true,
    get: function get() {
      return this[storageKey];
    },
    set: function set(value) {
      if (this[storageKey] === value) {
        return;
      }

      this[storageKey] = value;

      this._updated();
    }
  });
}

function defineProperty(target, key, descriptor) {
  var newDescriptor = Object.assign({}, {
    configurable: true
  }, descriptor);
  Object.defineProperty(target, key, newDescriptor);
  return newDescriptor;
}

var Point = /*@__PURE__*/function () {
  function Point(x, y) {
    if (x === void 0) x = 0;
    if (y === void 0) y = 0;
    this.x = x;
    this.y = y;
  }

  Point.prototype.from = function from(point) {
    this.x = point.x;
    this.y = point.y;
    return this;
  };

  Point.prototype.set = function set(x, y) {
    this.x = x;
    this.y = y;
    return this;
  };

  Point.prototype.negate = function negate() {
    this.x = -this.x;
    this.y = -this.y;
    return this;
  };

  Point.prototype.add = function add(x, y) {
    if (y === void 0) y = x;
    this.x += x;
    this.y += y;
    return this;
  };

  Point.prototype.multiply = function multiply(x, y) {
    if (y === void 0) y = x;
    this.x *= x;
    this.y *= y;
    return this;
  };

  Point.prototype.cross = function cross(point) {
    return this.x * point.y - this.y * point.x;
  };

  Point.prototype.dot = function dot(point) {
    return this.x * point.x + this.y * point.y;
  };

  Point.prototype.angleBetween = function angleBetween(point) {
    return Math.atan2(this.cross(point), this.dot(point)) * 180 / Math.PI;
  };

  Point.prototype.distanceBetween = function distanceBetween(point) {
    var dx = point.x - this.x;
    var dy = point.y - this.y;
    return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
  };

  Point.prototype.clone = function clone() {
    return new Point(this.x, this.y);
  };

  return Point;
}();
injectInspector(Point);

var _Matrix = /*@__PURE__*/function (Versioning) {
  function Matrix(a, b, c, d, tx, ty) {
    if (a === void 0) a = 1;
    if (b === void 0) b = 0;
    if (c === void 0) c = 0;
    if (d === void 0) d = 1;
    if (tx === void 0) tx = 0;
    if (ty === void 0) ty = 0;
    Versioning.call(this);
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.tx = tx;
    this.ty = ty;
  }

  if (Versioning) Matrix.__proto__ = Versioning;
  Matrix.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  Matrix.prototype.constructor = Matrix;
  var prototypeAccessors = {
    matrix: {
      configurable: true
    }
  };

  Matrix.fromMatrix = function fromMatrix(m) {
    return new Matrix(m.a, m.b, m.c, m.d, m.tx, m.ty);
  };

  prototypeAccessors.matrix.get = function matrix() {
    return this;
  };

  Matrix.prototype.set = function set(a, b, c, d, tx, ty) {
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.tx = tx;
    this.ty = ty;

    this._updated();

    return this;
  };

  Matrix.prototype.translate = function translate(x, y) {
    this.tx += x;
    this.ty += y;

    this._updated();

    return this;
  };

  Matrix.prototype.scale = function scale(sx, sy) {
    this.a *= sx;
    this.d *= sy;
    this.c *= sx;
    this.b *= sy;
    this.tx *= sx;
    this.ty *= sy;

    this._updated();

    return this;
  };

  Matrix.prototype.scaleInSitu = function scaleInSitu(sx, sy) {
    this.a *= sx;
    this.d *= sy;
    this.c *= sx;
    this.b *= sy;

    this._updated();

    return this;
  };

  Matrix.prototype.rotate = function rotate(theta) {
    if (theta === 0) {
      return this;
    }

    var cos = Math.cos(theta);
    var sin = Math.sin(theta);
    var ref = this;
    var a = ref.a;
    var c = ref.c;
    var tx = ref.tx;
    this.a = a * cos - this.b * sin;
    this.b = a * sin + this.b * cos;
    this.c = c * cos - this.d * sin;
    this.d = c * sin + this.d * cos;
    this.tx = tx * cos - this.ty * sin;
    this.ty = tx * sin + this.ty * cos;

    this._updated();

    return this;
  };

  Matrix.prototype.append = function append(matrix) {
    var ref = this;
    var a = ref.a;
    var b = ref.b;
    var c = ref.c;
    var d = ref.d;
    this.a = matrix.a * a + matrix.b * c;
    this.b = matrix.a * b + matrix.b * d;
    this.c = matrix.c * a + matrix.d * c;
    this.d = matrix.c * b + matrix.d * d;
    this.tx = matrix.tx * a + matrix.ty * c + this.tx;
    this.ty = matrix.tx * b + matrix.ty * d + this.ty;

    this._updated();

    return this;
  };

  Matrix.prototype.prepend = function prepend(matrix) {
    var ref = this;
    var a = ref.a;
    var c = ref.c;
    var tx = ref.tx;

    if (matrix.a !== 1 || matrix.b !== 0 || matrix.c !== 0 || matrix.d !== 1) {
      this.a = a * matrix.a + this.b * matrix.c;
      this.b = a * matrix.b + this.b * matrix.d;
      this.c = c * matrix.a + this.d * matrix.c;
      this.d = c * matrix.b + this.d * matrix.d;
    }

    this.tx = tx * matrix.a + this.ty * matrix.c + matrix.tx;
    this.ty = tx * matrix.b + this.ty * matrix.d + matrix.ty;

    this._updated();

    return this;
  };

  Matrix.prototype.identity = function identity() {
    this.a = 1;
    this.b = 0;
    this.c = 0;
    this.d = 1;
    this.tx = 0;
    this.ty = 0;

    this._updated();

    return this;
  };

  Matrix.prototype.invert = function invert() {
    var ref = this;
    var a = ref.a;
    var b = ref.b;
    var c = ref.c;
    var d = ref.d;
    var tx = ref.tx;
    var n = a * d - b * c;
    this.a = d / n;
    this.b = -b / n;
    this.c = -c / n;
    this.d = a / n;
    this.tx = (c * this.ty - d * tx) / n;
    this.ty = -(a * this.ty - b * tx) / n;

    this._updated();

    return this;
  };

  Matrix.prototype.copyFrom = function copyFrom(matrix) {
    this.a = matrix.a;
    this.b = matrix.b;
    this.c = matrix.c;
    this.d = matrix.d;
    this.tx = matrix.tx;
    this.ty = matrix.ty;

    this._updated();

    return this;
  };

  Matrix.prototype.apply = function apply(point, output) {
    if (output === void 0) output = new Point(0, 0);
    var x = point.x;
    var y = point.y;
    output.x = this.a * x + this.c * y + this.tx;
    output.y = this.b * x + this.d * y + this.ty;
    return output;
  };

  Matrix.prototype.applyInverse = function applyInverse(point, output) {
    if (output === void 0) output = new Point(0, 0);
    var x = point.x;
    var y = point.y;
    var id = 1 / (this.a * this.d + this.c * -this.b);
    output.x = this.d * id * x + -this.c * id * y + (this.ty * this.c - this.tx * this.d) * id;
    output.y = this.a * id * y + -this.b * id * x + (-this.ty * this.a + this.tx * this.b) * id;
    return output;
  };

  Object.defineProperties(Matrix.prototype, prototypeAccessors);
  return Matrix;
}(Versioning);

var Matrix = _Matrix;
Matrix.Identity = /*#__PURE__*/Object.freeze( /*#__PURE__*/new _Matrix());
injectInspector(Matrix, ["a", "b", "c", "d", "tx", "ty"]);

var Transform = /*@__PURE__*/function (Versioning) {
  function Transform() {
    Versioning.call(this);
    this._matrix = new Matrix();
    this._cachedId = 1;
    this._pivotX = 0;
    this._pivotY = 0;
    this._scaleX = 1;
    this._scaleY = 1;
    this._skewX = 0;
    this._skewY = 0;
    this._rotation = 0;
    this._translateX = 0;
    this._translateY = 0;
  }

  if (Versioning) Transform.__proto__ = Versioning;
  Transform.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  Transform.prototype.constructor = Transform;
  var prototypeAccessors = {
    pivotX: {
      configurable: true
    },
    pivotY: {
      configurable: true
    },
    scaleX: {
      configurable: true
    },
    scaleY: {
      configurable: true
    },
    skewX: {
      configurable: true
    },
    skewY: {
      configurable: true
    },
    rotation: {
      configurable: true
    },
    translateX: {
      configurable: true
    },
    translateY: {
      configurable: true
    },
    matrix: {
      configurable: true
    }
  };

  prototypeAccessors.pivotX.get = function pivotX() {
    return this._pivotX;
  };

  prototypeAccessors.pivotX.set = function pivotX(value) {
    if (this._pivotX === value) {
      return;
    }

    this._pivotX = value;

    this._updated();
  };

  prototypeAccessors.pivotY.get = function pivotY() {
    return this._pivotY;
  };

  prototypeAccessors.pivotY.set = function pivotY(value) {
    if (this._pivotY === value) {
      return;
    }

    this._pivotY = value;

    this._updated();
  };

  prototypeAccessors.scaleX.get = function scaleX() {
    return this._scaleX;
  };

  prototypeAccessors.scaleX.set = function scaleX(value) {
    if (this._scaleX === value) {
      return;
    }

    this._scaleX = value;

    this._updated();
  };

  prototypeAccessors.scaleY.get = function scaleY() {
    return this._scaleY;
  };

  prototypeAccessors.scaleY.set = function scaleY(value) {
    if (this._scaleY === value) {
      return;
    }

    this._scaleY = value;

    this._updated();
  };

  prototypeAccessors.skewX.get = function skewX() {
    return this._skewX;
  };

  prototypeAccessors.skewX.set = function skewX(value) {
    if (this._skewX === value) {
      return;
    }

    this._skewX = value;

    this._updated();
  };

  prototypeAccessors.skewY.get = function skewY() {
    return this._skewY;
  };

  prototypeAccessors.skewY.set = function skewY(value) {
    if (this._skewY === value) {
      return;
    }

    this._skewY = value;

    this._updated();
  };

  prototypeAccessors.rotation.get = function rotation() {
    return this._rotation;
  };

  prototypeAccessors.rotation.set = function rotation(value) {
    if (this._rotation === value) {
      return;
    }

    this._rotation = value;

    this._updated();
  };

  prototypeAccessors.translateX.get = function translateX() {
    return this._translateX;
  };

  prototypeAccessors.translateX.set = function translateX(value) {
    if (this._translateX === value) {
      return;
    }

    this._translateX = value;

    this._updated();
  };

  prototypeAccessors.translateY.get = function translateY() {
    return this._translateY;
  };

  prototypeAccessors.translateY.set = function translateY(value) {
    if (this._translateY === value) {
      return;
    }

    this._translateY = value;

    this._updated();
  };

  prototypeAccessors.matrix.get = function matrix() {
    if (this._cachedId !== this._id) {
      this._cachedId = this._id;
      var cx = Math.cos(this.rotation + this.skewY);
      var sx = Math.sin(this.rotation + this.skewY);
      var cy = -Math.sin(this.rotation - this.skewX);
      var sy = Math.cos(this.rotation - this.skewX);
      var a = cx * this.scaleX;
      var b = sx * this.scaleX;
      var c = cy * this.scaleY;
      var d = sy * this.scaleY;
      var tx = this.translateX - (this.pivotX * a + this.pivotY * c);
      var ty = this.translateY - (this.pivotX * b + this.pivotY * d);

      this._matrix.set(a, b, c, d, tx, ty);
    }

    return this._matrix;
  };

  Object.defineProperties(Transform.prototype, prototypeAccessors);
  return Transform;
}(Versioning);
var WorldTransform = /*@__PURE__*/function (Matrix) {
  function WorldTransform(transform) {
    Matrix.call(this);
    this._parent = null;
    this._cachedTransformId = 0;
    this._cachedParentId = 0;
    this.transform = transform;
  }

  if (Matrix) WorldTransform.__proto__ = Matrix;
  WorldTransform.prototype = /*#__PURE__*/Object.create(Matrix && Matrix.prototype);
  WorldTransform.prototype.constructor = WorldTransform;

  WorldTransform.prototype.setParent = function setParent(value) {
    if (this._parent === value) {
      return;
    }

    this._parent = value;
    this._cachedTransformId = 0;
    this._cachedParentId = 0;
  };

  WorldTransform.prototype.update = function update() {
    var selfChanged = this._cachedTransformId !== this.transform._id;
    var parentChanged = this._parent && this._cachedParentId !== this._parent._id;

    if (selfChanged || parentChanged) {
      this.copyFrom(this.transform.matrix);
      this._cachedTransformId = this.transform._id;

      if (this._parent) {
        this.prepend(this._parent);
        this._cachedParentId = this._parent._id;
      }
    }
  };

  WorldTransform.prototype.updateRecursively = function updateRecursively() {
    var _a;

    (_a = this._parent) == null ? void 0 : _a.updateRecursively();
    this.update();
  };

  return WorldTransform;
}(Matrix);
Transform.None = /*#__PURE__*/Object.freeze( /*#__PURE__*/new Transform());
injectInspector(Transform, ["pivotX", "pivotY", "scaleX", "scaleY", "skewX", "skewY", "rotation", "translateX", "translateY"]);
injectInspector(WorldTransform, [["transform", false]]);

var Bounds = /*@__PURE__*/function (Versioning) {
  function Bounds() {
    Versioning.call(this);
    this.minX = Infinity;
    this.minY = Infinity;
    this.maxX = -Infinity;
    this.maxY = -Infinity;
  }

  if (Versioning) Bounds.__proto__ = Versioning;
  Bounds.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  Bounds.prototype.constructor = Bounds;
  var prototypeAccessors = {
    isEmpty: {
      configurable: true
    },
    x: {
      configurable: true
    },
    y: {
      configurable: true
    },
    width: {
      configurable: true
    },
    height: {
      configurable: true
    }
  };

  Bounds.isEmpty = function isEmpty(bounds) {
    return bounds.minX > bounds.maxX || bounds.minY > bounds.maxY;
  };

  prototypeAccessors.isEmpty.get = function isEmpty() {
    return Bounds.isEmpty(this);
  };

  prototypeAccessors.x.get = function x() {
    if (this.isEmpty) {
      return 0;
    }

    return this.minX;
  };

  prototypeAccessors.y.get = function y() {
    if (this.isEmpty) {
      return 0;
    }

    return this.minY;
  };

  prototypeAccessors.width.get = function width() {
    if (this.isEmpty) {
      return 0;
    }

    return this.maxX - this.minX;
  };

  prototypeAccessors.height.get = function height() {
    if (this.isEmpty) {
      return 0;
    }

    return this.maxY - this.minY;
  };

  Bounds.prototype.clear = function clear() {
    this.minX = Infinity;
    this.minY = Infinity;
    this.maxX = -Infinity;
    this.maxY = -Infinity;

    this._updated();

    return this;
  };

  Bounds.prototype.set = function set(minX, minY, maxX, maxY) {
    this.minX = minX;
    this.minY = minY;
    this.maxX = maxX;
    this.maxY = maxY;

    this._updated();

    return this;
  };

  Bounds.prototype.addPoint = function addPoint(x, y) {
    this.minX = Math.min(this.minX, x);
    this.maxX = Math.max(this.maxX, x);
    this.minY = Math.min(this.minY, y);
    this.maxY = Math.max(this.maxY, y);

    this._updated();

    return this;
  };

  Bounds.prototype.addPointMatrix = function addPointMatrix(matrix, x, y) {
    var a = matrix.a;
    var b = matrix.b;
    var c = matrix.c;
    var d = matrix.d;
    var tx = matrix.tx;
    var ty = matrix.ty;
    var mx = a * x + c * y + tx;
    var my = b * x + d * y + ty;
    return this.addPoint(mx, my);
  };

  Bounds.prototype.addFrame = function addFrame(x0, y0, x1, y1) {
    this.minX = Math.min(this.minX, x0);
    this.maxX = Math.max(this.maxX, x1);
    this.minY = Math.min(this.minY, y0);
    this.maxY = Math.max(this.maxY, y1);

    this._updated();

    return this;
  };

  Bounds.prototype.addFrameMatrix = function addFrameMatrix(matrix, x0, y0, x1, y1) {
    this.addPointMatrix(matrix, x0, y0);
    this.addPointMatrix(matrix, x1, y0);
    this.addPointMatrix(matrix, x0, y1);
    this.addPointMatrix(matrix, x1, y1);
    return this;
  };

  Bounds.prototype.addBounds = function addBounds(bounds) {
    if (Bounds.isEmpty(bounds)) {
      return this;
    }

    return this.addFrame(bounds.minX, bounds.minY, bounds.maxX, bounds.maxY);
  };

  Bounds.prototype.addBoundsMatrix = function addBoundsMatrix(matrix, bounds) {
    if (Bounds.isEmpty(bounds)) {
      return this;
    }

    return this.addFrameMatrix(matrix, bounds.minX, bounds.minY, bounds.maxX, bounds.maxY);
  };

  Bounds.prototype.addBoundsRound = function addBoundsRound() {
    this.minX = Math.floor(this.minX);
    this.maxX = Math.ceil(this.maxX);
    this.minY = Math.floor(this.minY);
    this.maxY = Math.ceil(this.maxY);
    return this;
  };

  Bounds.prototype.pad = function pad(padding) {
    this.minX -= padding.left;
    this.minY -= padding.top;
    this.maxX += padding.right;
    this.maxY += padding.bottom;

    this._updated();

    return this;
  };

  Bounds.prototype.transform = function transform(matrix) {
    var ref = this;
    var minX = ref.minX;
    var minY = ref.minY;
    var maxX = ref.maxX;
    var maxY = ref.maxY;
    return this.clear().addFrameMatrix(matrix, minX, minY, maxX, maxY);
  };

  Bounds.prototype.paddingTo = function paddingTo(target, out) {
    out.top = this.minY - target.minY;
    out.right = target.maxX - this.maxX;
    out.bottom = target.maxY - this.maxY;
    out.left = this.minX - target.minX;
    return out;
  };

  Object.defineProperties(Bounds.prototype, prototypeAccessors);
  return Bounds;
}(Versioning);
var BoundsCalculation = /*@__PURE__*/function () {
  function BoundsCalculation(bounds, transform) {
    this.value = new Bounds();
    this._cachedBoundsId = 0;
    this._cachedTransformId = 0;
    this.bounds = bounds;
    this.transform = transform;
  }

  BoundsCalculation.prototype.update = function update() {
    var boundsChanged = this._cachedBoundsId !== this.bounds._id;
    var transformChanged = this._cachedTransformId !== this.transform._id;

    if (boundsChanged || transformChanged) {
      this.value.clear().addBoundsMatrix(this.transform.matrix, this.bounds);
      this._cachedBoundsId = this.bounds._id;
      this._cachedTransformId = this.transform._id;
    }
  };

  BoundsCalculation.prototype.updateRecursively = function updateRecursively() {
    if (this.transform instanceof WorldTransform) {
      this.transform.updateRecursively();
    }

    this.update();
  };

  return BoundsCalculation;
}();
injectInspector(Bounds, ["minX", "minY", "maxX", "maxY", "x", "y", "width", "height"]);

var PI = Math.PI;
var PI_2 = PI * 2;
var RAD_TO_DEG = 180 / PI;
var DEG_TO_RAD = PI / 180;
var EPSILON = 1e-12;
var MACHINE_EPSILON = 112e-18;
var CURVETIME_EPSILON = 1e-8;
function isMachineZero(val) {
  return val >= -MACHINE_EPSILON && val <= MACHINE_EPSILON;
}

var abs = Math.abs;
function solveQuadratic(a, b, c, roots) {
  var x1;
  var x2 = Infinity;

  if (abs(a) < EPSILON) {
    if (abs(b) < EPSILON) {
      return abs(c) < EPSILON ? -1 : 0;
    }

    x1 = -c / b;
  } else {
    b *= -0.5;
    var D = getDiscriminant(a, b, c);

    if (D && abs(D) < MACHINE_EPSILON) {
      var f = getNormalizationFactor(abs(a), abs(b), abs(c));

      if (f) {
        a *= f;
        b *= f;
        c *= f;
        D = getDiscriminant(a, b, c);
      }
    }

    if (D >= -MACHINE_EPSILON) {
      var Q = D < 0 ? 0 : Math.sqrt(D);
      var R = b + (b < 0 ? -Q : Q);

      if (R === 0) {
        x1 = c / a;
        x2 = -x1;
      } else {
        x1 = R / a;
        x2 = c / R;
      }
    }
  }

  var count = 0;

  if (isFinite(x1)) {
    roots[count++] = x1;
  }

  if (x2 !== x1 && isFinite(x2)) {
    roots[count++] = x2;
  }

  return count;
}

function getDiscriminant(a, b, c) {
  function split(v) {
    var x = v * 134217729;
    var y = v - x;
    var hi = y + x;
    var lo = v - hi;
    return [hi, lo];
  }

  var D = b * b - a * c;
  var E = b * b + a * c;

  if (abs(D) * 3 < E) {
    var ad = split(a);
    var bd = split(b);
    var cd = split(c);
    var p = b * b;
    var dp = bd[0] * bd[0] - p + 2 * bd[0] * bd[1] + bd[1] * bd[1];
    var q = a * c;
    var dq = ad[0] * cd[0] - q + ad[0] * cd[1] + ad[1] * cd[0] + ad[1] * cd[1];
    D = p - q + (dp - dq);
  }

  return D;
}

function getNormalizationFactor(a, b, c) {
  var norm = Math.max(a, b, c);
  return norm && (norm < 1e-8 || norm > 1e8) ? Math.pow(2, -Math.round(Math.log2(norm))) : 0;
}

var _Color = /*@__PURE__*/function () {
  function Color(r, g, b, a, rgbaHex) {
    this.red = r;
    this.green = g;
    this.blue = b;
    this.alpha = a;
    this.hex = rgbaHex;
    this.rgba = "rgba(" + r + "," + g + "," + b + "," + a / 255 + ")";
  }

  Color.fromRGB = function fromRGB(r, g, b) {
    return this.from(r, g, b, 255);
  };

  Color.fromRGBA = function fromRGBA(r, g, b, a) {
    return this.from(r, g, b, a * 255);
  };

  Color.fromHex = function fromHex(color, a) {
    if (a === void 0) a = 1;

    if (typeof color === "string") {
      var s = color[0] === "#" ? color.slice(1) : color;
      var r2 = parse16(s.slice(0, 2));
      var g2 = parse16(s.slice(2, 4));
      var b2 = parse16(s.slice(4, 6));
      return this.from(r2, g2, b2, a * 255);
    }

    var r = color >> 16 & 255;
    var g = color >> 8 & 255;
    var b = color & 255;
    return this.from(r, g, b, a * 255);
  };

  Color.fromHex8 = function fromHex8(color) {
    if (typeof color === "string") {
      var s = color[0] === "#" ? color.slice(1) : color;
      var r2 = parse16(s.slice(0, 2));
      var g2 = parse16(s.slice(2, 4));
      var b2 = parse16(s.slice(4, 6));
      var a2 = parse16(s.slice(6, 8));
      return this.from(r2, g2, b2, a2);
    }

    var r = color >> 24 & 255;
    var g = color >> 16 & 255;
    var b = color >> 8 & 255;
    var a = color & 255;
    return this.from(r, g, b, a);
  };

  Color.dispose = function dispose() {
    this.pool = {};
  };

  Color.from = function from(r, g, b, a) {
    var cr = clamp$1(r),
        cg = clamp$1(g),
        cb = clamp$1(b),
        ca = clamp$1(a);
    var rgbaHex = "#" + numToHex(cr) + numToHex(cg) + numToHex(cb) + numToHex(ca);

    if (!this.pool[rgbaHex]) {
      this.pool[rgbaHex] = new Color(cr, cg, cb, ca, rgbaHex);
    }

    return this.pool[rgbaHex];
  };

  return Color;
}();

var Color = _Color;
Color.pool = {};
Color.Black = /*#__PURE__*/Color.fromRGB(0, 0, 0);
Color.White = /*#__PURE__*/Color.fromRGB(255, 255, 255);
Color.Transparent = /*#__PURE__*/Color.fromRGBA(0, 0, 0, 0);

function clamp$1(num) {
  if (Number.isNaN(num)) {
    return 0;
  }

  return Math.min(255, Math.max(0, Math.floor(num)));
}

function parse16(str) {
  return parseInt(str, 16);
}

function numToHex(num) {
  return num.toString(16).padStart(2, "0");
}

injectInspector(Color, [["red", false], ["green", false], ["blue", false], ["alpha", false]]);

function clamp(x, min, max) {
  return Math.min(max, Math.max(min, x));
}

var Fill = /*@__PURE__*/function (Versioning) {
  function Fill() {
    Versioning.call(this);
    this._alpha = 1;
  }

  if (Versioning) Fill.__proto__ = Versioning;
  Fill.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  Fill.prototype.constructor = Fill;
  var prototypeAccessors = {
    alpha: {
      configurable: true
    }
  };

  prototypeAccessors.alpha.get = function alpha() {
    return this._alpha;
  };

  prototypeAccessors.alpha.set = function alpha(value) {
    if (this._alpha === value) {
      return;
    }

    this._alpha = value;

    this._updated();
  };

  Fill.prototype.isEmpty = function isEmpty() {
    return false;
  };

  Fill.prototype.applyTo = function applyTo(canvas, bounds) {
    if (bounds === void 0) bounds = canvas.bounds;
    var fillStyle = this.getNative(canvas, bounds);
    canvas.nativeFillStyle = fillStyle instanceof Color ? fillStyle.rgba : fillStyle;

    if (this.alpha !== 1) {
      canvas.globalAlpha *= clamp(this.alpha, 0, 1);
    }
  };

  Fill.prototype.applyToStroke = function applyToStroke(canvas, bounds) {
    if (bounds === void 0) bounds = canvas.bounds;
    var fillStyle = this.getNative(canvas, bounds);
    canvas.nativeStrokeStyle = fillStyle instanceof Color ? fillStyle.rgba : fillStyle;

    if (this.alpha !== 1) {
      canvas.globalAlpha *= clamp(this.alpha, 0, 1);
    }
  };

  Fill.prototype.applyTransformTo = function applyTransformTo(canvas, bounds) {
    if (bounds === void 0) bounds = canvas.bounds;
    var transform = this.getTransform(canvas, bounds);
    canvas.transform = _matrix$6.copyFrom(canvas.transform).append(transform);
  };

  Fill.prototype.generateRawRenderData = function generateRawRenderData(bounds) {
    return {
      noFill: 1
    };
  };

  Fill.prototype.getNative = function getNative(canvas, bounds) {
    return Color.Transparent;
  };

  Fill.prototype.getTransform = function getTransform(canvas, bounds) {
    return Matrix.Identity;
  };

  Object.defineProperties(Fill.prototype, prototypeAccessors);
  return Fill;
}(Versioning);
var EmptyFill = /*@__PURE__*/function (Fill) {
  function EmptyFill() {
    Fill.apply(this, arguments);
  }

  if (Fill) EmptyFill.__proto__ = Fill;
  EmptyFill.prototype = /*#__PURE__*/Object.create(Fill && Fill.prototype);
  EmptyFill.prototype.constructor = EmptyFill;

  EmptyFill.prototype.isEmpty = function isEmpty() {
    return true;
  };

  EmptyFill.prototype._addUpdateListener = function _addUpdateListener() {};

  EmptyFill.prototype._removeUpdateListener = function _removeUpdateListener() {};

  EmptyFill.prototype._updated = function _updated() {};

  return EmptyFill;
}(Fill);
Fill.None = /*#__PURE__*/Object.freeze( /*#__PURE__*/new EmptyFill());

var _matrix$6 = /*#__PURE__*/new Matrix();

injectInspector(Fill, []);
injectInspector(EmptyFill, [["$", function () {
  return EmptyFill.name;
}]]);

var StrokeStyle = /*@__PURE__*/function (Versioning) {
  function StrokeStyle() {
    Versioning.call(this);
    this._fill = Fill.None;
    this._width = 0;
    this._lineJoin = LineJoin.Miter;
    this._lineCap = LineCap.Butt;
    this._miterLimit = 10;
    this._dash = NO_LINE_DASH;
    this._dashOffset = 0;
  }

  if (Versioning) StrokeStyle.__proto__ = Versioning;
  StrokeStyle.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  StrokeStyle.prototype.constructor = StrokeStyle;
  var prototypeAccessors = {
    width: {
      configurable: true
    },
    lineJoin: {
      configurable: true
    },
    lineCap: {
      configurable: true
    },
    miterLimit: {
      configurable: true
    },
    dash: {
      configurable: true
    },
    dashOffset: {
      configurable: true
    },
    fill: {
      configurable: true
    }
  };

  prototypeAccessors.width.get = function width() {
    return this._width;
  };

  prototypeAccessors.width.set = function width(value) {
    if (this._width === value) {
      return;
    }

    this._width = value;

    this._updated();
  };

  prototypeAccessors.lineJoin.get = function lineJoin() {
    return this._lineJoin;
  };

  prototypeAccessors.lineJoin.set = function lineJoin(value) {
    if (this._lineJoin === value) {
      return;
    }

    this._lineJoin = value;

    this._updated();
  };

  prototypeAccessors.lineCap.get = function lineCap() {
    return this._lineCap;
  };

  prototypeAccessors.lineCap.set = function lineCap(value) {
    if (this._lineCap === value) {
      return;
    }

    this._lineCap = value;

    this._updated();
  };

  prototypeAccessors.miterLimit.get = function miterLimit() {
    return this._miterLimit;
  };

  prototypeAccessors.miterLimit.set = function miterLimit(value) {
    if (this._miterLimit === value) {
      return;
    }

    this._miterLimit = value;

    this._updated();
  };

  prototypeAccessors.dash.get = function dash() {
    return this._dash;
  };

  prototypeAccessors.dash.set = function dash(value) {
    if (this._dash === value) {
      return;
    }

    this._dash = value;

    this._updated();
  };

  prototypeAccessors.dashOffset.get = function dashOffset() {
    return this._dashOffset;
  };

  prototypeAccessors.dashOffset.set = function dashOffset(value) {
    if (this._dashOffset === value) {
      return;
    }

    this._dashOffset = value;

    this._updated();
  };

  prototypeAccessors.fill.get = function fill() {
    return this._fill;
  };

  prototypeAccessors.fill.set = function fill(value) {
    if (this._fill === value) {
      return;
    }

    this._fill._removeUpdateListener(this._updated);

    value._addUpdateListener(this._updated);

    this._fill = value;

    this._updated();
  };

  StrokeStyle.prototype.isEmpty = function isEmpty() {
    return this.width === 0 || this.fill.isEmpty();
  };

  StrokeStyle.prototype.applyTo = function applyTo(canvas) {
    canvas.lineWidth = this.width;
    canvas.lineJoin = this.lineJoin;
    canvas.lineCap = this.lineCap;
    canvas.miterLimit = this.miterLimit;
    canvas.lineDash = this.dash;
    canvas.lineDashOffset = this.dashOffset;
  };

  StrokeStyle.prototype.dispose = function dispose() {
    this._fill._removeUpdateListener(this._updated);
  };

  Object.defineProperties(StrokeStyle.prototype, prototypeAccessors);
  return StrokeStyle;
}(Versioning);
var LineCap = /* @__PURE__ */function (LineCap2) {
  LineCap2["Butt"] = "butt";
  LineCap2["Round"] = "round";
  LineCap2["Square"] = "square";
  return LineCap2;
}(LineCap || {});
var LineJoin = /* @__PURE__ */function (LineJoin2) {
  LineJoin2["Miter"] = "miter";
  LineJoin2["Bevel"] = "bevel";
  LineJoin2["Round"] = "round";
  return LineJoin2;
}(LineJoin || {});
var NO_LINE_DASH = /*#__PURE__*/Object.freeze([]);
injectInspector(StrokeStyle, ["width", "lineJoin", "lineCap", "miterLimit", "dash", "dashOffset", "fill"]);

var Command = /* @__PURE__ */function (Command2) {
  Command2["MoveTo"] = "M";
  Command2["LineTo"] = "L";
  Command2["CurveTo"] = "C";
  Command2["QuadraticCurveTo"] = "Q";
  Command2["ClosePath"] = "Z";
  return Command2;
}(Command || {});
var MoveTo = /*@__PURE__*/function () {
  function MoveTo(x, y) {
    this.x = x;
    this.y = y;
    this.cmd = "M"
    /* MoveTo */
    ;
  }

  var prototypeAccessors = {
    seg: {
      configurable: true
    }
  };

  prototypeAccessors.seg.get = function seg() {
    return {
      args: {
        x: this.x,
        y: this.y
      }
    };
  };

  Object.defineProperties(MoveTo.prototype, prototypeAccessors);
  return MoveTo;
}();
var LineTo = /*@__PURE__*/function () {
  function LineTo(startX, startY, x, y) {
    this.startX = startX;
    this.startY = startY;
    this.x = x;
    this.y = y;
    this.cmd = "L"
    /* LineTo */
    ;
  }

  var prototypeAccessors$1 = {
    cp1x: {
      configurable: true
    },
    cp1y: {
      configurable: true
    },
    cp2x: {
      configurable: true
    },
    cp2y: {
      configurable: true
    },
    seg: {
      configurable: true
    }
  };

  prototypeAccessors$1.cp1x.get = function () {
    return this.startX;
  };

  prototypeAccessors$1.cp1y.get = function () {
    return this.startY;
  };

  prototypeAccessors$1.cp2x.get = function () {
    return this.x;
  };

  prototypeAccessors$1.cp2y.get = function () {
    return this.y;
  };

  prototypeAccessors$1.seg.get = function () {
    return {
      args: {
        x1: this.x,
        y1: this.y,
        x2: this.x,
        y2: this.y,
        x: this.x,
        y: this.y
      }
    };
  };

  Object.defineProperties(LineTo.prototype, prototypeAccessors$1);
  return LineTo;
}();
var CurveTo = /*@__PURE__*/function () {
  function CurveTo(startX, startY, x1, y1, x2, y2, x, y) {
    this.startX = startX;
    this.startY = startY;
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
    this.x = x;
    this.y = y;
    this.cmd = "C"
    /* CurveTo */
    ;
  }

  var prototypeAccessors$2 = {
    cp1x: {
      configurable: true
    },
    cp1y: {
      configurable: true
    },
    cp2x: {
      configurable: true
    },
    cp2y: {
      configurable: true
    },
    seg: {
      configurable: true
    }
  };

  prototypeAccessors$2.cp1x.get = function () {
    return this.x1;
  };

  prototypeAccessors$2.cp1y.get = function () {
    return this.y1;
  };

  prototypeAccessors$2.cp2x.get = function () {
    return this.x2;
  };

  prototypeAccessors$2.cp2y.get = function () {
    return this.y2;
  };

  prototypeAccessors$2.seg.get = function () {
    return {
      args: {
        x1: this.x1,
        y1: this.y1,
        x2: this.x2,
        y2: this.y2,
        x: this.x,
        y: this.y
      }
    };
  };

  Object.defineProperties(CurveTo.prototype, prototypeAccessors$2);
  return CurveTo;
}();
var QuadraticCurveTo = /*@__PURE__*/function () {
  function QuadraticCurveTo(startX, startY, x1, y1, x, y) {
    this.startX = startX;
    this.startY = startY;
    this.x1 = x1;
    this.y1 = y1;
    this.x = x;
    this.y = y;
    this.cmd = "Q"
    /* QuadraticCurveTo */
    ;
  }

  var prototypeAccessors$3 = {
    cp1x: {
      configurable: true
    },
    cp1y: {
      configurable: true
    },
    cp2x: {
      configurable: true
    },
    cp2y: {
      configurable: true
    },
    seg: {
      configurable: true
    }
  };

  prototypeAccessors$3.cp1x.get = function () {
    return this.startX / 3 + 2 / 3 * this.x1;
  };

  prototypeAccessors$3.cp1y.get = function () {
    return this.startY / 3 + 2 / 3 * this.y1;
  };

  prototypeAccessors$3.cp2x.get = function () {
    return this.x / 3 + 2 / 3 * this.x1;
  };

  prototypeAccessors$3.cp2y.get = function () {
    return this.y / 3 + 2 / 3 * this.y1;
  };

  prototypeAccessors$3.seg.get = function () {
    var cpx = this.x / 3 + 2 / 3 * this.x1;
    var cpy = this.y / 3 + 2 / 3 * this.y1;
    return {
      args: {
        x1: cpx,
        y1: cpy,
        x2: cpx,
        y2: cpy,
        x: this.x,
        y: this.y
      }
    };
  };

  Object.defineProperties(QuadraticCurveTo.prototype, prototypeAccessors$3);
  return QuadraticCurveTo;
}();
var ClosePath = /*@__PURE__*/function () {
  function ClosePath(startX, startY, x, y) {
    this.startX = startX;
    this.startY = startY;
    this.x = x;
    this.y = y;
    this.cmd = "Z"
    /* ClosePath */
    ;
  }

  var prototypeAccessors$4 = {
    cp1x: {
      configurable: true
    },
    cp1y: {
      configurable: true
    },
    cp2x: {
      configurable: true
    },
    cp2y: {
      configurable: true
    },
    seg: {
      configurable: true
    }
  };

  prototypeAccessors$4.cp1x.get = function () {
    return this.startX;
  };

  prototypeAccessors$4.cp1y.get = function () {
    return this.startY;
  };

  prototypeAccessors$4.cp2x.get = function () {
    return this.x;
  };

  prototypeAccessors$4.cp2y.get = function () {
    return this.y;
  };

  prototypeAccessors$4.seg.get = function () {
    return {
      args: {
        x1: this.x,
        y1: this.y,
        x2: this.x,
        y2: this.y,
        x: this.x,
        y: this.y
      }
    };
  };

  Object.defineProperties(ClosePath.prototype, prototypeAccessors$4);
  return ClosePath;
}();

var PathBoundsCalculator = /*@__PURE__*/function () {
  function PathBoundsCalculator() {
    this.output = new Bounds();
    this.radius = 0;
    this.lineJoin = LineJoin.Miter;
    this.lineCap = LineCap.Butt;
    this.miterLimit = 10;
    this.startX = 0;
    this.startY = 0;
    this.firstCurve = void 0;
    this.previousCurve = void 0;
  }

  PathBoundsCalculator.calcBounds = function calcBounds(segments, output) {
    pathBoundsCalculator.exec(segments, NO_STROKE);
    return output.clear().addBounds(pathBoundsCalculator.output);
  };

  PathBoundsCalculator.calcStrokeBounds = function calcStrokeBounds(segments, strokeStyle, output) {
    pathBoundsCalculator.exec(segments, strokeStyle);
    return output.clear().addBounds(pathBoundsCalculator.output);
  };

  PathBoundsCalculator.prototype.exec = function exec(segments, strokeStyle) {
    var _a, _b, _c, _d;

    this.reset(strokeStyle);

    if (segments.length <= 1) {
      return;
    }

    for (var i = 0; i < segments.length; i++) {
      var curve = segments[i];

      if (curve.cmd === Command.MoveTo) {
        this.handlePathEnd(this.previousCurve);
        this.startX = curve.x;
        this.startY = curve.y;
        this.firstCurve = void 0;
        this.previousCurve = void 0;
        continue;
      }

      var sx = (_b = (_a = this.previousCurve) == null ? void 0 : _a.x) != null ? _b : this.startX;
      var sy = (_d = (_c = this.previousCurve) == null ? void 0 : _c.y) != null ? _d : this.startY;
      var isSamePoint = isMachineZero(sx - curve.x) && isMachineZero(sy - curve.y);

      if (isSamePoint) {
        if (i === segments.length - 1) {
          this.handlePathEnd(this.previousCurve);
        }

        continue;
      }

      this.addCurve(curve.startX, curve.cp1x, curve.cp2x, curve.x, "minX", "maxX");
      this.addCurve(curve.startY, curve.cp1y, curve.cp2y, curve.y, "minY", "maxY");

      if (this.previousCurve) {
        this.addJoin(this.previousCurve, curve);
      }

      if (i === segments.length - 1) {
        this.handlePathEnd(curve);
      }

      this.previousCurve = curve;
      this.firstCurve || (this.firstCurve = curve);
    }
  };

  PathBoundsCalculator.prototype.reset = function reset(strokeStyle) {
    this.output.clear();
    this.radius = strokeStyle.width / 2;
    this.lineJoin = strokeStyle.lineJoin;
    this.lineCap = strokeStyle.lineCap;
    this.miterLimit = strokeStyle.lineJoin === LineJoin.Bevel ? 0 : strokeStyle.miterLimit;
    this.startX = 0;
    this.startY = 0;
    this.firstCurve = void 0;
    this.previousCurve = void 0;
  };

  PathBoundsCalculator.prototype.handlePathEnd = function handlePathEnd(curve) {
    if (!curve) {
      return;
    }

    var isClosed = this.startX === curve.x && this.startY === curve.y;

    if (!isClosed) {
      this.addCap(this.firstCurve || curve, 0);
      this.addCap(curve, 1);
    } else if (this.firstCurve) {
      this.addJoin(curve, this.firstCurve);
    }
  };

  PathBoundsCalculator.prototype.addCurve = function addCurve(v0, v1, v2, v3, kMin, kMax) {
    var minPad = this.output[kMin];
    var maxPad = this.output[kMax];
    var hasValueOutside = v0 - this.radius < minPad || v1 - this.radius < minPad || v2 - this.radius < minPad || v3 - this.radius < minPad || v0 + this.radius > maxPad || v1 + this.radius > maxPad || v2 + this.radius > maxPad || v3 + this.radius > maxPad;

    if (!hasValueOutside) {
      return;
    }

    if (v1 < v0 !== v1 < v3 && v2 < v0 !== v2 < v3) {
      this.addPaddedValue(v0, this.radius, kMin, kMax);
      this.addPaddedValue(v3, this.radius, kMin, kMax);
    } else {
      var a = 3 * (v1 - v2) - v0 + v3;
      var b = 2 * (v0 + v2) - 4 * v1;
      var c = v1 - v0;
      var count = solveQuadratic(a, b, c, _roots);
      var tMin = CURVETIME_EPSILON;
      var tMax = 1 - tMin;
      this.addPaddedValue(v3, this.radius, kMin, kMax);

      for (var i = 0; i < count; i++) {
        var t = _roots[i];
        var u = 1 - t;

        if (tMin <= t && t <= tMax) {
          var v = u * u * u * v0 + 3 * u * u * t * v1 + 3 * u * t * t * v2 + t * t * t * v3;
          this.addPaddedValue(v, this.radius, kMin, kMax);
        }
      }
    }
  };

  PathBoundsCalculator.prototype.addPaddedValue = function addPaddedValue(value, padding, kMin, kMax) {
    var left = value - padding;
    var right = value + padding;

    if (left < this.output[kMin]) {
      this.output[kMin] = left;
    }

    if (right > this.output[kMax]) {
      this.output[kMax] = right;
    }
  };

  PathBoundsCalculator.prototype.addCap = function addCap(curve, t) {
    var x = t === 0 ? curve.startX : curve.x;
    var y = t === 0 ? curve.startY : curve.y;

    if (this.lineCap === LineCap.Round) {
      return this.addRound(x, y);
    }

    var ref = getNormal(curve, t, _normal).multiply(t === 0 ? this.radius : -this.radius);
    var vx = ref.x;
    var vy = ref.y;
    this.output.addPoint(x + vx, y + vy);
    this.output.addPoint(x - vx, y - vy);

    if (this.lineCap === LineCap.Square) {
      this.output.addPoint(x + vy + vx, y - vx + vy);
      this.output.addPoint(x + vy - vx, y - vx - vy);
    }
  };

  PathBoundsCalculator.prototype.addJoin = function addJoin(curve1, curve2) {
    if (this.radius <= 0) {
      return;
    }

    if (this.lineJoin === LineJoin.Round) {
      this.addRound(curve2.startX, curve2.startY);
    } else {
      this.addMiterJoin(curve1, curve2);
    }
  };

  PathBoundsCalculator.prototype.addMiterJoin = function addMiterJoin(curve1, curve2) {
    var normal1 = getNormal(curve1, 1, _normal1).multiply(this.radius);
    var normal2 = getNormal(curve2, 0, _normal2).multiply(this.radius);
    var angle = normal1.angleBetween(normal2);

    if (angle < 0 || angle >= 180) {
      normal1.negate();
      normal2.negate();
    }

    var point1 = _point1.from(curve1).add(normal1.x, normal1.y);

    var point2 = _point2.from(curve1).add(normal2.x, normal2.y);

    this.output.addPoint(point1.x, point1.y);
    this.output.addPoint(point2.x, point2.y);

    if (this.miterLimit === 0) {
      return;
    }

    var cross = -normal1.y * normal2.x + normal1.x * normal2.y;

    if (isMachineZero(cross)) {
      return;
    }

    var dx = point1.x - point2.x;
    var dy = point1.y - point2.y;
    var u1 = -(normal2.y * dy + normal2.x * dx) / cross;
    var cornerX = point1.x + u1 * -normal1.y;
    var cornerY = point1.y + u1 * normal1.x;
    var len = Math.sqrt(Math.pow(cornerX - curve1.x, 2) + Math.pow(cornerY - curve1.y, 2));

    if (len < this.miterLimit * this.radius) {
      this.output.addPoint(cornerX, cornerY);
    }
  };

  PathBoundsCalculator.prototype.addRound = function addRound(cx, cy) {
    var ref = this;
    var output = ref.output;
    var radius = ref.radius;
    output.addPoint(cx - radius, cy - radius);
    output.addPoint(cx + radius, cy + radius);
  };

  return PathBoundsCalculator;
}();

function getNormal(curve, t, out) {
  var x;
  var y;

  if (t === 0) {
    x = curve.cp1x - curve.startX;
    y = curve.cp1y - curve.startY;
  } else {
    x = curve.x - curve.cp2x;
    y = curve.y - curve.cp2y;
  }

  if (isMachineZero(x) && isMachineZero(y)) {
    x = curve.x - curve.startX;
    y = curve.y - curve.startY;
  }

  var len = Math.sqrt(x * x + y * y);
  return out.set(y / len, -x / len);
}

var pathBoundsCalculator = /*#__PURE__*/new PathBoundsCalculator();
var NO_STROKE = /*#__PURE__*/new StrokeStyle();

var _normal = /*#__PURE__*/new Point();

var _roots = [];

var _point1 = /*#__PURE__*/new Point();

var _point2 = /*#__PURE__*/new Point();

var _normal1 = /*#__PURE__*/new Point();

var _normal2 = /*#__PURE__*/new Point();

var PathParser = /*@__PURE__*/function () {
  function PathParser(_s) {
    this._s = _s;
    this._pos = 0;
    this._prevCmd = null;
    this.startX = 0;
    this.startY = 0;
    this.x = 0;
    this.y = 0;
    this.pendingArcCurves = [];
    this._prevCurve = null;
    this._prevQuadraticCurve = null;
  }

  var prototypeAccessors = {
    _char: {
      configurable: true
    },
    _eof: {
      configurable: true
    }
  };

  PathParser.parse = function parse(path) {
    var parser = new PathParser(path);
    var result = [];
    var segment;

    while (segment = parser.next()) {
      result.push(segment);
    }

    return result;
  };

  prototypeAccessors._char.get = function _char() {
    return this._s[this._pos];
  };

  prototypeAccessors._eof.get = function _eof() {
    return this._pos >= this._s.length;
  };

  PathParser.prototype.next = function next() {
    try {
      if (this.pendingArcCurves.length) {
        return this.pendingArcCurve();
      }

      return this.readSegment();
    } catch (e) {
      return null;
    }
  };

  PathParser.prototype.readSegment = function readSegment() {
    this.readSpaces();

    if (this._eof) {
      return null;
    }

    var cmd = this.readCommand();

    if (cmd === null) {
      return null;
    }

    if (this._prevCmd === null && cmd !== "M" && cmd !== "m") {
      return null;
    }

    this._prevCmd = cmd;

    switch (cmd) {
      case "M":
        return this.moveTo();

      case "m":
        return this.moveToRelative();

      case "L":
        return this.lineTo();

      case "l":
        return this.lineToRelative();

      case "Z":
      case "z":
        return this.closePath();

      case "C":
        return this.curveTo();

      case "c":
        return this.curveToRelative();

      case "H":
        return this.horizontalLineTo();

      case "h":
        return this.horizontalLineToRelative();

      case "V":
        return this.verticalLineTo();

      case "v":
        return this.verticalLineToRelative();

      case "A":
        return this.arcTo();

      case "a":
        return this.arcToRelative();

      case "S":
        return this.smoothCurveTo();

      case "s":
        return this.smoothCurveToRelative();

      case "Q":
        return this.quadraticCurveTo();

      case "q":
        return this.quadraticCurveToRelative();

      case "T":
        return this.smoothQuadraticCurveTo();

      case "t":
        return this.smoothQuadraticCurveToRelative();

      default:
        throw new SyntaxError();
    }
  };

  PathParser.prototype.moveTo = function moveTo() {
    var segment = new MoveTo(this.readNumber(), this.readNumber());
    this.startX = segment.x;
    this.startY = segment.y;
    this.x = segment.x;
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.moveToRelative = function moveToRelative() {
    var segment = new MoveTo(this.readNumber() + this.x, this.readNumber() + this.y);
    this.startX = segment.x;
    this.startY = segment.y;
    this.x = segment.x;
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.lineTo = function lineTo() {
    var segment = new LineTo(this.x, this.y, this.readNumber(), this.readNumber());
    this.x = segment.x;
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.lineToRelative = function lineToRelative() {
    var segment = new LineTo(this.x, this.y, this.readNumber() + this.x, this.readNumber() + this.y);
    this.x = segment.x;
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.closePath = function closePath() {
    var segment = new ClosePath(this.x, this.y, this.startX, this.startY);
    this.x = segment.x;
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.curveTo = function curveTo() {
    var segment = new CurveTo(this.x, this.y, this.readNumber(), this.readNumber(), this.readNumber(), this.readNumber(), this.readNumber(), this.readNumber());
    this.x = segment.x;
    this.y = segment.y;
    this._prevCurve = segment;
    return segment;
  };

  PathParser.prototype.curveToRelative = function curveToRelative() {
    var segment = new CurveTo(this.x, this.y, this.readNumber() + this.x, this.readNumber() + this.y, this.readNumber() + this.x, this.readNumber() + this.y, this.readNumber() + this.x, this.readNumber() + this.y);
    this.x = segment.x;
    this.y = segment.y;
    this._prevCurve = segment;
    return segment;
  };

  PathParser.prototype.arcTo = function arcTo() {
    return this.arcHelper(this.readNumber(), this.readNumber(), this.readNumber(), this.readFlag(), this.readFlag(), this.readNumber(), this.readNumber());
  };

  PathParser.prototype.arcToRelative = function arcToRelative() {
    return this.arcHelper(this.readNumber(), this.readNumber(), this.readNumber(), this.readFlag(), this.readFlag(), this.readNumber() + this.x, this.readNumber() + this.y);
  };

  PathParser.prototype.arcHelper = function arcHelper(rx, ry, xRot, largeArc, sweep, cx, cy) {
    var this$1$1 = this;
    var curves = arcToCurve({
      px: this.x,
      py: this.y,
      rx: rx || EPSILON,
      ry: ry || EPSILON,
      xAxisRotation: xRot,
      largeArcFlag: largeArc,
      sweepFlag: sweep,
      cx: cx,
      cy: cy
    });

    if (curves.length === 0) {
      return new LineTo(this.x, this.y, this.x, this.y);
    }

    this.pendingArcCurves = curves.map(function (ref, index) {
      var x1 = ref.x1;
      var y1 = ref.y1;
      var x2 = ref.x2;
      var y2 = ref.y2;
      var x = ref.x;
      var y = ref.y;

      if (index === 0) {
        return new CurveTo(this$1$1.x, this$1$1.y, x1, y1, x2, y2, x, y);
      }

      return new CurveTo(curves[index - 1].x, curves[index - 1].y, x1, y1, x2, y2, x, y);
    });
    return this.pendingArcCurve();
  };

  PathParser.prototype.pendingArcCurve = function pendingArcCurve() {
    var segment = this.pendingArcCurves.shift();
    this.x = segment.x;
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.smoothCurveTo = function smoothCurveTo() {
    var x1 = this.x;
    var y1 = this.y;

    if (this._prevCurve !== null) {
      x1 += x1 - this._prevCurve.x2;
      y1 += y1 - this._prevCurve.y2;
    }

    var segment = new CurveTo(this.x, this.y, x1, y1, this.readNumber(), this.readNumber(), this.readNumber(), this.readNumber());
    this.x = segment.x;
    this.y = segment.y;
    this._prevCurve = segment;
    return segment;
  };

  PathParser.prototype.smoothCurveToRelative = function smoothCurveToRelative() {
    var x1 = this.x;
    var y1 = this.y;

    if (this._prevCurve !== null) {
      x1 += x1 - this._prevCurve.x2;
      y1 += y1 - this._prevCurve.y2;
    }

    var segment = new CurveTo(this.x, this.y, x1, y1, this.readNumber() + this.x, this.readNumber() + this.y, this.readNumber() + this.x, this.readNumber() + this.y);
    this.x = segment.x;
    this.y = segment.y;
    this._prevCurve = segment;
    return segment;
  };

  PathParser.prototype.quadraticCurveTo = function quadraticCurveTo() {
    var segment = new QuadraticCurveTo(this.x, this.y, this.readNumber(), this.readNumber(), this.readNumber(), this.readNumber());
    this.x = segment.x;
    this.y = segment.y;
    this._prevQuadraticCurve = segment;
    return segment;
  };

  PathParser.prototype.quadraticCurveToRelative = function quadraticCurveToRelative() {
    var segment = new QuadraticCurveTo(this.x, this.y, this.readNumber() + this.x, this.readNumber() + this.y, this.readNumber() + this.x, this.readNumber() + this.y);
    this.x = segment.x;
    this.y = segment.y;
    this._prevQuadraticCurve = segment;
    return segment;
  };

  PathParser.prototype.smoothQuadraticCurveTo = function smoothQuadraticCurveTo() {
    var x1 = this.x;
    var y1 = this.y;

    if (this._prevQuadraticCurve !== null) {
      x1 += x1 - this._prevQuadraticCurve.x1;
      y1 += y1 - this._prevQuadraticCurve.y1;
    }

    var segment = new QuadraticCurveTo(this.x, this.y, x1, y1, this.readNumber() + this.x, this.readNumber() + this.y);
    this.x = segment.x;
    this.y = segment.y;
    this._prevQuadraticCurve = segment;
    return segment;
  };

  PathParser.prototype.smoothQuadraticCurveToRelative = function smoothQuadraticCurveToRelative() {
    var x1 = this.x;
    var y1 = this.y;

    if (this._prevQuadraticCurve !== null) {
      x1 += x1 - this._prevQuadraticCurve.x1;
      y1 += y1 - this._prevQuadraticCurve.y1;
    }

    var segment = new QuadraticCurveTo(this.x, this.y, x1, y1, this.readNumber(), this.readNumber());
    this.x = segment.x;
    this.y = segment.y;
    this._prevQuadraticCurve = segment;
    return segment;
  };

  PathParser.prototype.horizontalLineTo = function horizontalLineTo() {
    var segment = new LineTo(this.x, this.y, this.readNumber(), this.y);
    this.x = segment.x;
    return segment;
  };

  PathParser.prototype.horizontalLineToRelative = function horizontalLineToRelative() {
    var segment = new LineTo(this.x, this.y, this.readNumber() + this.x, this.y);
    this.x = segment.x;
    return segment;
  };

  PathParser.prototype.verticalLineTo = function verticalLineTo() {
    var segment = new LineTo(this.x, this.y, this.x, this.readNumber());
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.verticalLineToRelative = function verticalLineToRelative() {
    var segment = new LineTo(this.x, this.y, this.x, this.readNumber() + this.y);
    this.y = segment.y;
    return segment;
  };

  PathParser.prototype.advance = function advance() {
    this._pos += 1;
  };

  PathParser.prototype.readSpaces = function readSpaces() {
    while (!this._eof && isSpace(this._char)) {
      this.advance();
    }
  };

  PathParser.prototype.readCommand = function readCommand() {
    var char = this._char;

    if (!isCmd(char)) {
      var prevCmd = this._prevCmd;

      if (prevCmd && isNumberStart(char)) {
        if (prevCmd === "M") {
          return "L";
        }

        if (prevCmd === "m") {
          return "l";
        }

        if (prevCmd === "Z" || prevCmd === "z") {
          return null;
        }

        return prevCmd;
      }

      return null;
    }

    this.advance();
    return char;
  };

  PathParser.prototype.readNumber = function readNumber() {
    this.readSpaces();

    if (this._eof) {
      throw new SyntaxError();
    }

    var dot = false;
    var exp = false;
    var sign = true;
    var start = this._pos;
    var pos = start;
    var char;

    while (char = this._s[pos]) {
      if (isDigit(char)) {
        if (sign) {
          sign = false;
        }
      } else if (char === ".") {
        if (dot) {
          break;
        }

        sign = false;
        dot = true;
      } else if (char === "-" || char === "+") {
        if (!sign) {
          break;
        }

        sign = false;
      } else if (char === "e" || char === "E") {
        if (exp) {
          break;
        }

        exp = true;
        sign = true;
      } else {
        break;
      }

      pos += 1;
    }

    this._pos = pos;
    return +this._s.slice(start, pos);
  };

  PathParser.prototype.readFlag = function readFlag() {
    this.readSpaces();

    if (this._eof) {
      throw new SyntaxError();
    }

    var char = this._char;
    this.advance();

    if (char === "0") {
      return 0;
    }

    if (char === "1") {
      return 1;
    }

    throw new SyntaxError();
  };

  Object.defineProperties(PathParser.prototype, prototypeAccessors);
  return PathParser;
}();

function isCmd(char) {
  switch (char) {
    case "M":
    case "m":
    case "L":
    case "l":
    case "Z":
    case "z":
    case "C":
    case "c":
    case "H":
    case "h":
    case "V":
    case "v":
    case "A":
    case "a":
    case "S":
    case "s":
    case "Q":
    case "q":
    case "T":
    case "t":
      return true;

    default:
      return false;
  }
}

function isSpace(char) {
  switch (char) {
    case " ":
    case ",":
    case "\n":
    case "\r":
    case "	":
      return true;

    default:
      return false;
  }
}

function isDigit(char) {
  switch (char) {
    case "0":
    case "1":
    case "2":
    case "3":
    case "4":
    case "5":
    case "6":
    case "7":
    case "8":
    case "9":
      return true;

    default:
      return false;
  }
}

function isNumberStart(char) {
  switch (char) {
    case "-":
    case "0":
    case "1":
    case ".":
    case "2":
    case "3":
    case "4":
    case "5":
    case "6":
    case "7":
    case "8":
    case "9":
    case "+":
      return true;

    default:
      return false;
  }
}

function drawPath(canvas, segments) {
  canvas.beginPath();

  for (var i = 0, list = segments; i < list.length; i += 1) {
    var segment = list[i];

    switch (segment.cmd) {
      case Command.MoveTo:
        canvas.moveTo(segment.x, segment.y);
        break;

      case Command.LineTo:
        canvas.lineTo(segment.x, segment.y);
        break;

      case Command.ClosePath:
        canvas.closePath();
        break;

      case Command.CurveTo:
        canvas.bezierCurveTo(segment.x1, segment.y1, segment.x2, segment.y2, segment.x, segment.y);
        break;

      case Command.QuadraticCurveTo:
        canvas.quadraticCurveTo(segment.x1, segment.y1, segment.x, segment.y);
        break;
    }
  }
}

var ObjectPool = /*@__PURE__*/function () {
  function ObjectPool() {
    this.availableObjects = /* @__PURE__ */new Set();
    this.inUseObjects = /* @__PURE__ */new Set();
    this.sizeLimit = Infinity;
  }

  ObjectPool.prototype.checkOut = function checkOut(type) {
    var object;

    if (this.availableObjects.size === 0) {
      object = this.create(type);
    } else {
      object = this.availableObjects.values().next().value;
      this.availableObjects.delete(object);
    }

    this.inUseObjects.add(object);
    return object;
  };

  ObjectPool.prototype.checkIn = function checkIn(object) {
    if (this.inUseObjects.has(object)) {
      this.inUseObjects.delete(object);
    }

    this.availableObjects.add(object);
  };

  ObjectPool.prototype.size = function size() {
    return this.availableObjects.size + this.inUseObjects.size;
  };

  ObjectPool.prototype.create = function create(Type) {
    return new Type();
  };

  return ObjectPool;
}();

var CanvasPoolSingleton = /*@__PURE__*/function (ObjectPool) {
  function CanvasPoolSingleton() {
    ObjectPool.apply(this, arguments);
  }

  if (ObjectPool) CanvasPoolSingleton.__proto__ = ObjectPool;
  CanvasPoolSingleton.prototype = /*#__PURE__*/Object.create(ObjectPool && ObjectPool.prototype);
  CanvasPoolSingleton.prototype.constructor = CanvasPoolSingleton;

  CanvasPoolSingleton.getInstance = function getInstance() {
    if (!CanvasPoolSingleton.instance) {
      CanvasPoolSingleton.instance = new CanvasPoolSingleton();
    }

    return CanvasPoolSingleton.instance;
  };

  CanvasPoolSingleton.prototype.checkIn = function checkIn(object) {
    ObjectPool.prototype.checkIn.call(this, object);
    object.width = 0;
    object.height = 0;
    object.clear();
    object.padding.clear();
    object.bounds.clear();
    object.parent = void 0;
    object.isBuffer = false;
    object.contextScale.sx = 1;
    object.contextScale.sy = 1;

    if (object.autoMount) {
      object.autoMount = false;
    }
  };

  CanvasPoolSingleton.prototype.checkOut = function checkOut(type, options) {
    var buffer = ObjectPool.prototype.checkOut.call(this, type);

    if (options == null ? void 0 : options.autoMount) {
      buffer.autoMount = true;
    }

    return buffer;
  };

  CanvasPoolSingleton.prototype.sizeAvailable = function sizeAvailable() {
    return this.availableObjects.size > 0 || this.size() < this.sizeLimit;
  };

  CanvasPoolSingleton.prototype.create = function create(Type) {
    return new Type(this.createOptions);
  };

  return CanvasPoolSingleton;
}(ObjectPool);

function expand(x) {
  return Math.sign(x) * Math.ceil(Math.abs(x));
}

var _Padding = /*@__PURE__*/function () {
  function Padding(top, right, bottom, left) {
    if (top === void 0) top = 0;
    if (right === void 0) right = 0;
    if (bottom === void 0) bottom = 0;
    if (left === void 0) left = 0;
    this.set(top, right, bottom, left);
  }

  Padding.prototype.copyFrom = function copyFrom(input) {
    this.top = input.top;
    this.right = input.right;
    this.bottom = input.bottom;
    this.left = input.left;
    return this;
  };

  Padding.prototype.clear = function clear() {
    this.top = 0;
    this.right = 0;
    this.bottom = 0;
    this.left = 0;
    return this;
  };

  Padding.prototype.add = function add(input) {
    this.top += input.top;
    this.right += input.right;
    this.bottom += input.bottom;
    this.left += input.left;
    return this;
  };

  Padding.prototype.overlay = function overlay(input) {
    this.top = Math.max(this.top, input.top);
    this.right = Math.max(this.right, input.right);
    this.bottom = Math.max(this.bottom, input.bottom);
    this.left = Math.max(this.left, input.left);
    return this;
  };

  Padding.prototype.scale = function scale(sx, sy) {
    if (sy === void 0) sy = sx;
    this.top *= sy;
    this.right *= sx;
    this.bottom *= sy;
    this.left *= sx;
    return this;
  };

  Padding.prototype.expand = function expand$1() {
    this.top = expand(this.top);
    this.right = expand(this.right);
    this.bottom = expand(this.bottom);
    this.left = expand(this.left);
    return this;
  };

  Padding.prototype.set = function set(top, right, bottom, left) {
    this.top = top;
    this.right = right;
    this.bottom = bottom;
    this.left = left;
    return this;
  };

  Padding.prototype.setEvenly = function setEvenly(value) {
    return this.set(value, value, value, value);
  };

  Padding.prototype.flip = function flip() {
    return this.set(this.bottom, this.left, this.top, this.right);
  };

  return Padding;
}();

var Padding = _Padding;
Padding.Zero = /*#__PURE__*/new _Padding();
injectInspector(Padding);

var _Cache = /*@__PURE__*/function (Versioning) {
  function Cache(obj) {
    Versioning.call(this);
    this.renderBuffer = null;
    this.renderImageCache = null;
    this.renderImageInfo = {
      bounds: new Bounds(),
      padding: new Padding(),
      width: 0,
      height: 0,
      sx: 1,
      sy: 1
    };
    this.hitBuffer = null;
    this.hitImageCache = null;
    this.hitImageInfo = {
      bounds: new Bounds(),
      padding: new Padding(),
      width: 0,
      height: 0
    };
    this.isManual = false;
    this._renderId = 0;
    this._bounds = new Bounds();
    this._worldTransformId = 0;
    this._worldTransformInverted = new Matrix();
    this._transformToCurrentPlace = new Matrix();
    this._markedReuse = false;
    this.renderImageVersion = 0;
    this.hitImageVersion = 0;
    this.obj = obj;
    this._rendering = CacheRendering.Auto;
  }

  if (Versioning) Cache.__proto__ = Versioning;
  Cache.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  Cache.prototype.constructor = Cache;
  var prototypeAccessors = {
    rendering: {
      configurable: true
    },
    isEmpty: {
      configurable: true
    },
    isValid: {
      configurable: true
    },
    isHitValid: {
      configurable: true
    },
    _isRenderValid: {
      configurable: true
    }
  };

  prototypeAccessors.rendering.get = function rendering() {
    return this._rendering;
  };

  prototypeAccessors.rendering.set = function rendering(value) {
    if (this._rendering === value) {
      return;
    }

    this._rendering = value;

    this._updated();
  };

  prototypeAccessors.isEmpty.get = function isEmpty() {
    return this.renderBuffer === null && (this.renderImageCache === null || this.renderImageCache.loading);
  };

  prototypeAccessors.isValid.get = function isValid() {
    return !this.isEmpty && this._isRenderValid && this._canReuse();
  };

  prototypeAccessors.isHitValid.get = function isHitValid() {
    return (!!this.hitBuffer || !!this.hitImageCache && !this.hitImageCache.loading) && this.isValid;
  };

  prototypeAccessors._isRenderValid.get = function _isRenderValid() {
    return this._renderId === this.obj._id;
  };

  Cache.prototype.getTransform = function getTransform(worldTransform) {
    return this._transformToCurrentPlace.set(1, 0, 0, 1, this._bounds.x, this._bounds.y).prepend(this._worldTransformInverted).prepend(worldTransform || this.obj.worldTransform);
  };

  Cache.prototype.update = function update(renderBuffer, bounds, worldTransform) {
    var this$1$1 = this;
    this.renderBuffer = renderBuffer;

    if (Cache.options.cacheAsImage) {
      this.renderImageVersion += 1;
      this.renderImageCache = null;

      if (this.obj.app) {
        var ref = this.obj.app.target;
        var loader = ref.loader;
        var currentVersion = this.renderImageVersion;
        setTimeout(function () {
          if (this$1$1.renderImageVersion !== currentVersion || renderBuffer !== this$1$1.renderBuffer) {
            return;
          }

          renderBuffer.toBlob(function (blob) {
            if (this$1$1.renderImageVersion !== currentVersion || renderBuffer !== this$1$1.renderBuffer) {
              return;
            }

            if (renderBuffer.width === 0 || renderBuffer.height === 0) {
              return;
            }

            if (blob) {
              var imageUrl = URL.createObjectURL(blob);
              var imageResource = loader.loadImage(imageUrl);
              this$1$1.renderImageCache = imageResource;
              this$1$1.renderImageInfo.bounds.clear().addBounds(renderBuffer.bounds);
              this$1$1.renderImageInfo.padding.copyFrom(renderBuffer.padding);
              this$1$1.renderImageInfo.width = renderBuffer.width;
              this$1$1.renderImageInfo.height = renderBuffer.height;
              this$1$1.renderImageInfo.sx = renderBuffer.contextScale.sx;
              this$1$1.renderImageInfo.sy = renderBuffer.contextScale.sy;
              this$1$1.renderImageCache.onImageLoaded(function () {
                if (this$1$1.renderBuffer !== renderBuffer) {
                  CanvasPoolSingleton.getInstance().checkIn(renderBuffer);
                }

                if (imageResource === this$1$1.renderImageCache && this$1$1.renderImageVersion === currentVersion && this$1$1.renderBuffer === renderBuffer) {
                  CanvasPoolSingleton.getInstance().checkIn(renderBuffer);
                  this$1$1.renderBuffer = null;
                }

                URL.revokeObjectURL(imageUrl);
              });
              this$1$1.renderImageCache.onImageErrored(function () {
                if (this$1$1.renderBuffer !== renderBuffer) {
                  CanvasPoolSingleton.getInstance().checkIn(renderBuffer);
                }

                URL.revokeObjectURL(imageUrl);

                if (imageResource === this$1$1.renderImageCache) {
                  this$1$1.renderImageCache = null;
                }
              });
            }
          });
        }, 0);
      }
    }

    this._clearHitCache();

    this._renderId = this.obj._id;

    this._bounds.clear().addBounds(bounds);

    var transform = worldTransform || this.obj.worldTransform;
    this._worldTransformId = transform._id;

    this._worldTransformInverted.copyFrom(transform).invert();
  };

  Cache.prototype.updateHit = function updateHit(hitBuffer) {
    var this$1$1 = this;
    this.hitBuffer = hitBuffer;

    if (Cache.options.cacheAsImage) {
      this.hitImageVersion += 1;
      this.hitImageCache = null;

      if (!this.obj.app) {
        return;
      }

      var ref = this.obj.app.target;
      var loader = ref.loader;
      var currentVersion = this.hitImageVersion;
      setTimeout(function () {
        if (this$1$1.hitImageVersion !== currentVersion || this$1$1.hitBuffer !== hitBuffer) {
          return;
        }

        hitBuffer.toBlob(function (blob) {
          if (this$1$1.hitImageVersion !== currentVersion || this$1$1.hitBuffer !== hitBuffer) {
            return;
          }

          if (blob) {
            var imageUrl = URL.createObjectURL(blob);
            var imageResource = loader.loadImage(imageUrl);
            this$1$1.hitImageCache = imageResource;
            this$1$1.hitImageInfo.padding.copyFrom(hitBuffer.padding);
            this$1$1.hitImageInfo.width = hitBuffer.width;
            this$1$1.hitImageInfo.height = hitBuffer.height;
            this$1$1.hitImageCache.onImageLoaded(function () {
              URL.revokeObjectURL(imageUrl);

              if (this$1$1.hitBuffer !== hitBuffer) {
                CanvasPoolSingleton.getInstance().checkIn(hitBuffer);
              }

              if (imageResource === this$1$1.hitImageCache && currentVersion === this$1$1.hitImageVersion && this$1$1.hitBuffer === hitBuffer) {
                CanvasPoolSingleton.getInstance().checkIn(hitBuffer);
                this$1$1.hitBuffer = null;
              }
            });
            this$1$1.hitImageCache.onImageErrored(function () {
              if (this$1$1.hitBuffer !== hitBuffer) {
                CanvasPoolSingleton.getInstance().checkIn(hitBuffer);
              }

              URL.revokeObjectURL(imageUrl);

              if (imageResource === this$1$1.hitImageCache) {
                this$1$1.hitImageCache = null;
              }
            });
          }
        });
      }, 0);
    }
  };

  Cache.prototype.markReuse = function markReuse() {
    this._markedReuse = this._isRenderValid;
  };

  Cache.prototype.tryReuse = function tryReuse() {
    if (this._markedReuse) {
      this._renderId = this.obj._id;
    }

    this._markedReuse = false;
  };

  Cache.prototype.clear = function clear() {
    if (this.isEmpty) {
      return;
    }

    this._clearRenderCache();

    this._clearHitCache();

    this.isManual = false;
    this._renderId = 0;

    this._bounds.clear();

    this._worldTransformId = 0;

    this._worldTransformInverted.identity();
  };

  Cache.prototype.dispose = function dispose() {
    this._clearRenderCache();

    this._clearHitCache();
  };

  Cache.prototype._clearRenderCache = function _clearRenderCache() {
    if (this.renderBuffer) {
      CanvasPoolSingleton.getInstance().checkIn(this.renderBuffer);
      this.renderBuffer = null;
    }

    this.renderImageCache = null;
    this.renderImageInfo.bounds.clear();
    this.renderImageInfo.padding.clear();
    this.renderImageInfo.width = 0;
    this.renderImageInfo.height = 0;
    this.renderImageInfo.sx = 1;
    this.renderImageInfo.sy = 1;
  };

  Cache.prototype._clearHitCache = function _clearHitCache() {
    if (this.hitBuffer) {
      CanvasPoolSingleton.getInstance().checkIn(this.hitBuffer);
      this.hitBuffer = null;
    }

    this.hitImageCache = null;
    this.hitImageInfo.padding.clear();
    this.hitImageInfo.width = 0;
    this.hitImageInfo.height = 0;
  };

  Cache.prototype._canReuse = function _canReuse() {
    if (this.rendering === CacheRendering.Auto) {
      var ref = this.obj;
      var worldTransform = ref.worldTransform;

      if (this._worldTransformId === worldTransform._id) {
        return true;
      }

      var diff = _diff.copyFrom(worldTransform).prepend(this._worldTransformInverted);

      var sx = Math.sqrt(Math.pow(diff.a, 2) + Math.pow(diff.b, 2));
      var sy = Math.sqrt(Math.pow(diff.c, 2) + Math.pow(diff.d, 2));
      return sx >= 0.5 - EPSILON && sx <= 1 + EPSILON && sy >= 0.5 - EPSILON && sy <= 1 + EPSILON;
    }

    return this.rendering === CacheRendering.Always;
  };

  Object.defineProperties(Cache.prototype, prototypeAccessors);
  return Cache;
}(Versioning);

var Cache = _Cache;
Cache.options = {
  cacheAsImage: false
};
var CacheRendering = /* @__PURE__ */function (CacheRendering2) {
  CacheRendering2["Auto"] = "auto";
  CacheRendering2["Never"] = "never";
  CacheRendering2["Always"] = "always";
  return CacheRendering2;
}(CacheRendering || {});

var _diff = /*#__PURE__*/new Matrix();

injectInspector(Cache, ["isEmpty", "isValid", "isHitValid", "rendering", ["obj", false], ["renderBuffer", false], ["hitBuffer", false]]);

var EventEmitter = /*@__PURE__*/function () {
  function EventEmitter() {
    this.all = {};
  }

  EventEmitter.prototype.on = function on(type, handler) {
    var this$1$1 = this;
    var handlers = this.all[type];

    if (handlers) {
      handlers.push(handler);
    } else {
      this.all[type] = [handler];
    }

    var dispose = function () {
      return this$1$1.off(type, handler);
    };

    return {
      dispose: dispose
    };
  };

  EventEmitter.prototype.off = function off(type, handler) {
    var handlers = this.all[type];

    if (handlers) {
      if (handler) {
        var index = handlers.indexOf(handler);

        if (index >= 0) {
          handlers.splice(index, 1);
        }
      } else {
        this.all[type] = void 0;
      }
    }
  };

  EventEmitter.prototype.emit = function emit(type, event) {
    var handlers = this.all[type];

    if (handlers) {
      for (var i = 0, list = handlers; i < list.length; i += 1) {
        var handler = list[i];
        handler(event);
      }
    }
  };

  EventEmitter.prototype.dispose = function dispose() {
    this.all = {};
  };

  return EventEmitter;
}();

function guard(fn) {
  if (process.env.NODE_ENV !== "production") {
    return fn;
  }

  var guardedFn = function guardedFn2() {
    var args = arguments;

    try {
      fn.apply(this, args);
    } catch (err) {
      console.error(err);
    }
  };

  return guardedFn;
}

function isEqualMatrix(firstMatrix, secondMatrix) {
  var a = firstMatrix.a;
  var b = firstMatrix.b;
  var c = firstMatrix.c;
  var d = firstMatrix.d;
  var tx = firstMatrix.tx;
  var ty = firstMatrix.ty;
  return a === secondMatrix.a && b === secondMatrix.b && c === secondMatrix.c && d === secondMatrix.d && tx === secondMatrix.tx && ty === secondMatrix.ty;
}

function noop() {}

var PointerEvents = /* @__PURE__ */function (PointerEvents2) {
  PointerEvents2["AUTO"] = "auto";
  PointerEvents2["NONE"] = "none";
  return PointerEvents2;
}(PointerEvents || {});
var INVISIBLE_THRESHOLD = 0.1;
var E_OPERATION_TYPE = /* @__PURE__ */function (E_OPERATION_TYPE2) {
  E_OPERATION_TYPE2[E_OPERATION_TYPE2["POSITION"] = 0] = "POSITION";
  E_OPERATION_TYPE2[E_OPERATION_TYPE2["SIZE"] = 1] = "SIZE";
  E_OPERATION_TYPE2[E_OPERATION_TYPE2["FILL"] = 2] = "FILL";
  E_OPERATION_TYPE2[E_OPERATION_TYPE2["IMAGE"] = 3] = "IMAGE";
  return E_OPERATION_TYPE2;
}(E_OPERATION_TYPE || {});

var _DisplayObject = /*@__PURE__*/function (Versioning) {
  function DisplayObject() {
    var this$1$1 = this;
    Versioning.call(this);
    this.needPartRender = false;
    this.isPartRefreshObject = false;
    this.id = "";
    this.colorKey = null;
    this.forceRenderOutsideView = false;
    this._events = new EventEmitter();
    this._parent = null;
    this._initialBounds = new Bounds();
    this._initialBoundsDirty = true;
    this._filterBounds = new Bounds();
    this._filterBoundsDirty = true;
    this._pointerEvents = null;
    this._cursor = null;
    this.disposeColorKey = noop;
    this._alphaToRender = 1;
    this._shouldRenderInBuffer = false;

    this._visualUnrelatedUpdate = function () {
      this$1$1.cache.markReuse();

      this$1$1._updated();

      this$1$1.cache.tryReuse();
    };

    this._transformUpdated = function () {
      var _a;

      this$1$1._visualUnrelatedUpdate();

      (_a = this$1$1.parent) == null ? void 0 : _a._boundsUpdated();
    };

    this._alpha = 1;
    this._alphaRendering = AlphaRendering.Separated;
    this._visible = true;
    this._filters = [];
    this.transform = new Transform();

    this.transform._addUpdateListener(this._transformUpdated);

    this._worldTransform = new WorldTransform(this.transform);
    this._localBounds = new BoundsCalculation(this._initialBounds, this.transform);
    this._worldBounds = new BoundsCalculation(this._initialBounds, this._worldTransform);
    this._filterWorldBounds = new BoundsCalculation(this._filterBounds, this._worldTransform);
    this.cache = new Cache(this);

    this.cache._addUpdateListener(this._visualUnrelatedUpdate);

    this.render = this._runRender.bind(this, this.render.bind(this));
    this.partRender = this._runPartRender.bind(this, this.partRender.bind(this));
  }

  if (Versioning) DisplayObject.__proto__ = Versioning;
  DisplayObject.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  DisplayObject.prototype.constructor = DisplayObject;
  var prototypeAccessors = {
    alphaRendering: {
      configurable: true
    },
    app: {
      configurable: true
    },
    parent: {
      configurable: true
    },
    filters: {
      configurable: true
    },
    pointerEvents: {
      configurable: true
    },
    cursor: {
      configurable: true
    },
    alpha: {
      configurable: true
    },
    visible: {
      configurable: true
    },
    pivotX: {
      configurable: true
    },
    pivotY: {
      configurable: true
    },
    scaleX: {
      configurable: true
    },
    scaleY: {
      configurable: true
    },
    skewX: {
      configurable: true
    },
    skewY: {
      configurable: true
    },
    rotation: {
      configurable: true
    },
    angle: {
      configurable: true
    },
    x: {
      configurable: true
    },
    y: {
      configurable: true
    },
    width: {
      configurable: true
    },
    height: {
      configurable: true
    },
    worldTransform: {
      configurable: true
    },
    initialBounds: {
      configurable: true
    },
    localBounds: {
      configurable: true
    },
    worldBounds: {
      configurable: true
    },
    filterWorldBounds: {
      configurable: true
    },
    minX: {
      configurable: true
    },
    minY: {
      configurable: true
    },
    maxX: {
      configurable: true
    },
    maxY: {
      configurable: true
    },
    filterBounds: {
      configurable: true
    },
    _filterCacheBounds: {
      configurable: true
    },
    _isCompoundObject: {
      configurable: true
    }
  };

  prototypeAccessors.alphaRendering.get = function alphaRendering() {
    return this._alphaRendering;
  };

  prototypeAccessors.alphaRendering.set = function alphaRendering(value) {
    if (this._alphaRendering === value) {
      return;
    }

    this._alphaRendering = value;

    this._updated();
  };

  prototypeAccessors.app.get = function app() {
    return this._parent instanceof DisplayObject ? this._parent.app : this._parent;
  };

  prototypeAccessors.parent.get = function parent() {
    return this._parent instanceof DisplayObject ? this._parent : null;
  };

  prototypeAccessors.filters.get = function filters() {
    return this._filters;
  };

  prototypeAccessors.filters.set = function filters(value) {
    while (this._filters.length) {
      this.removeFilter(this._filters[0]);
    }

    for (var i = 0, list = value; i < list.length; i += 1) {
      var filter = list[i];
      this.addFilter(filter);
    }
  };

  prototypeAccessors.pointerEvents.get = function pointerEvents() {
    if (this._pointerEvents) {
      return this._pointerEvents;
    }

    return this.parent ? this.parent.pointerEvents : "auto"
    /* AUTO */
    ;
  };

  prototypeAccessors.pointerEvents.set = function pointerEvents(value) {
    this._pointerEvents = value;
  };

  prototypeAccessors.cursor.get = function cursor() {
    if (this._cursor) {
      return this._cursor;
    }

    return this.parent ? this.parent.cursor : "auto";
  };

  prototypeAccessors.cursor.set = function cursor(value) {
    this._cursor = value;
  };

  prototypeAccessors.alpha.get = function alpha() {
    return this._alpha;
  };

  prototypeAccessors.alpha.set = function alpha(value) {
    if (this._alpha === value) {
      return;
    }

    this._alpha = value;

    this._visualUnrelatedUpdate();
  };

  DisplayObject.prototype._updateAlphaToRender = function _updateAlphaToRender() {
    var ref = this;
    var alpha = ref.alpha;
    var parent = ref.parent;
    this._alphaToRender = !parent || parent._shouldRenderInBuffer ? alpha : alpha * parent._alphaToRender;
    return this._alphaToRender;
  };

  DisplayObject.prototype._updateShouldRenderInBuffer = function _updateShouldRenderInBuffer() {
    this._shouldRenderInBuffer = this.cache.isManual || this.filters.length !== 0 || this._alphaToRender < 1 && this._isCompoundObject && this.alphaRendering === AlphaRendering.Whole;
    return this._shouldRenderInBuffer;
  };

  prototypeAccessors.visible.get = function visible() {
    return this._visible;
  };

  prototypeAccessors.visible.set = function visible(value) {
    if (this._visible === value) {
      return;
    }

    this._visible = value;

    this._visualUnrelatedUpdate();

    this._boundsUpdated();
  };

  prototypeAccessors.pivotX.get = function pivotX() {
    return this.transform.pivotX;
  };

  prototypeAccessors.pivotX.set = function pivotX(value) {
    this.transform.pivotX = value;
  };

  prototypeAccessors.pivotY.get = function pivotY() {
    return this.transform.pivotY;
  };

  prototypeAccessors.pivotY.set = function pivotY(value) {
    this.transform.pivotY = value;
  };

  prototypeAccessors.scaleX.get = function scaleX() {
    return this.transform.scaleX;
  };

  prototypeAccessors.scaleX.set = function scaleX(value) {
    this.transform.scaleX = value;
  };

  prototypeAccessors.scaleY.get = function scaleY() {
    return this.transform.scaleY;
  };

  prototypeAccessors.scaleY.set = function scaleY(value) {
    this.transform.scaleY = value;
  };

  prototypeAccessors.skewX.get = function skewX() {
    return this.transform.skewX;
  };

  prototypeAccessors.skewX.set = function skewX(value) {
    this.transform.skewX = value;
  };

  prototypeAccessors.skewY.get = function skewY() {
    return this.transform.skewY;
  };

  prototypeAccessors.skewY.set = function skewY(value) {
    this.transform.skewY = value;
  };

  prototypeAccessors.rotation.get = function rotation() {
    return this.transform.rotation;
  };

  prototypeAccessors.rotation.set = function rotation(value) {
    this.transform.rotation = value;
  };

  prototypeAccessors.angle.get = function angle() {
    return this.transform.rotation * RAD_TO_DEG;
  };

  prototypeAccessors.angle.set = function angle(value) {
    this.transform.rotation = value * DEG_TO_RAD;
  };

  prototypeAccessors.x.get = function x() {
    return this.transform.translateX;
  };

  prototypeAccessors.x.set = function x(value) {
    this.transform.translateX = value;
  };

  prototypeAccessors.y.get = function y() {
    return this.transform.translateY;
  };

  prototypeAccessors.y.set = function y(value) {
    this.transform.translateY = value;
  };

  prototypeAccessors.width.get = function width() {
    return this.scaleX * this.initialBounds.width;
  };

  prototypeAccessors.width.set = function width(value) {
    if (this.initialBounds.width !== 0) {
      this.scaleX = value / this.initialBounds.width;
    }
  };

  prototypeAccessors.height.get = function height() {
    return this.scaleY * this.initialBounds.height;
  };

  prototypeAccessors.height.set = function height(value) {
    if (this.initialBounds.height !== 0) {
      this.scaleY = value / this.initialBounds.height;
    }
  };

  prototypeAccessors.worldTransform.get = function worldTransform() {
    this._worldTransform.updateRecursively();

    return this._worldTransform;
  };

  prototypeAccessors.initialBounds.get = function initialBounds() {
    this._updateInitialBounds();

    return this._initialBounds;
  };

  prototypeAccessors.localBounds.get = function localBounds() {
    this._updateInitialBounds();

    this._localBounds.updateRecursively();

    return this._localBounds.value;
  };

  prototypeAccessors.worldBounds.get = function worldBounds() {
    this._updateInitialBounds();

    this._worldBounds.updateRecursively();

    return this._worldBounds.value;
  };

  prototypeAccessors.filterWorldBounds.get = function filterWorldBounds() {
    this._updateFilterBounds();

    this._filterWorldBounds.updateRecursively();

    return this._filterWorldBounds.value;
  };

  prototypeAccessors.minX.get = function minX() {
    return this.filterWorldBounds.minX;
  };

  prototypeAccessors.minY.get = function minY() {
    return this.filterWorldBounds.minY;
  };

  prototypeAccessors.maxX.get = function maxX() {
    return this.filterWorldBounds.maxX;
  };

  prototypeAccessors.maxY.get = function maxY() {
    return this.filterWorldBounds.maxY;
  };

  prototypeAccessors.filterBounds.get = function filterBounds() {
    this._updateFilterBounds();

    return this._filterBounds;
  };

  prototypeAccessors._filterCacheBounds.get = function _filterCacheBounds() {
    this._updateInitialBounds();

    return this._worldBounds;
  };

  prototypeAccessors._isCompoundObject.get = function _isCompoundObject() {
    return true;
  };

  DisplayObject.prototype.addFilter = function addFilter(filter, index) {
    if (index === void 0) index = this.filters.length;

    filter._addUpdateListener(this._updated);

    this.filters.splice(index, 0, filter);

    this._updated();

    this._filterBoundsDirty = true;
  };

  DisplayObject.prototype.removeFilter = function removeFilter(filter) {
    var index = this.filters.indexOf(filter);

    if (index !== -1) {
      this.filters.splice(index, 1);

      filter._removeUpdateListener(this._updated);
    }

    this._updated();

    this._filterBoundsDirty = true;
  };

  DisplayObject.prototype.contains = function contains(point) {
    var ref = this.worldTransform.applyInverse(point);
    var x = ref.x;
    var y = ref.y;
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  };

  DisplayObject.prototype.emit = function emit(event) {
    var _a;

    event.currentTarget = this;

    this._events.emit(event.type, event);

    if (!event.stopped) {
      (_a = this.parent) == null ? void 0 : _a.emit(event);
    }
  };

  DisplayObject.prototype.dispatch = function dispatch(event) {
    event.target = this;
    this.emit(event);
  };

  DisplayObject.prototype.partRender = function partRender(canvas) {
    if (!this.visible || this.alpha <= 0 || !this.isInvolvedInRendering()) {
      return;
    }

    var renderCanvas = canvas;

    if (!(this instanceof Container)) {
      if (!this.needPartRender) {
        return;
      }
    }

    this.needPartRender = false;
    renderCanvas.globalAlpha = this._updateAlphaToRender();

    this._worldTransform.update();

    if (this.cache.isValid) {
      this.renderCache(renderCanvas);
      return;
    }

    this.cache.clear();

    if (!this._updateShouldRenderInBuffer()) {
      if (renderCanvas.hasContextScale()) {
        renderCanvas.transform = new Matrix().copyFrom(this._worldTransform).scale(renderCanvas.contextScale.sx, renderCanvas.contextScale.sy);
      } else {
        renderCanvas.transform = this._worldTransform;
      }

      return this.draw(renderCanvas, true);
    }

    this._filterCacheBounds.update();

    this.addBoundsRound();
    var bounds = this._filterCacheBounds.value;

    var buffer = this._drawToBuffer(renderCanvas, bounds);

    if (!buffer) {
      return;
    }

    this.applyFilters(buffer, this.filters);
    renderCanvas.transform = Matrix.Identity;
    renderCanvas.mergeCanvas(buffer, bounds.x, bounds.y);

    if (CanvasPoolSingleton.getInstance().sizeAvailable()) {
      this.cache.update(buffer, bounds, this._worldTransform);
    } else {
      CanvasPoolSingleton.getInstance().checkIn(buffer);
    }
  };

  DisplayObject.prototype.renderCache = function renderCache(renderCanvas) {
    renderCanvas.transform = this.cache.getTransform(this._worldTransform);

    if (this.cache.renderBuffer) {
      renderCanvas.mergeCanvas(this.cache.renderBuffer, 0, 0);
    } else if (this.cache.renderImageCache) {
      renderCanvas.mergeImageResource(this.cache.renderImageCache, -this.cache.renderImageInfo.padding.left / this.cache.renderImageInfo.sx, -this.cache.renderImageInfo.padding.top / this.cache.renderImageInfo.sy, this.cache.renderImageInfo.width, this.cache.renderImageInfo.height, this.cache.renderImageInfo.bounds, Cache.options.maxUncompressedBufferSize ? this.cache.renderImageInfo.sx : void 0, Cache.options.maxUncompressedBufferSize ? this.cache.renderImageInfo.sy : void 0);
    }
  };

  DisplayObject.prototype.render = function render(canvas) {
    if (!this.visible || this.alpha <= 0 || !this.isInvolvedInRendering()) {
      return;
    }

    canvas.globalAlpha = this._updateAlphaToRender();

    this._worldTransform.update();

    if (this.cache.isValid) {
      this.renderCache(canvas);
      return;
    }

    this.cache.clear();

    if (!this._updateShouldRenderInBuffer()) {
      if (canvas.hasContextScale()) {
        canvas.transform = new Matrix().copyFrom(this._worldTransform).scale(canvas.contextScale.sx, canvas.contextScale.sy);
      } else {
        canvas.transform = this._worldTransform;
      }

      return this.draw(canvas);
    }

    this._filterCacheBounds.update();

    this.addBoundsRound();
    var bounds = this._filterCacheBounds.value;

    var buffer = this._drawToBuffer(canvas, bounds);

    if (!buffer) {
      return;
    }

    this.applyFilters(buffer, this.filters);
    canvas.transform = Matrix.Identity;
    canvas.mergeCanvas(buffer, bounds.x, bounds.y);

    if (CanvasPoolSingleton.getInstance().sizeAvailable()) {
      this.cache.update(buffer, bounds, this._worldTransform);
    } else {
      CanvasPoolSingleton.getInstance().checkIn(buffer);
    }
  };

  DisplayObject.prototype.renderHit = function renderHit(canvas) {
    if (!this.visible || !this.isInvolvedInRendering()) {
      return;
    }

    this._worldTransform.update();

    canvas.transform = this._worldTransform;
    this.drawHit(canvas);
    this.renderFilterHitArea(canvas);
  };

  DisplayObject.prototype.renderFilterHit = function renderFilterHit(canvas, matrix) {
    if (!isEqualMatrix(matrix, Matrix.Identity)) {
      canvas.transform = new Matrix().copyFrom(this._worldTransform).append(matrix);
      this.drawHit(canvas);
    }
  };

  DisplayObject.prototype.makeCache = function makeCache(requestRender, autoMount) {
    if (requestRender === void 0) requestRender = true;
    if (autoMount === void 0) autoMount = false;
    var ref = this;
    var app = ref.app;

    if (!app) {
      return;
    }

    this._worldTransform.updateRecursively();

    this._filterCacheBounds.update();

    this.addBoundsRound();
    this.cache.isManual = true;
    var bounds = this._filterCacheBounds.value;

    var buffer = this._drawToBuffer(app.view, bounds, autoMount);

    if (!buffer) {
      return;
    }

    this.applyFilters(buffer, this.filters);
    this.cache.update(buffer, bounds, this._worldTransform);

    if (app.hitView) {
      if (!(bounds.width < INVISIBLE_THRESHOLD || bounds.height < INVISIBLE_THRESHOLD)) {
        var hitBuffer = CanvasPoolSingleton.getInstance().checkOut(app.hitView.constructor);
        hitBuffer.resetBufferInfo(app.hitView, bounds.width, bounds.height, app.hitView.resolution);
        hitBuffer.contextTranslate.x = -bounds.x;
        hitBuffer.contextTranslate.y = -bounds.y;
        hitBuffer.transform = this._worldTransform;
        this.drawHit(hitBuffer);
        this.renderFilterHitArea(hitBuffer);
        this.cache.updateHit(hitBuffer);
      }
    }

    if (requestRender) {
      this._visualUnrelatedUpdate();
    }
  };

  DisplayObject.prototype.clearCache = function clearCache(requestRender) {
    if (requestRender === void 0) requestRender = true;
    this.cache.clear();

    if (requestRender) {
      this._visualUnrelatedUpdate();
    }
  };

  DisplayObject.prototype._setParent = function _setParent(value) {
    if (value === this._parent) {
      return;
    }

    this._parent = value;

    if (this.parent) {
      this._worldTransform.setParent(this.parent._worldTransform);

      this.onDidAttach();
    } else {
      this._worldTransform.setParent(null);

      this.onDidDetach();
    }
  };

  DisplayObject.prototype.dispose = function dispose() {
    this.cache.dispose();

    this._events.dispose();

    this.disposeColorKey();

    for (var i = 0, list = this.filters; i < list.length; i += 1) {
      var filter = list[i];

      filter._removeUpdateListener(this._updated);
    }

    this.filters.length = 0;
  };

  DisplayObject.prototype.onTouchStart = function onTouchStart(cb) {
    return this._events.on("onTouchStart", cb);
  };

  DisplayObject.prototype.onTouchCancel = function onTouchCancel(cb) {
    return this._events.on("onTouchCancel", cb);
  };

  DisplayObject.prototype.onTouchMove = function onTouchMove(cb) {
    return this._events.on("onTouchMove", cb);
  };

  DisplayObject.prototype.onTouchEnd = function onTouchEnd(cb) {
    return this._events.on("onTouchEnd", cb);
  };

  DisplayObject.prototype.onMouseMove = function onMouseMove(cb) {
    return this._events.on("onMouseMove", cb);
  };

  DisplayObject.prototype.onMouseDown = function onMouseDown(cb) {
    return this._events.on("onMouseDown", cb);
  };

  DisplayObject.prototype.onContextMenu = function onContextMenu(cb) {
    return this._events.on("onContextMenu", cb);
  };

  DisplayObject.prototype.onMouseUp = function onMouseUp(cb) {
    return this._events.on("onMouseUp", cb);
  };

  DisplayObject.prototype.onWheel = function onWheel(cb) {
    return this._events.on("onWheel", cb);
  };

  DisplayObject.prototype.onClick = function onClick(cb) {
    return this._events.on("onClick", cb);
  };

  DisplayObject.prototype.onDoubleClick = function onDoubleClick(cb) {
    return this._events.on("onDoubleClick", cb);
  };

  DisplayObject.prototype.onDragOver = function onDragOver(cb) {
    return this._events.on("onDragOver", cb);
  };

  DisplayObject.prototype.onDrop = function onDrop(cb) {
    return this._events.on("onDrop", cb);
  };

  DisplayObject.prototype.onMouseEnter = function onMouseEnter(cb) {
    return this._events.on("onMouseEnter", cb);
  };

  DisplayObject.prototype.onMouseLeave = function onMouseLeave(cb) {
    return this._events.on("onMouseLeave", cb);
  };

  DisplayObject.prototype.generateRawRenderData = function generateRawRenderData(canvas) {
    this._worldTransform.update();

    var metadata = {
      type: "DisplayObject"
    };
    return metadata;
  };

  DisplayObject.prototype.draw = function draw(canvas, isPartRender) {};

  DisplayObject.prototype._drawToBuffer = function _drawToBuffer(canvas, bounds, autoMount) {
    if (autoMount === void 0) autoMount = false;

    if (bounds.width < INVISIBLE_THRESHOLD || bounds.height < INVISIBLE_THRESHOLD) {
      return null;
    }

    var ref = Cache.options;
    var maxUncompressedBufferSize = ref.maxUncompressedBufferSize;
    var width = bounds.width;
    var height = bounds.height;

    if (maxUncompressedBufferSize) {
      var ref$1 = this.filterWorldBounds;
      var filterWidth = ref$1.width;
      var filterHeight = ref$1.height;
      width = Math.min(filterWidth, maxUncompressedBufferSize) / filterWidth * width;
      height = Math.min(filterHeight, maxUncompressedBufferSize) / filterHeight * height;
    }

    var buffer = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor, {
      autoMount: autoMount
    });
    buffer.resetBufferInfo(canvas, width, height, canvas.resolution);
    var scaleX = buffer.width / bounds.width;
    var scaleY = buffer.height / bounds.height;
    buffer.contextTranslate.x = -bounds.x * scaleX;
    buffer.contextTranslate.y = -bounds.y * scaleY;
    var matrix = this._worldTransform;

    if (maxUncompressedBufferSize) {
      matrix = _matrix$5.copyFrom(this._worldTransform).scale(scaleX, scaleY);
      buffer.contextScale.sx = width / bounds.width;
      buffer.contextScale.sy = height / bounds.height;
    }

    buffer.transform = matrix;
    this.draw(buffer);
    return buffer;
  };

  DisplayObject.prototype.drawHit = function drawHit(canvas) {
    if (!this.app) {
      return;
    }

    var ref = this.app;
    var hitColorFinder = ref.hitColorFinder;

    if (this.colorKey && hitColorFinder.getTarget(this.colorKey) === this) {
      return;
    }

    this.disposeColorKey();
    var colorKey = hitColorFinder.getRandomHexColor(this);
    this.colorKey = colorKey;

    this.disposeColorKey = function () {
      return hitColorFinder.deleteTarget(colorKey);
    };
  };

  DisplayObject.prototype.updateInitialBounds = function updateInitialBounds(initialBounds) {};

  DisplayObject.prototype._updateInitialBounds = function _updateInitialBounds() {
    if (this._initialBoundsDirty) {
      this._initialBoundsDirty = false;
      this.updateInitialBounds(this._initialBounds);
    }
  };

  DisplayObject.prototype._updateFilterBounds = function _updateFilterBounds() {
    if (this._filterBoundsDirty) {
      this._filterBoundsDirty = false;
      this.updateFilterBounds(this._filterBounds);
    }
  };

  DisplayObject.prototype.updateFilterBounds = function updateFilterBounds(filterBounds) {
    var this$1$1 = this;

    this._updateInitialBounds();

    filterBounds.clear().addBounds(this._initialBounds);

    if (this.filters.length === 0) {
      return;
    }

    this.filters.forEach(function (filter) {
      return filter.updateFilterBounds(this$1$1, filterBounds, this$1$1._initialBounds);
    });
  };

  DisplayObject.prototype.onDidAttach = function onDidAttach() {};

  DisplayObject.prototype.onDidDetach = function onDidDetach() {};

  DisplayObject.prototype.onWillRender = function onWillRender() {};

  DisplayObject.prototype.onDidRender = function onDidRender() {};

  DisplayObject.prototype.applyFilters = function applyFilters(buffer, filters) {
    var this$1$1 = this;

    var _a;

    var ref = ((_a = this.parent) == null ? void 0 : _a._worldTransform) || Matrix.Identity;
    var a = ref.a;
    var b = ref.b;
    var c = ref.c;
    var d = ref.d;
    var sx = Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2));
    var sy = Math.sqrt(Math.pow(c, 2) + Math.pow(d, 2));
    buffer.contextTranslate.x = 0;
    buffer.contextTranslate.y = 0;
    buffer.transform = Matrix.Identity;
    filters.forEach(function (filter) {
      return filter.apply(this$1$1, buffer, sx, sy);
    });
  };

  DisplayObject.prototype._boundsUpdated = function _boundsUpdated(options) {
    var _a;

    this._initialBoundsDirty = true;
    this._filterBoundsDirty = true;

    this._updated(false, options);

    (_a = this.parent) == null ? void 0 : _a._boundsUpdated();
  };

  DisplayObject.prototype._updated = function _updated(notifyParent, options) {
    var _a;

    Versioning.prototype._updated.call(this);

    var ref = this;
    var app = ref.app;

    if (this.isPartRefreshObject && (options == null ? void 0 : options.operationType) === 2
    /* FILL */
    && options.source === this) {
      this.needPartRender = true;
    }

    if ((options == null ? void 0 : options.operationType) !== 2
    /* FILL */
    ) {
      (_a = app == null ? void 0 : app.quadTree) == null ? void 0 : _a.setDirty();
    }

    if (notifyParent !== false && this.parent) {
      this.parent._updated(notifyParent, options);
    } else {
      if (app == null ? void 0 : app.autoRender) {
        app.requestRender(options == null ? void 0 : options.source);
      }
    }
  };

  DisplayObject.prototype.isInvolvedInRendering = function isInvolvedInRendering() {
    return true;
  };

  DisplayObject.prototype.renderFilterHitArea = function renderFilterHitArea(canvas) {
    var this$1$1 = this;

    if (this.filters.length !== 0) {
      this.filters.forEach(function (item) {
        var matrix = item.getFilterMatrix(this$1$1, this$1$1.initialBounds);
        this$1$1.renderFilterHit(canvas, matrix);
      });
      var mergeMatrix = this.filters.reduce(function (matrix, item) {
        return matrix.append(item.getFilterMatrix(this$1$1, this$1$1.initialBounds));
      }, _matrix$5.identity());
      this.renderFilterHit(canvas, mergeMatrix);
    }
  };

  DisplayObject.prototype._runRender = function _runRender(boundRender, canvas) {
    this.onWillRender();
    boundRender(canvas);
    this.onDidRender();
  };

  DisplayObject.prototype._runPartRender = function _runPartRender(boundRender, canvas) {
    this.onWillRender();
    boundRender(canvas);
    this.onDidRender();
  };

  DisplayObject.prototype.addBoundsRound = function addBoundsRound() {
    var bounds = this._filterCacheBounds.value;

    if (bounds.width < INVISIBLE_THRESHOLD || bounds.height < INVISIBLE_THRESHOLD) {
      return;
    }

    bounds.addBoundsRound();
  };

  Object.defineProperties(DisplayObject.prototype, prototypeAccessors);
  return DisplayObject;
}(Versioning);

var DisplayObject = _DisplayObject;

(function () {
  _DisplayObject.prototype.render = /*#__PURE__*/guard(_DisplayObject.prototype.render);
  _DisplayObject.prototype.onDidAttach = /*#__PURE__*/guard(_DisplayObject.prototype.onDidAttach);
  _DisplayObject.prototype.onDidDetach = /*#__PURE__*/guard(_DisplayObject.prototype.onDidDetach);
  _DisplayObject.prototype.dispose = /*#__PURE__*/guard(_DisplayObject.prototype.dispose);
})();

var AlphaRendering = /* @__PURE__ */function (AlphaRendering2) {
  AlphaRendering2["Separated"] = "separated";
  AlphaRendering2["Whole"] = "whole";
  return AlphaRendering2;
}(AlphaRendering || {});

var _matrix$5 = /*#__PURE__*/new Matrix();

injectInspector(DisplayObject, ["alpha", "alphaRendering", ["bounds (initial)", "initialBounds"], ["bounds (local)", "localBounds"], ["bounds (world)", "worldBounds"], "visible", "filters", "pointerEvents", "cursor", "transform", ["transform (matrix)", function (obj) {
  return obj.transform.matrix;
}], ["transform (world)", "worldTransform"]]);

var ColorFill = /*@__PURE__*/function (Fill) {
  function ColorFill(color) {
    if (color === void 0) color = Color.Black;
    Fill.call(this);
    this._color = color;
  }

  if (Fill) ColorFill.__proto__ = Fill;
  ColorFill.prototype = /*#__PURE__*/Object.create(Fill && Fill.prototype);
  ColorFill.prototype.constructor = ColorFill;
  var prototypeAccessors = {
    color: {
      configurable: true
    }
  };

  prototypeAccessors.color.get = function color() {
    return this._color;
  };

  prototypeAccessors.color.set = function color(value) {
    if (this._color === value) {
      return;
    }

    this._color = value;

    this._updated();
  };

  ColorFill.prototype.isEmpty = function isEmpty() {
    return this.color.alpha === 0;
  };

  ColorFill.prototype.generateRawRenderData = function generateRawRenderData() {
    return {
      solidFill: {
        color: this.color.hex,
        alpha: this.alpha
      }
    };
  };

  ColorFill.prototype.getNative = function getNative() {
    return this.color;
  };

  Object.defineProperties(ColorFill.prototype, prototypeAccessors);
  return ColorFill;
}(Fill);
injectInspector(ColorFill, [["$", function () {
  return ColorFill.name;
}], "color"]);

var LineStyleProxy = /*@__PURE__*/function () {
  function LineStyleProxy(strokeStyle) {
    this.strokeStyle = strokeStyle;
  }

  var prototypeAccessors = {
    color: {
      configurable: true
    },
    width: {
      configurable: true
    },
    lineCap: {
      configurable: true
    },
    lineJoin: {
      configurable: true
    },
    miterLimit: {
      configurable: true
    },
    lineDash: {
      configurable: true
    },
    lineDashOffset: {
      configurable: true
    },
    alignment: {
      configurable: true
    }
  };

  prototypeAccessors.color.get = function color() {
    var ref = this.strokeStyle;
    var fill = ref.fill;

    if (fill === Fill.None) {
      return null;
    }

    return fill instanceof ColorFill ? fill.color : Color.Transparent;
  };

  prototypeAccessors.color.set = function color(value) {
    this.strokeStyle.fill = value === null ? Fill.None : new ColorFill(value);
  };

  prototypeAccessors.width.get = function width() {
    return this.strokeStyle.width;
  };

  prototypeAccessors.width.set = function width(value) {
    this.strokeStyle.width = value;
  };

  prototypeAccessors.lineCap.get = function lineCap() {
    return this.strokeStyle.lineCap;
  };

  prototypeAccessors.lineCap.set = function lineCap(value) {
    this.strokeStyle.lineCap = value;
  };

  prototypeAccessors.lineJoin.get = function lineJoin() {
    return this.strokeStyle.lineJoin;
  };

  prototypeAccessors.lineJoin.set = function lineJoin(value) {
    this.strokeStyle.lineJoin = value;
  };

  prototypeAccessors.miterLimit.get = function miterLimit() {
    return this.strokeStyle.miterLimit;
  };

  prototypeAccessors.miterLimit.set = function miterLimit(value) {
    this.strokeStyle.miterLimit = value;
  };

  prototypeAccessors.lineDash.get = function lineDash() {
    return this.strokeStyle.dash;
  };

  prototypeAccessors.lineDash.set = function lineDash(value) {
    this.strokeStyle.dash = value;
  };

  prototypeAccessors.lineDashOffset.get = function lineDashOffset() {
    return this.strokeStyle.dashOffset;
  };

  prototypeAccessors.lineDashOffset.set = function lineDashOffset(value) {
    this.strokeStyle.dashOffset = value;
  };

  prototypeAccessors.alignment.get = function alignment() {
    return 0.5;
  };

  prototypeAccessors.alignment.set = function alignment(value) {
    throw new Error("Cannot set alignment");
  };

  Object.defineProperties(LineStyleProxy.prototype, prototypeAccessors);
  return LineStyleProxy;
}();
var TextStyleProxy = /*@__PURE__*/function () {
  function TextStyleProxy(textStyle) {
    this.textStyle = textStyle;
    this.underline = new LineStyleProxy(textStyle.underline);
    this.lineThrough = new LineStyleProxy(textStyle.lineThrough);
  }

  var prototypeAccessors$1 = {
    fontFamily: {
      configurable: true
    },
    fontSize: {
      configurable: true
    },
    fontWeight: {
      configurable: true
    },
    fontStyle: {
      configurable: true
    },
    textBaseline: {
      configurable: true
    },
    font: {
      configurable: true
    }
  };

  prototypeAccessors$1.fontFamily.get = function () {
    return this.textStyle.fontFamily;
  };

  prototypeAccessors$1.fontFamily.set = function (value) {
    this.textStyle.fontFamily = value;
  };

  prototypeAccessors$1.fontSize.get = function () {
    return this.textStyle.fontSize;
  };

  prototypeAccessors$1.fontSize.set = function (value) {
    this.textStyle.fontSize = value;
  };

  prototypeAccessors$1.fontWeight.get = function () {
    return this.textStyle.fontWeight;
  };

  prototypeAccessors$1.fontWeight.set = function (value) {
    this.textStyle.fontWeight = value;
  };

  prototypeAccessors$1.fontStyle.get = function () {
    return this.textStyle.fontStyle;
  };

  prototypeAccessors$1.fontStyle.set = function (value) {
    this.textStyle.fontStyle = value;
  };

  prototypeAccessors$1.textBaseline.get = function () {
    return this.textStyle.textBaseline;
  };

  prototypeAccessors$1.textBaseline.set = function (value) {
    this.textStyle.textBaseline = value;
  };

  prototypeAccessors$1.font.get = function () {
    return this.textStyle.font;
  };

  Object.defineProperties(TextStyleProxy.prototype, prototypeAccessors$1);
  return TextStyleProxy;
}();

var FillStrokeAble = /*@__PURE__*/function (DisplayObject) {
  function FillStrokeAble() {
    var this$1$1 = this;
    DisplayObject.call(this);
    this.fillFilters = [];
    this.strokeStyle = new StrokeStyle();
    this._fill = Fill.None;
    this._strokeBounds = new Bounds();
    this._strokeBoundsDirty = true;
    this.lineStyle = new LineStyleProxy(this.strokeStyle);

    this.strokeStyle._addUpdateListener(function () {
      return this$1$1._strokeUpdated();
    });

    this._strokeWorldBounds = new BoundsCalculation(this._strokeBounds, this.worldTransform);
    this._alphaRendering = AlphaRendering.Whole;
  }

  if (DisplayObject) FillStrokeAble.__proto__ = DisplayObject;
  FillStrokeAble.prototype = /*#__PURE__*/Object.create(DisplayObject && DisplayObject.prototype);
  FillStrokeAble.prototype.constructor = FillStrokeAble;
  var prototypeAccessors = {
    fill: {
      configurable: true
    },
    stroke: {
      configurable: true
    },
    strokeWidth: {
      configurable: true
    },
    strokeLineJoin: {
      configurable: true
    },
    strokeLineCap: {
      configurable: true
    },
    strokeMiterLimit: {
      configurable: true
    },
    strokeDash: {
      configurable: true
    },
    strokeDashOffset: {
      configurable: true
    },
    strokeBounds: {
      configurable: true
    },
    _filterCacheBounds: {
      configurable: true
    },
    _isCompoundObject: {
      configurable: true
    }
  };

  FillStrokeAble.prototype.addFillFilter = function addFillFilter(filter, index) {
    if (index === void 0) index = this.fillFilters.length;

    filter._addUpdateListener(this._updated);

    this.fillFilters.splice(index, 0, filter);

    this._updated(void 0);
  };

  FillStrokeAble.prototype.removeFillFilter = function removeFillFilter(filter) {
    var index = this.fillFilters.indexOf(filter);

    if (index !== -1) {
      this.fillFilters.splice(index, 1);

      filter._removeUpdateListener(this._updated);

      this._updated(void 0);
    }
  };

  prototypeAccessors.fill.get = function fill() {
    return this._fill;
  };

  prototypeAccessors.fill.set = function fill(value) {
    if (this._fill === value) {
      return;
    }

    this._fill._removeUpdateListener(this._updated);

    value._addUpdateListener(this._updated);

    this._fill = value;

    this._updated(void 0, {
      source: this,
      operationType: E_OPERATION_TYPE.FILL
    });
  };

  prototypeAccessors.stroke.get = function stroke() {
    return this.strokeStyle.fill;
  };

  prototypeAccessors.stroke.set = function stroke(value) {
    this.strokeStyle.fill = value;
  };

  prototypeAccessors.strokeWidth.get = function strokeWidth() {
    return this.strokeStyle.width;
  };

  prototypeAccessors.strokeWidth.set = function strokeWidth(value) {
    this.strokeStyle.width = value;
  };

  prototypeAccessors.strokeLineJoin.get = function strokeLineJoin() {
    return this.strokeStyle.lineJoin;
  };

  prototypeAccessors.strokeLineJoin.set = function strokeLineJoin(value) {
    this.strokeStyle.lineJoin = value;
  };

  prototypeAccessors.strokeLineCap.get = function strokeLineCap() {
    return this.strokeStyle.lineCap;
  };

  prototypeAccessors.strokeLineCap.set = function strokeLineCap(value) {
    this.strokeStyle.lineCap = value;
  };

  prototypeAccessors.strokeMiterLimit.get = function strokeMiterLimit() {
    return this.strokeStyle.miterLimit;
  };

  prototypeAccessors.strokeMiterLimit.set = function strokeMiterLimit(value) {
    this.strokeStyle.miterLimit = value;
  };

  prototypeAccessors.strokeDash.get = function strokeDash() {
    return this.strokeStyle.dash;
  };

  prototypeAccessors.strokeDash.set = function strokeDash(value) {
    this.strokeStyle.dash = value;
  };

  prototypeAccessors.strokeDashOffset.get = function strokeDashOffset() {
    return this.strokeStyle.dashOffset;
  };

  prototypeAccessors.strokeDashOffset.set = function strokeDashOffset(value) {
    this.strokeStyle.dashOffset = value;
  };

  prototypeAccessors.strokeBounds.get = function strokeBounds() {
    this._updateStrokeBounds();

    return this._strokeBounds;
  };

  prototypeAccessors._filterCacheBounds.get = function _filterCacheBounds() {
    this._updateStrokeBounds();

    return this._strokeWorldBounds;
  };

  prototypeAccessors._isCompoundObject.get = function _isCompoundObject() {
    return !this.fill.isEmpty() && !this.strokeStyle.isEmpty();
  };

  FillStrokeAble.prototype.dispose = function dispose() {
    DisplayObject.prototype.dispose.call(this);
    this.strokeStyle.dispose();

    this._fill._removeUpdateListener(this._updated);

    for (var i = 0, list = this.fillFilters; i < list.length; i += 1) {
      var filter = list[i];

      filter._removeUpdateListener(this._updated);
    }

    this.fillFilters.length = 0;
  };

  FillStrokeAble.prototype.updateStrokeBounds = function updateStrokeBounds(strokeBounds) {
    strokeBounds.clear().addBounds(this.initialBounds);
  };

  FillStrokeAble.prototype._updateStrokeBounds = function _updateStrokeBounds() {
    if (this._strokeBoundsDirty) {
      this._strokeBoundsDirty = false;
      this.updateStrokeBounds(this._strokeBounds);
    }
  };

  FillStrokeAble.prototype._boundsUpdated = function _boundsUpdated() {
    this._strokeBoundsDirty = true;

    DisplayObject.prototype._boundsUpdated.call(this);
  };

  FillStrokeAble.prototype._strokeUpdated = function _strokeUpdated() {
    var _a;

    this._strokeBoundsDirty = true;

    this._updated();

    (_a = this.parent) == null ? void 0 : _a._boundsUpdated();
  };

  FillStrokeAble.prototype._updateShouldRenderInBuffer = function _updateShouldRenderInBuffer() {
    return this.fillFilters.length !== 0 || DisplayObject.prototype._updateShouldRenderInBuffer.call(this);
  };

  Object.defineProperties(FillStrokeAble.prototype, prototypeAccessors);
  return FillStrokeAble;
}(DisplayObject);
injectInspector(FillStrokeAble, [["lineStyle", false], ["bounds (stroke)", "strokeBounds"], "fill", "stroke", "strokeStyle"]);

var Container = /*@__PURE__*/function (DisplayObject) {
  function Container() {
    DisplayObject.apply(this, arguments);
    this.children = [];
    this.forceRenderChildren = false;
    this._clipPath = "";
    this._clipSegments = [];
    this._clipSegmentsDirty = true;
  }

  if (DisplayObject) Container.__proto__ = DisplayObject;
  Container.prototype = /*#__PURE__*/Object.create(DisplayObject && DisplayObject.prototype);
  Container.prototype.constructor = Container;
  var prototypeAccessors = {
    clipPath: {
      configurable: true
    },
    clipSegments: {
      configurable: true
    }
  };

  Container.prototype.addChild = function addChild(child) {
    var _a;

    (_a = child.parent) == null ? void 0 : _a.removeChild(child);

    child._setParent(this);

    this.children.push(child);

    this._boundsUpdated();

    this.insertElementInQuadTree(child);
  };

  Container.prototype.insertElementInQuadTree = function insertElementInQuadTree(child) {
    var this$1$1 = this;

    var _a;

    if ((_a = this.app) == null ? void 0 : _a.quadTree) {
      if (child instanceof Container) {
        child.children.forEach(function (grandson) {
          this$1$1.insertElementInQuadTree(grandson);
        });
      } else {
        this.app.quadTree.insertElement(child);
      }
    }
  };

  Container.prototype.addChildAt = function addChildAt(child, index) {
    var _a;

    if (index < 0 || index > this.children.length) {
      throw new Error("addChildAt: The index " + index + " supplied is out of bounds " + this.children.length);
    }

    (_a = child.parent) == null ? void 0 : _a.removeChild(child);

    child._setParent(this);

    this.children.splice(index, 0, child);

    this._boundsUpdated();

    this.insertElementInQuadTree(child);
  };

  Container.prototype.removeChild = function removeChild(child) {
    if (child.parent !== this) {
      return;
    }

    this.removeElementInQuadTree(child);

    child._setParent(null);

    var index = this.children.indexOf(child);
    this.children.splice(index, 1);

    this._boundsUpdated();
  };

  Container.prototype.removeElementInQuadTree = function removeElementInQuadTree(child) {
    var this$1$1 = this;

    var _a;

    if ((_a = this.app) == null ? void 0 : _a.quadTree) {
      if (child instanceof Container) {
        child.children.forEach(function (grandson) {
          this$1$1.removeElementInQuadTree(grandson);
        });
      } else {
        this.app.quadTree.removeElement(child);
      }
    }
  };

  prototypeAccessors.clipPath.get = function clipPath() {
    return this._clipPath;
  };

  prototypeAccessors.clipPath.set = function clipPath(value) {
    if (this._clipPath === value) {
      return;
    }

    this._clipPath = value;
    this._clipSegmentsDirty = true;

    this._boundsUpdated();
  };

  prototypeAccessors.clipSegments.get = function clipSegments() {
    if (this._clipSegmentsDirty) {
      this._clipSegmentsDirty = false;
      this._clipSegments = PathParser.parse(this._clipPath);
    }

    return this._clipSegments;
  };

  Container.prototype.dispose = function dispose() {
    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];
      child.dispose();
    }

    DisplayObject.prototype.dispose.call(this);
  };

  Container.prototype.renderFilterHit = function renderFilterHit(canvas, matrix) {
    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];
      child.renderFilterHit(canvas, matrix);
    }
  };

  Container.prototype.generateRawRenderData = function generateRawRenderData(canvas) {
    var metadata = DisplayObject.prototype.generateRawRenderData.call(this, canvas) || {};
    var type = "Container";
    var children = [];

    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];
      var renderData = child.generateRawRenderData(canvas);

      if (renderData) {
        if (renderData.type === type) {
          children.push.apply(children, renderData.children);
        } else {
          children.push(renderData);
        }
      }
    }

    metadata.type = type;
    metadata.children = children;
    return metadata;
  };

  Container.prototype.draw = function draw(canvas, isPartRender) {
    this.drawClipPath(canvas);

    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];

      if (isPartRender) {
        child.partRender(canvas);
      } else {
        child.render(canvas);
      }
    }

    this.restoreClipPath(canvas);
  };

  Container.prototype.drawHit = function drawHit(canvas) {
    this.drawClipPath(canvas);

    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];
      child.renderHit(canvas);
    }

    this.restoreClipPath(canvas);
  };

  Container.prototype.updateInitialBounds = function updateInitialBounds(bounds) {
    bounds.clear();

    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];

      if (!child.visible) {
        continue;
      }

      if (child instanceof FillStrokeAble) {
        bounds.addBoundsMatrix(child.transform.matrix, child.strokeBounds);
      } else {
        bounds.addBounds(child.localBounds);
      }
    }
  };

  Container.prototype.updateFilterBounds = function updateFilterBounds(bounds) {
    bounds.clear();
    DisplayObject.prototype.updateFilterBounds.call(this, bounds);

    for (var i = 0, list = this.children; i < list.length; i += 1) {
      var child = list[i];

      if (!child.visible) {
        continue;
      }

      bounds.addBoundsMatrix(child.transform.matrix, child.filterBounds);
    }
  };

  Container.prototype.isInvolvedInRendering = function isInvolvedInRendering() {
    var ref = this.app || {};
    var width = ref.width;
    if (width === void 0) width = 0;
    var height = ref.height;
    if (height === void 0) height = 0;
    var notRenderOutsideView = ref.notRenderOutsideView;

    if (!notRenderOutsideView || this.forceRenderOutsideView || this.isParentForceRender()) {
      return true;
    }

    var ref$1 = this.filterWorldBounds;
    var minX = ref$1.minX;
    var minY = ref$1.minY;
    var maxX = ref$1.maxX;
    var maxY = ref$1.maxY;
    var isInRenderArea = maxX > 0 && maxY > 0 && minX < width && minY < height;
    this.forceRenderChildren = this.filters.length > 0 && isInRenderArea;
    return isInRenderArea;
  };

  Container.prototype.isParentForceRender = function isParentForceRender() {
    var _a, _b;

    if ((_a = this.parent) == null ? void 0 : _a.forceRenderChildren) {
      return true;
    }

    return !!((_b = this.parent) == null ? void 0 : _b.isParentForceRender());
  };

  Container.prototype.drawClipPath = function drawClipPath(canvas) {
    if (this.clipPath) {
      canvas.save();
      this.applyClipPath(canvas);
      canvas.clip();
    }
  };

  Container.prototype.restoreClipPath = function restoreClipPath(canvas) {
    if (this.clipPath) {
      canvas.restore();
    }
  };

  Container.prototype.applyClipPath = function applyClipPath(canvas) {
    drawPath(canvas, this.clipSegments);
  };

  Object.defineProperties(Container.prototype, prototypeAccessors);
  return Container;
}(DisplayObject);
var Component = /*@__PURE__*/function (Container) {
  function Component() {
    Container.apply(this, arguments);
  }

  if (Container) Component.__proto__ = Container;
  Component.prototype = /*#__PURE__*/Object.create(Container && Container.prototype);
  Component.prototype.constructor = Component;
  return Component;
}(Container);

var wrappers = /* @__PURE__ */new WeakMap();
function wrap(obj) {
  if (!wrappers.has(obj)) {
    var wrapper;

    if (obj instanceof Container) {
      wrapper = new ContainerWrapper(obj);
    } else {
      wrapper = new ObjectWrapper(obj);
    }

    wrappers.set(obj, wrapper);
  }

  return wrappers.get(obj);
}
var ObjectWrapper = /*@__PURE__*/function () {
  function ObjectWrapper(obj) {
    this.obj = obj;
    this._currentElement = {
      type: Object.getPrototypeOf(obj).constructor,

      get key() {
        return obj.id || void 0;
      },

      props: inspect(obj)
    };
  }

  var prototypeAccessors = {
    _instance: {
      configurable: true
    }
  };

  prototypeAccessors._instance.get = function _instance() {
    return this.obj;
  };

  ObjectWrapper.prototype.getPublicInstance = function getPublicInstance() {
    return this;
  };

  ObjectWrapper.prototype.dispose = function dispose() {
    wrappers.delete(this.obj);
  };

  Object.defineProperties(ObjectWrapper.prototype, prototypeAccessors);
  return ObjectWrapper;
}();
var ContainerWrapper = /*@__PURE__*/function (ObjectWrapper) {
  function ContainerWrapper(obj) {
    var this$1$1 = this;
    ObjectWrapper.call(this, obj);
    this._renderedChildren = [];

    this.handleContainerUpdate = function () {
      var _a;

      (_a = this$1$1.onChildrenChange) == null ? void 0 : _a.call(this$1$1);
    };

    obj._addUpdateListener(this.handleContainerUpdate);
  }

  if (ObjectWrapper) ContainerWrapper.__proto__ = ObjectWrapper;
  ContainerWrapper.prototype = /*#__PURE__*/Object.create(ObjectWrapper && ObjectWrapper.prototype);
  ContainerWrapper.prototype.constructor = ContainerWrapper;

  ContainerWrapper.prototype.computeChildren = function computeChildren() {
    this._renderedChildren = this.obj.children.map(wrap);
  };

  ContainerWrapper.prototype.getChildrenRefs = function getChildrenRefs() {
    return this.obj.children.map(wrap);
  };

  ContainerWrapper.prototype.diff = function diff() {
    var newRenderedChildren = this.getChildrenRefs();
    var adds = exclude(newRenderedChildren, this._renderedChildren);
    var removes = exclude(this._renderedChildren, newRenderedChildren);
    return {
      adds: adds,
      removes: removes
    };
  };

  ContainerWrapper.prototype.dispose = function dispose() {
    ObjectWrapper.prototype.dispose.call(this);

    this.obj._removeUpdateListener(this.handleContainerUpdate);
  };

  return ContainerWrapper;
}(ObjectWrapper);

function exclude(a, b) {
  var rest = new Set(b);
  return a.filter(function (element) {
    return !rest.has(element);
  });
}

var ComponentTree = /*@__PURE__*/function () {
  function ComponentTree(devtool) {
    this.devtool = devtool;
  }

  ComponentTree.prototype.getClosestInstanceFromNode = function getClosestInstanceFromNode(node) {
    var app = this.devtool.apps.find(function (app2) {
      return app2.view.native === node;
    });
    return app ? wrap(app) : null;
  };

  ComponentTree.prototype.getNodeFromInstance = function getNodeFromInstance(ref) {
    if (ref.obj instanceof Application) {
      return this.getCanvasElement(ref.obj);
    }

    if (ref.obj instanceof DisplayObject) {
      return this.showOverlay(ref.obj);
    }

    return null;
  };

  ComponentTree.prototype.getCanvasElement = function getCanvasElement(app) {
    var native = app == null ? void 0 : app.view.native;

    if ((native == null ? void 0 : native.tagName) === "CANVAS") {
      return native;
    }

    return null;
  };

  ComponentTree.prototype.setupOverlay = function setupOverlay(obj) {
    var canvas = this.getCanvasElement(obj.app);

    if (!canvas) {
      return null;
    }

    var document = canvas.ownerDocument;
    var overlay = document.querySelector("#alloy-renderer-overlay");

    if (overlay) {
      return overlay;
    }

    var newOverlay = document.createElement("div");
    newOverlay.id = "alloy-renderer-overlay";
    Object.assign(newOverlay.style, {
      pointerEvents: "none",
      transformOrigin: "0 0",
      position: "fixed",
      left: "0",
      top: "0",
      width: "1px",
      height: "1px",
      zIndex: -1
    });
    document.body.appendChild(newOverlay);
    return newOverlay;
  };

  ComponentTree.prototype.showOverlay = function showOverlay(obj) {
    var overlay = this.setupOverlay(obj);

    if (overlay) {
      var ref = obj.app;
      var view = ref.view;
      var canvasBounds = view.native.getBoundingClientRect();
      var sx = canvasBounds.width / view.width;
      var sy = canvasBounds.height / view.height;
      overlay.style.transform = ["translate(" + (obj.worldBounds.x * sx + canvasBounds.x) + "px, " + (obj.worldBounds.y * sy + canvasBounds.y) + "px)", "scale(" + obj.worldBounds.width * sx + ", " + obj.worldBounds.height * sy + ")"].join(" ");
    }

    return overlay;
  };

  return ComponentTree;
}();

var Reconciler = /*@__PURE__*/function () {
  function Reconciler() {}

  Reconciler.prototype.mountComponent = function mountComponent(wrapper, unused2, unused3, hostContainerInfo) {
    var this$1$1 = this;

    if (wrapper.obj instanceof Application) {
      var app = wrapper.obj;
      this.mountComponent(wrap(app.stage), null, null, hostContainerInfo);
    }

    if (wrapper instanceof ContainerWrapper) {
      var container = wrapper.obj;

      wrapper.onChildrenChange = function () {
        return this$1$1.performUpdateIfNecessary(wrap(container), null, null, hostContainerInfo);
      };

      wrapper.computeChildren();
      container.children.forEach(function (child) {
        this$1$1.mountComponent(wrap(child), null, null, hostContainerInfo);
      });
    }
  };

  Reconciler.prototype.performUpdateIfNecessary = function performUpdateIfNecessary(wrapper, unused2, unused3, hostContainerInfo) {
    var this$1$1 = this;
    var ref = wrapper.diff();
    var adds = ref.adds;
    var removes = ref.removes;
    adds.forEach(function (child) {
      return this$1$1.mountComponent(child, null, null, hostContainerInfo);
    });
    removes.forEach(function (child) {
      return this$1$1.unmountRecursively(child);
    });
    wrapper.computeChildren();
  };

  Reconciler.prototype.unmountComponent = function unmountComponent(ref) {
    ref.dispose();
  };

  Reconciler.prototype.unmountRecursively = function unmountRecursively(ref) {
    var this$1$1 = this;
    var obj = ref.obj;

    if (obj instanceof Container || obj instanceof Application) {
      var children = "stage" in obj ? [obj.stage] : obj.children;
      children.forEach(function (child) {
        return this$1$1.unmountRecursively(wrap(child));
      });
    }

    this.unmountComponent(ref);
  };

  return Reconciler;
}();

var Devtool = /*@__PURE__*/function () {
  function Devtool() {
    this.version = "15";
    this.bundleType = 1;
    this.ComponentTree = new ComponentTree(this);
    this.Mount = {};
    this.Reconciler = new Reconciler();
    this.root = new DevtoolRoot();
    this.rootWrapper = wrap(this.root);
    this.hostContainerInfo = {
      _topLevelWrapper: this
    };
  }

  var prototypeAccessors = {
    apps: {
      configurable: true
    }
  };

  prototypeAccessors.apps.get = function apps() {
    return this.root.children;
  };

  Devtool.prototype.addApplication = function addApplication(app) {
    this.root.addApplication(app);
  };

  Devtool.prototype.removeApplication = function removeApplication(app) {
    this.root.removeApplication(app);
  };

  Devtool.prototype.onAttached = function onAttached() {
    this.Reconciler.mountComponent(this.rootWrapper, null, null, this.hostContainerInfo);
  };

  Object.defineProperties(Devtool.prototype, prototypeAccessors);
  return Devtool;
}();
function injectDisplayName(ctor, displayName) {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  Object.assign(ctor, {
    displayName: displayName
  });
}

var appToObj = function (app) {
  return app;
};

var DevtoolRoot = /*@__PURE__*/function (Container) {
  function DevtoolRoot() {
    Container.apply(this, arguments);
  }

  if (Container) DevtoolRoot.__proto__ = Container;
  DevtoolRoot.prototype = /*#__PURE__*/Object.create(Container && Container.prototype);
  DevtoolRoot.prototype.constructor = DevtoolRoot;

  DevtoolRoot.prototype.addApplication = function addApplication(app) {
    this.children.push(app);

    this._updated();
  };

  DevtoolRoot.prototype.removeApplication = function removeApplication(app) {
    var index = this.children.indexOf(appToObj(app));
    this.children.splice(index, 1);

    this._updated();
  };

  return DevtoolRoot;
}(Container);

var devtools;

if (process.env.NODE_ENV !== "production") {
  devtools = /*#__PURE__*/new Devtool();
  void Promise.resolve().then(function () {
    var _a;

    var hook = typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ !== "undefined" ? __REACT_DEVTOOLS_GLOBAL_HOOK__ : void 0;
    (_a = hook == null ? void 0 : hook.on) == null ? void 0 : _a.call(hook, "renderer-attached", function (attached) {
      if (attached.renderer !== devtools) {
        return;
      }

      devtools.onAttached();
    });
    hook == null ? void 0 : hook.inject(devtools);
  });
}

var HitColorFinder = /*@__PURE__*/function () {
  function HitColorFinder() {
    this.pool = {
      "#000000": null
    };
  }

  HitColorFinder.prototype.getTarget = function getTarget(colorKey) {
    return this.pool[colorKey];
  };

  HitColorFinder.prototype.deleteTarget = function deleteTarget(colorKey) {
    this.pool[colorKey] = void 0;
  };

  HitColorFinder.prototype.getRandomHexColor = function getRandomHexColor(obj) {
    var randColor = (Math.random() * 16777215 << 0).toString(16);

    while (randColor.length < 6) {
      randColor = "0" + randColor;
    }

    randColor = "#" + randColor;

    if (this.pool[randColor]) {
      randColor = this.getRandomHexColor(obj);
    } else {
      this.pool[randColor] = obj;
    }

    return randColor;
  };

  HitColorFinder.prototype.dispose = function dispose() {
    this.pool = {};
  };

  return HitColorFinder;
}();
injectInspector(HitColorFinder);

var InteractionManager = /*@__PURE__*/function () {
  function InteractionManager(app) {
    var this$1$1 = this;
    this.lastTarget = null;
    this.pointerDownTarget = null;
    this.pointerUpTarget = null;
    this.lastClickTime = 0;
    this.isDragging = false;
    this.unDispatchedClickEvent = null;
    this.unDispatchedDoubleClickEvent = null;

    this.onEvents = function (events) {
      var _a, _b;

      var lastTarget = null;

      for (var i = 0, list = events; i < list.length; i += 1) {
        var event = list[i];
        var point = new Point(event.worldX, event.worldY);

        if (!this$1$1.isDragging && !this$1$1.isPointInView(point)) {
          continue;
        }

        var target = this$1$1.findTarget(point);
        lastTarget = target;

        if ((target == null ? void 0 : target.pointerEvents) === PointerEvents.NONE) {
          continue;
        }

        if (event.type === "onClick") {
          if (!this$1$1.unDispatchedClickEvent) {
            continue;
          }

          this$1$1.unDispatchedClickEvent.nativeEvent = event.nativeEvent;
          (_a = this$1$1.pointerUpTarget) == null ? void 0 : _a.dispatch(this$1$1.unDispatchedClickEvent);
          this$1$1.unDispatchedClickEvent = null;
          continue;
        }

        if (event.type === "onDoubleClick") {
          if (!this$1$1.unDispatchedDoubleClickEvent) {
            continue;
          }

          this$1$1.unDispatchedDoubleClickEvent.nativeEvent = event.nativeEvent;
          (_b = this$1$1.pointerUpTarget) == null ? void 0 : _b.dispatch(this$1$1.unDispatchedDoubleClickEvent);
          this$1$1.unDispatchedDoubleClickEvent = null;
          continue;
        }

        if (event.type === "onDragOver") {
          event.preventDefault();
        }

        target == null ? void 0 : target.dispatch(event);

        if (event.type === "onMouseMove") {
          this$1$1.app.view.cursor = (target == null ? void 0 : target.cursor) || "auto";
          this$1$1.onMouseEnterAndLeave(target, event);
        }

        if (event.type === "onMouseDown") {
          this$1$1.pointerDownTarget = target;
        }

        if (event.type === "onMouseUp") {
          this$1$1.onClick(target, event);
        }
      }

      this$1$1.lastTarget = lastTarget;
    };

    this.onMouseEnterAndLeave = function (target, event) {
      if (target !== this$1$1.lastTarget) {
        var commonAncestor = this$1$1.findCommonAncestor(target, this$1$1.lastTarget);
        var leaveStack = [];
        var targetParent = target;
        var lastTargetParent = this$1$1.lastTarget;

        while (targetParent && targetParent !== commonAncestor) {
          var mouseEnterInteractionEvent = this$1$1.generateInteractionEvent(event, "mouseenter");
          mouseEnterInteractionEvent.stopped = true;
          targetParent.dispatch(mouseEnterInteractionEvent);
          targetParent = targetParent.parent;
        }

        while (lastTargetParent && lastTargetParent !== commonAncestor) {
          leaveStack.push(lastTargetParent);
          lastTargetParent = lastTargetParent.parent;
        }

        for (var i = leaveStack.length - 1; i >= 0; i--) {
          var mouseLeaveInteractionEvent = this$1$1.generateInteractionEvent(event, "mouseleave");
          mouseLeaveInteractionEvent.stopped = true;
          leaveStack[i].dispatch(mouseLeaveInteractionEvent);
        }
      }
    };

    this.onClick = function (target, event) {
      if (!target) {
        this$1$1.pointerUpTarget = null;
        return;
      }

      this$1$1.pointerUpTarget = target;

      if (this$1$1.pointerDownTarget !== target) {
        return;
      }

      var clickInteractionEvent = this$1$1.generateInteractionEvent(event, "click");
      this$1$1.unDispatchedClickEvent = clickInteractionEvent;
      var doubleClickInteractionEvent = this$1$1.generateInteractionEvent(event, "dblclick");
      this$1$1.unDispatchedDoubleClickEvent = doubleClickInteractionEvent;
    };

    this.app = app;
  }

  InteractionManager.prototype.dispose = function dispose() {
    this.lastTarget = null;
    this.pointerDownTarget = null;
    this.pointerUpTarget = null;
    this.lastClickTime = 0;
    this.isDragging = false;
    this.unDispatchedClickEvent = null;
    this.unDispatchedDoubleClickEvent = null;
  };

  InteractionManager.prototype.findTarget = function findTarget(point) {
    var ref = this.app;
    var hitView = ref.hitView;
    var hitColorFinder = ref.hitColorFinder;

    if (!hitView) {
      return null;
    }

    var x = Math.round(point.x * hitView.resolution);
    var y = Math.round(point.y * hitView.resolution);
    var pointImageData = hitView.getImageData(x, y, 1, 1).data;
    var pointColor = Color.fromRGB(pointImageData[0], pointImageData[1], pointImageData[2]);
    var pointColorHex = pointColor.hex.substring(0, 7);
    var target = hitColorFinder.getTarget(pointColorHex);
    return target === void 0 ? this.lastTarget : target;
  };

  InteractionManager.prototype.findCommonAncestor = function findCommonAncestor(displayObject1, displayObject2) {
    var parent1 = displayObject1;
    var parent2 = displayObject2;
    var commonAncestor = null;
    var nodeSet = /* @__PURE__ */new Set();

    while (parent1) {
      nodeSet.add(parent1);
      parent1 = parent1.parent;
    }

    while (parent2) {
      if (nodeSet.has(parent2)) {
        return parent2;
      }

      parent2 = parent2.parent;
    }

    return commonAncestor;
  };

  InteractionManager.prototype.generateInteractionEvent = function generateInteractionEvent(event, eventType) {
    var eventOption = {
      nativeEvent: event.nativeEvent,
      worldX: event.worldX,
      worldY: event.worldY,
      type: eventType
    };
    var newInteractionEvent = new this.app.interactionEvent(eventOption);
    return newInteractionEvent;
  };

  InteractionManager.prototype.isPointInView = function isPointInView(point) {
    var x = point.x;
    var y = point.y;
    var ref = this.app.view;
    var width = ref.width;
    var height = ref.height;
    return x >= 0 && x < width && y >= 0 && y < height;
  };

  return InteractionManager;
}();
injectInspector(InteractionManager, [["app", false]]);

var MAX_ITEM_NUMBER = 7;
var MAX_LEVEL = 7;
var QuadTree = /*@__PURE__*/function () {
  function QuadTree(props) {
    var this$1$1 = this;
    this.elementList = [];
    this.allElementList = [];
    this.subAreas = [];
    this.x = 0;
    this.y = 0;
    this.level = 1;
    this.isDirty = false;
    this.privateWidth = 0;
    this.privateHeight = 0;

    this.setDirty = function () {
      this$1$1.isDirty = true;
    };

    this.clearElementsAndSubArea = function () {
      this$1$1.elementList = [];
      this$1$1.subAreas = [];
    };

    this.updateElement = function (element, filterWorldBounds) {
      if (element.parentQuadTree) {
        var parentQuadTree = element.parentQuadTree;
        var minX = filterWorldBounds.minX;
        var minY = filterWorldBounds.minY;
        var maxY = filterWorldBounds.maxY;
        var maxX = filterWorldBounds.maxX;

        if (minX < 0) {
          minX = 0;
        }

        if (minY < 0) {
          minY = 0;
        }

        if (!(minX >= parentQuadTree.x && minY >= parentQuadTree.y && maxX <= parentQuadTree.x + parentQuadTree.width && maxY <= parentQuadTree.y + parentQuadTree.height)) {
          this$1$1.removeElement(element);
          this$1$1.insertElement(element);
        }
      }
    };

    this.removeElement = function (deleteElement) {
      var deleteIndexInAllElementList = this$1$1.allElementList.indexOf(deleteElement);
      var deleteIndexInCurrentElementList = this$1$1.elementList.indexOf(deleteElement);
      deleteIndexInAllElementList !== -1 && this$1$1.allElementList.splice(deleteIndexInAllElementList, 1);
      deleteIndexInCurrentElementList !== -1 && this$1$1.elementList.splice(deleteIndexInCurrentElementList, 1);

      for (var i = 0, list = this$1$1.subAreas; i < list.length; i += 1) {
        var subArea = list[i];
        subArea.removeElement(deleteElement);
      }
    };

    this.insertElement = function (element) {
      this$1$1.allElementList.push(element);

      if (this$1$1.elementList.length <= MAX_ITEM_NUMBER) {
        this$1$1.elementList.push(element);
      } else if (this$1$1.level === MAX_LEVEL) {
        this$1$1.elementList.push(element);
      } else if (this$1$1.checkElementCollideSubArea(element, this$1$1)) {
        this$1$1.elementList.push(element);
      } else {
        if (!this$1$1.subAreas.length) {
          this$1$1.divideCurrentSubArea();
        }

        var minX = element.minX;
        var minY = element.minY;
        var maxY = element.maxY;
        var maxX = element.maxX;

        if (minX < 0) {
          minX = 0;
        }

        if (minY < 0) {
          minY = 0;
        }

        var viewWidth = this$1$1.getRootContainerWidth();
        var viewHeight = this$1$1.getRootContainerHeight();

        if (maxX > viewWidth) {
          maxX = viewWidth;
        }

        if (maxY > viewHeight) {
          maxY = viewHeight;
        }

        this$1$1.subAreas.forEach(function (subQuadTree) {
          if (minX >= subQuadTree.x && minY >= subQuadTree.y && maxX <= subQuadTree.x + subQuadTree.width && maxY <= subQuadTree.y + subQuadTree.height) {
            subQuadTree.insertElement(element);
          }
        });
      }
    };

    this.getRootContainerWidth = function () {
      if (this$1$1.parentQuadTree) {
        return this$1$1.parentQuadTree.getRootContainerWidth();
      }

      return this$1$1.width;
    };

    this.getRootContainerHeight = function () {
      if (this$1$1.parentQuadTree) {
        return this$1$1.parentQuadTree.getRootContainerHeight();
      }

      return this$1$1.height;
    };

    this.queryElement = function (targetElement, collideElementList, index) {
      if (index === void 0) index = 0;
      var resList = collideElementList != null ? collideElementList : /* @__PURE__ */new Set();

      if (this$1$1.isDirty) {
        this$1$1.rebuildAllSubArea();
      }

      this$1$1.elementList.forEach(function (curAreaElement) {
        if (curAreaElement === targetElement) {
          return;
        }

        var targetBounds = {
          minX: targetElement.minX,
          minY: targetElement.minY,
          maxX: targetElement.maxX,
          maxY: targetElement.maxY
        };
        var curBounds = {
          minX: curAreaElement.minX,
          minY: curAreaElement.minY,
          maxX: curAreaElement.maxX,
          maxY: curAreaElement.maxY
        };

        if (this$1$1.checkCollide(targetBounds, curBounds)) {
          resList.add(curAreaElement);
          targetElement.needPartRender = true;
          curAreaElement.needPartRender = true;
        }
      });

      if (this$1$1.subAreas.length) {
        var collideAreas = this$1$1.subAreas.filter(function (subQuadTree) {
          return this$1$1.checkCollide({
            minX: subQuadTree.x,
            minY: subQuadTree.y,
            maxX: subQuadTree.x + subQuadTree.width,
            maxY: subQuadTree.y + subQuadTree.height
          }, {
            minX: targetElement.minX,
            minY: targetElement.minY,
            maxX: targetElement.maxX,
            maxY: targetElement.maxY
          });
        });
        collideAreas.forEach(function (subQuadTree) {
          subQuadTree.queryElement(targetElement, resList, index + this$1$1.elementList.length);
        });
      }

      return resList;
    };

    this.checkCollide = function (bound1, bound2) {
      if (bound1.minY > bound2.maxY) {
        return false;
      }

      if (bound1.maxX < bound2.minX) {
        return false;
      }

      if (bound1.maxY < bound2.minY) {
        return false;
      }

      if (bound1.minX > bound2.maxX) {
        return false;
      }

      return true;
    };

    this.checkElementCollideSubArea = function (element, quadTree) {
      var minX = element.minX;
      var minY = element.minY;
      var maxX = element.maxX;
      var maxY = element.maxY;
      var centerX = quadTree.x + quadTree.width / 2;
      var centerY = quadTree.y + quadTree.height / 2;
      return minX < centerX && maxX >= centerX || minY < centerY && maxY >= centerY;
    };

    this.divideCurrentSubArea = function () {
      var ref = this$1$1;
      var x = ref.x;
      var y = ref.y;
      var width = ref.width;
      var height = ref.height;
      var leftTopAreaBounds = {
        x: x,
        y: y,
        width: width / 2,
        height: height / 2
      };
      var leftBottomAreaBounds = {
        x: x,
        y: y + height / 2,
        width: width / 2,
        height: height / 2
      };
      var rightTopAreaBounds = {
        x: x + width / 2,
        y: y,
        width: width / 2,
        height: height / 2
      };
      var rightBottomAreaBounds = {
        x: x + width / 2,
        y: y + height / 2,
        width: width / 2,
        height: height / 2
      };
      this$1$1.subAreas.push(new QuadTree(Object.assign({}, {
        level: this$1$1.level + 1
      }, leftTopAreaBounds)));
      this$1$1.subAreas.push(new QuadTree(Object.assign({}, {
        level: this$1$1.level + 1
      }, leftBottomAreaBounds, {
        parentQuadTree: this$1$1
      })));
      this$1$1.subAreas.push(new QuadTree(Object.assign({}, {
        level: this$1$1.level + 1
      }, rightTopAreaBounds, {
        parentQuadTree: this$1$1
      })));
      this$1$1.subAreas.push(new QuadTree(Object.assign({}, {
        level: this$1$1.level + 1
      }, rightBottomAreaBounds, {
        parentQuadTree: this$1$1
      })));
    };

    this.rebuildAllSubArea = function () {
      this$1$1.clearElementsAndSubArea();
      var ref = this$1$1;
      var allElementList = ref.allElementList;
      this$1$1.allElementList = [];
      allElementList.forEach(function (element) {
        this$1$1.insertElement(element);
      });
      this$1$1.isDirty = false;
    };

    var level = props.level;
    var width = props.width;
    var height = props.height;
    var x = props.x;
    var y = props.y;
    var parentQuadTree = props.parentQuadTree;
    this.level = level;
    this.x = x;
    this.y = y;
    this.width = width != null ? width : 0;
    this.height = height != null ? height : 0;
    this.parentQuadTree = parentQuadTree;
  }

  var prototypeAccessors = {
    width: {
      configurable: true
    },
    height: {
      configurable: true
    }
  };

  prototypeAccessors.width.get = function width() {
    return this.privateWidth;
  };

  prototypeAccessors.width.set = function width(width) {
    this.privateWidth = width;
    this.setDirty();
  };

  prototypeAccessors.height.set = function height(height) {
    this.privateHeight = height;
    this.setDirty();
  };

  prototypeAccessors.height.get = function height() {
    return this.privateHeight;
  };

  Object.defineProperties(QuadTree.prototype, prototypeAccessors);
  return QuadTree;
}();

var Application = /*@__PURE__*/function () {
  function Application(target, options) {
    var this$1$1 = this;
    this.hitColorFinder = new HitColorFinder();
    this.hitView = null;
    this.quadTree = null;
    this.partRefresh = false;
    this.allRefresh = false;
    this.renderRequested = false;
    this._hitViewTimeoutId = null;
    this._stopUpdateHitRegion = false;
    this.willUpdateObject = /* @__PURE__ */new Set();
    this.collideUpdateObject = /* @__PURE__ */new Set();
    this.disposed = false;

    this._renderHitView = function () {
      if (this$1$1.hitView === null) {
        return;
      }

      this$1$1._hitViewTimeoutId = null;
      this$1$1.hitView.clear();
      this$1$1.stage.renderHit(this$1$1.hitView);

      if (process.env.NODE_ENV !== "production" && this$1$1.showHit) {
        this$1$1.view.clear();
        this$1$1.view.drawCanvas(this$1$1.hitView);
      }
    };

    this.target = target;
    this.view = target.createCanvas(options);
    this.stage = new Container();

    this.stage._setParent(this);

    if (options == null ? void 0 : options.openPartRender) {
      this.quadTree = new QuadTree({
        level: 1,
        x: 0,
        y: 0
      });
    }

    var ref = options != null ? options : EMPTY_OPTIONS;
    var id = ref.id;
    var autoRender = ref.autoRender;
    if (autoRender === void 0) autoRender = true;
    var interactive = ref.interactive;
    if (interactive === void 0) interactive = true;
    var delayDrawHit = ref.delayDrawHit;
    if (delayDrawHit === void 0) delayDrawHit = true;
    var notRenderOutsideView = ref.notRenderOutsideView;
    if (notRenderOutsideView === void 0) notRenderOutsideView = false;
    this.id = id;
    this.autoRender = autoRender;
    this.interactive = interactive;
    this.delayDrawHit = delayDrawHit;
    this.notRenderOutsideView = notRenderOutsideView;
    this.interactionEvent = target.interactionFactory;
    this.interactionManager = new InteractionManager(this);
    this.view.onEvents = this.interactionManager.onEvents;

    if (process.env.NODE_ENV !== "production") {
      var showHit = false;
      Object.defineProperty(this, "showHit", {
        configurable: true,
        enumerable: false,
        get: function () {
          return showHit;
        },
        set: function (value) {
          showHit = value;
          this$1$1.requestRender();
        }
      });
      devtools.addApplication(this);
    }
  }

  var prototypeAccessors = {
    interactive: {
      configurable: true
    },
    stopUpdateHitRegion: {
      configurable: true
    },
    loader: {
      configurable: true
    },
    resource: {
      configurable: true
    },
    width: {
      configurable: true
    },
    height: {
      configurable: true
    },
    resolution: {
      configurable: true
    }
  };

  prototypeAccessors.interactive.get = function interactive() {
    return this._interactive;
  };

  prototypeAccessors.interactive.set = function interactive(value) {
    if (this._interactive === value) {
      return;
    }

    this._interactive = value;

    if (value) {
      this._mallocHitView();
    } else {
      this._freeHitView();
    }
  };

  prototypeAccessors.stopUpdateHitRegion.get = function stopUpdateHitRegion() {
    return this._stopUpdateHitRegion;
  };

  prototypeAccessors.stopUpdateHitRegion.set = function stopUpdateHitRegion(value) {
    if (this._stopUpdateHitRegion === value) {
      return;
    }

    this._stopUpdateHitRegion = value;

    if (value) {
      return;
    }

    this._mallocHitView();

    this._renderHitView();
  };

  prototypeAccessors.loader.get = function loader() {
    return this.target.loader;
  };

  prototypeAccessors.resource.get = function resource() {
    return this.target.loader;
  };

  prototypeAccessors.width.get = function width() {
    return this.view.width;
  };

  prototypeAccessors.width.set = function width(value) {
    this.view.width = value;

    if (this.hitView !== null) {
      this.hitView.width = value;
    }

    if (this.quadTree !== null) {
      this.quadTree.width = value;
    }

    this.allRefresh = true;
  };

  prototypeAccessors.height.get = function height() {
    return this.view.height;
  };

  prototypeAccessors.height.set = function height(value) {
    this.view.height = value;

    if (this.hitView !== null) {
      this.hitView.height = value;
    }

    if (this.quadTree !== null) {
      this.quadTree.height = value;
    }

    this.allRefresh = true;
  };

  prototypeAccessors.resolution.get = function resolution() {
    return this.view.resolution;
  };

  prototypeAccessors.resolution.set = function resolution(value) {
    this.view.resolution = value;

    if (this.hitView !== null) {
      this.hitView.resolution = value;
    }

    this.partRefresh = true;
  };

  Application.prototype.render = function render() {
    var isPartRefresh = this.partRefresh && !this.allRefresh && this.quadTree;

    if (isPartRefresh) {
      this.partRenderStage();
    } else {
      this._renderStage();
    }

    if (process.env.NODE_ENV !== "production" && this.showHit && this.hitView) {
      this.view.clear();
      this.view.drawCanvas(this.hitView);
    }

    if (this.stopUpdateHitRegion || isPartRefresh) {
      return;
    }

    this.renderHit();
  };

  Application.prototype.renderHit = function renderHit() {
    if (this.delayDrawHit) {
      if (this.hitView !== null) {
        if (this._hitViewTimeoutId !== null) {
          clearTimeout(this._hitViewTimeoutId);
        }

        this._hitViewTimeoutId = setTimeout(this._renderHitView, 50);
      }
    } else {
      this._renderHitView();
    }
  };

  Application.prototype.requestRender = function requestRender(updateDisplayObject) {
    var this$1$1 = this;

    if (updateDisplayObject == null ? void 0 : updateDisplayObject.needPartRender) {
      this.partRefresh = true;
      this.willUpdateObject.add(updateDisplayObject);
    } else {
      this.allRefresh = true;
    }

    if (this.renderRequested || this.disposed) {
      return;
    }

    this.renderRequested = true;
    requestAnimationFrame(function () {
      this$1$1.renderRequested = false;

      if (this$1$1.disposed) {
        return;
      }

      this$1$1.render();
      this$1$1.partRefresh = false;
      this$1$1.allRefresh = false;
    });
  };

  Application.prototype.collectRawRenderData = function collectRawRenderData() {
    return {
      width: this.width * this.resolution,
      height: this.height * this.resolution,
      items: this.stage.generateRawRenderData(this.view)
    };
  };

  Application.prototype.dispose = function dispose(clearView) {
    if (clearView === void 0) clearView = true;
    this.disposed = true;
    this.stage.dispose();

    if (clearView) {
      this.view.clear();
    }

    this.view.dispose();
    this.hitColorFinder.dispose();
    this.interactionManager.dispose();

    this._freeHitView();

    if (this._hitViewTimeoutId) {
      clearTimeout(this._hitViewTimeoutId);
    }

    if (process.env.NODE_ENV !== "production") {
      devtools.removeApplication(this);
    }
  };

  Application.prototype.partRenderStage = function partRenderStage() {
    this.collideUpdateObject = /* @__PURE__ */new Set();
    var cacheMap = /* @__PURE__ */new Map();
    var partView = this.mallocPartView();
    this.collectNeedRefreshPart(cacheMap, partView);
    this.clearPartRefreshViewBeforeRender();
    this.stage.partRender(partView);
    this.renderPartViewOnStage(partView);
    this.freePartView(cacheMap);
    this.collideUpdateObject = /* @__PURE__ */new Set();
    this.willUpdateObject = /* @__PURE__ */new Set();
  };

  Application.prototype.collectNeedRefreshPart = function collectNeedRefreshPart(cacheMap, partView) {
    var this$1$1 = this;
    this.willUpdateObject.forEach(function (displayObject) {
      if (!this$1$1.quadTree) {
        return;
      }

      this$1$1.quadTree.queryElement(displayObject, this$1$1.collideUpdateObject);
      this$1$1.collideUpdateObject.forEach(function (collideDisplayObject) {
        cacheMap.set(collideDisplayObject, partView);
      });
    });
  };

  Application.prototype.renderPartViewOnStage = function renderPartViewOnStage(partView) {
    var this$1$1 = this;
    partView.transform = this.view.transform;
    var ref = partView.padding;
    var top = ref.top;
    var left = ref.left;
    var ref$1 = this.view.padding;
    var viewTop = ref$1.top;
    var viewLeft = ref$1.left;
    this.willUpdateObject.forEach(function (obj) {
      var dx = Math.floor(obj.filterWorldBounds.x) + (left - viewLeft);
      var dy = Math.floor(obj.filterWorldBounds.y) + (top - viewTop);
      var dw = Math.ceil(obj.filterWorldBounds.width);
      var dh = Math.ceil(obj.filterWorldBounds.height);
      var sx = dx * this$1$1.resolution;
      var sy = dy * this$1$1.resolution;
      var sw = dw * this$1$1.resolution;
      var sh = dh * this$1$1.resolution;

      if (dw === 0 || dh === 0 || sw === 0 || sh === 0 || partView.width === 0 || partView.height === 0) {
        return;
      }

      this$1$1.view.drawCanvas(partView, dx, dy, dw, dh, sx, sy, sw, sh);
    });
  };

  Application.prototype._renderStage = function _renderStage() {
    this.view.clear();
    this.stage.render(this.view);
  };

  Application.prototype.clearPartRefreshViewBeforeRender = function clearPartRefreshViewBeforeRender() {
    var this$1$1 = this;

    if (this.willUpdateObject.size) {
      this.willUpdateObject.forEach(function (obj) {
        var dx = Math.floor(obj.filterWorldBounds.x);
        var dy = Math.floor(obj.filterWorldBounds.y);
        var dw = Math.ceil(obj.filterWorldBounds.width);
        var dh = Math.ceil(obj.filterWorldBounds.height);
        this$1$1.view.clearPartRect(dx, dy, dw, dh);
      });
    }
  };

  Application.prototype._mallocHitView = function _mallocHitView() {
    if (this.hitView) {
      return;
    }

    var buffer = CanvasPoolSingleton.getInstance().checkOut(this.view.constructor);
    buffer.resetBufferInfo(this.view, this.view.width, this.view.height, this.view.resolution);
    this.hitView = buffer;
    this.hitView.isHit = true;
  };

  Application.prototype.mallocPartView = function mallocPartView() {
    var ref = this.view;
    var width = ref.width;
    var height = ref.height;
    var buffer = CanvasPoolSingleton.getInstance().checkOut(this.view.constructor);
    buffer.resetBufferInfo(this.view, width, height, this.view.resolution, true);
    var partialRefreshView = buffer;
    partialRefreshView.isBuffer = true;
    return partialRefreshView;
  };

  Application.prototype._freeHitView = function _freeHitView() {
    if (!this.hitView) {
      return;
    }

    this.hitView.isHit = false;
    CanvasPoolSingleton.getInstance().checkIn(this.hitView);
    this.hitView = null;
  };

  Application.prototype.freePartView = function freePartView(cacheMap) {
    cacheMap.forEach(function (view) {
      CanvasPoolSingleton.getInstance().checkIn(view);
    });
  };

  Object.defineProperties(Application.prototype, prototypeAccessors);
  return Application;
}();
var EMPTY_OPTIONS = {};
injectDisplayName(Application, "alloy-renderer(Application)");
injectInspector(Application, ["view", "hitView", "interactionManager", "hitColorFinder", "delayDrawHit", "interactive", "showHit", ["stage", false], ["renderRequested", false], ["resource", false], ["target", false]]);

var Canvas = /*@__PURE__*/function () {
  function Canvas() {
    this.isHit = false;
    this.isBuffer = false;
    this.contextTranslate = {
      x: 0,
      y: 0
    };
    this.contextScale = {
      sx: 1,
      sy: 1
    };
    this.padding = new Padding();
    this._transform = new Matrix();
    this._autoMount = false;
    this._bounds = new Bounds();
  }

  var prototypeAccessors = {
    transform: {
      configurable: true
    },
    autoMount: {
      configurable: true
    },
    contentWidth: {
      configurable: true
    },
    contentHeight: {
      configurable: true
    },
    naturalWidth: {
      configurable: true
    },
    naturalHeight: {
      configurable: true
    },
    bounds: {
      configurable: true
    }
  };

  prototypeAccessors.transform.get = function transform() {
    return this._transform;
  };

  prototypeAccessors.transform.set = function transform(m) {
    this._transform.copyFrom(m);

    this._updateTransform();
  };

  prototypeAccessors.autoMount.get = function autoMount() {
    return this._autoMount;
  };

  prototypeAccessors.autoMount.set = function autoMount(value) {
    this._autoMount = value;
  };

  prototypeAccessors.contentWidth.get = function contentWidth() {
    return this.width - this.padding.left - this.padding.right;
  };

  prototypeAccessors.contentHeight.get = function contentHeight() {
    return this.height - this.padding.top - this.padding.bottom;
  };

  prototypeAccessors.naturalWidth.get = function naturalWidth() {
    return this.width * this.resolution;
  };

  prototypeAccessors.naturalHeight.get = function naturalHeight() {
    return this.height * this.resolution;
  };

  Canvas.prototype.hasContextScale = function hasContextScale() {
    return this.contextScale.sx !== 1 || this.contextScale.sy !== 1;
  };

  prototypeAccessors.bounds.get = function bounds() {
    this._bounds.minX = -this.padding.left;
    this._bounds.minY = -this.padding.top;
    this._bounds.maxX = this.width - this.padding.left;
    this._bounds.maxY = this.height - this.padding.top;
    return this._bounds;
  };

  Canvas.prototype.clear = function clear() {
    this.transform = Matrix.Identity;
    this.clearRect(-this.padding.left, -this.padding.top, this.width, this.height);
    this._glBuffer = void 0;
    this.contextTranslate.x = 0;
    this.contextTranslate.y = 0;
  };

  Canvas.prototype.clearPartRect = function clearPartRect(x, y, width, height) {
    this.transform = Matrix.Identity;
    this.clearRect(x, y, width, height);
    this._glBuffer = void 0;
    this.contextTranslate.x = 0;
    this.contextTranslate.y = 0;
  };

  Canvas.prototype.drawLine = function drawLine(x, y, width) {
    this.beginPath();
    this.lineWidth = width;
    this.moveTo(0, y);
    this.lineTo(x, y);
    this.stroke();
  };

  Canvas.prototype.drawText = function drawText(text, x, y) {
    this.fillText(text, x, y);
    this.strokeText(text, x, y);
  };

  Canvas.prototype.mergeCanvas = function mergeCanvas(target, x, y) {
    if (this.isBuffer) {
      var m = _matrix$4.copyFrom(this.transform).translate(this.padding.left, this.padding.top).translate(this.contextTranslate.x, this.contextTranslate.y).translate(x, y);

      var targetBounds = _bounds.clear().addBoundsMatrix(m, target.bounds);

      var padding = this.bounds.paddingTo(targetBounds, _padding$2).overlay(Padding.Zero);
      this.pad(padding);
    }

    if (target.hasContextScale()) {
      var width = target.width * target.resolution;
      var height = target.height * target.resolution;
      var ref = target.contextScale;
      var sx = ref.sx;
      var sy = ref.sy;
      this.drawCanvas(target, x, y, target.width / sx, target.height / sy, 0, 0, width, height);
    } else {
      this.drawCanvas(target, x, y);
    }
  };

  Canvas.prototype.mergeImageResource = function mergeImageResource(image, x, y, width, height, bounds, sx, sy) {
    if (this.isBuffer) {
      var m = _matrix$4.copyFrom(this.transform).translate(this.padding.left, this.padding.top).translate(this.contextTranslate.x, this.contextTranslate.y).translate(x, y);

      var targetBounds = _bounds.clear().addBoundsMatrix(m, bounds);

      var padding = this.bounds.paddingTo(targetBounds, _padding$2).overlay(Padding.Zero);
      this.pad(padding);
    }

    if (sx !== void 0 && sy !== void 0) {
      this.drawImage(image, x, y, width / sx, height / sy, 0, 0, image.width, image.height);
    } else {
      this.drawImage(image, x, y, width, height);
    }
  };

  Canvas.prototype.getGlBuffer = function getGlBuffer(width, height, resolution) {
    if (resolution === void 0) resolution = this.resolution;

    var _a, _b;

    if (this.parent) {
      return this.parent.getGlBuffer(width, height, resolution);
    }

    var w = (width !== void 0 ? width : this.width) * resolution;
    var h = (height !== void 0 ? height : this.height) * resolution;

    if (this._glBuffer === void 0) {
      this._glBuffer = this.createGlBuffer(width, height, resolution);
    } else {
      if (width !== void 0 && height !== void 0) {
        (_a = this._glBuffer) == null ? void 0 : _a.resize(w, h);
      }
    }

    (_b = this._glBuffer) == null ? void 0 : _b.gl.viewport(0, 0, w, h);
    return this._glBuffer;
  };

  Canvas.prototype.dispose = function dispose() {};

  Canvas.prototype._updateTransform = function _updateTransform() {
    var r = this.resolution;
    var ref = this._transform;
    var a = ref.a;
    var b = ref.b;
    var c = ref.c;
    var d = ref.d;
    var tx = ref.tx;
    var ty = ref.ty;
    this.setTransform(a * r, b * r, c * r, d * r, (tx + this.padding.left + this.contextTranslate.x) * r, (ty + this.padding.top + this.contextTranslate.y) * r);
  };

  Object.defineProperties(Canvas.prototype, prototypeAccessors);
  return Canvas;
}();

var _matrix$4 = /*#__PURE__*/new Matrix();

var _bounds = /*#__PURE__*/new Bounds();

var _padding$2 = /*#__PURE__*/new Padding();

injectInspector(Canvas, ["isHit", "isBuffer", "native", "width", "height", "cursor", "resolution", "contextTranslate", "bounds"]);

var GlBuffer = /*@__PURE__*/function () {
  function GlBuffer() {
    this.shaders = {};
    this.programs = {};
  }

  GlBuffer.prototype.createAndBindTexture = function createAndBindTexture(src) {
    var ref = this;
    var gl = ref.gl;
    var texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    if (src) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    }

    return texture;
  };

  GlBuffer.prototype.createAndBindBufferTexture = function createAndBindBufferTexture(width, height) {
    if (width === void 0) width = this.width;
    if (height === void 0) height = this.height;
    var ref = this;
    var gl = ref.gl;
    var texture = this.createAndBindTexture();
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    return texture;
  };

  GlBuffer.prototype.createAndBindFramebuffer = function createAndBindFramebuffer(texture) {
    var ref = this;
    var gl = ref.gl;
    var fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

    if (texture) {
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    }

    return fb;
  };

  GlBuffer.prototype.drawRect = function drawRect() {
    this.useDefaultFillRect();
    var ref = this;
    var gl = ref.gl;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  };

  GlBuffer.prototype.getShader = function getShader(id, type, source) {
    if (this.shaders[id]) {
      return this.shaders[id];
    }

    var ref = this;
    var gl = ref.gl;
    var shader = gl.createShader(type);
    this.shaders[id] = shader;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader));
    }

    return shader;
  };

  GlBuffer.prototype.getDefaultVertexShader = function getDefaultVertexShader() {
    return this.getShader("default-vertex", this.gl.VERTEX_SHADER, "\n      attribute vec2 a_position;\n      attribute vec2 a_texCoord;\n      varying vec2 v_texCoord;\n\n      void main() {\n        gl_Position = vec4(a_position, 0, 1);\n        v_texCoord = a_texCoord;\n      }\n    ");
  };

  GlBuffer.prototype.useDefaultFillRect = function useDefaultFillRect(program, positionName, texCoordName) {
    if (program === void 0) program = this.currentProgram;
    if (positionName === void 0) positionName = "a_position";
    if (texCoordName === void 0) texCoordName = "a_texCoord";

    if (!program) {
      return;
    }

    var ref = this;
    var gl = ref.gl;

    if (this.positionBuffer === void 0) {
      this.positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    }

    if (this.texCoordBuffer === void 0) {
      this.texCoordBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]), gl.STATIC_DRAW);
    }

    var positionLocation = gl.getAttribLocation(program, positionName);
    gl.enableVertexAttribArray(positionLocation);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    var texCoordLocation = gl.getAttribLocation(program, texCoordName);
    gl.enableVertexAttribArray(texCoordLocation);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);
  };

  GlBuffer.prototype.useTexture = function useTexture(src) {
    var ref = this;
    var gl = ref.gl;

    if (!this.texture) {
      this.texture = this.createAndBindTexture();
    } else {
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
    }

    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    return this.texture;
  };

  GlBuffer.prototype.useProgram = function useProgram(id, vertexShader, fragmentShader) {
    var ref = this;
    var gl = ref.gl;

    if (!this.programs[id]) {
      var program2 = gl.createProgram();
      this.programs[id] = program2;
      gl.attachShader(program2, vertexShader);
      gl.attachShader(program2, fragmentShader);
      gl.linkProgram(program2);

      if (!gl.getProgramParameter(program2, gl.LINK_STATUS)) {
        console.error(gl.getProgramInfoLog(program2));
      }
    }

    var program = this.programs[id];
    gl.useProgram(program);
    this.currentProgram = program;
    return program;
  };

  return GlBuffer;
}();
var uniformMethods = ["uniform1f", "uniform1fv", "uniform1i", "uniform1iv", "uniform2f", "uniform2fv", "uniform2i", "uniform2iv", "uniform3f", "uniform3fv", "uniform3i", "uniform3iv", "uniform4f", "uniform4fv", "uniform4i", "uniform4iv", "uniformMatrix2fv", "uniformMatrix3fv", "uniformMatrix4fv"];
uniformMethods.forEach(function (method) {
  GlBuffer.prototype[method] = function (name) {
    var ref;
    var args = [],
        len = arguments.length - 1;

    while (len-- > 0) args[len] = arguments[len + 1];

    if (!this.currentProgram) {
      return;
    }

    var location = this.gl.getUniformLocation(this.currentProgram, name);
    (ref = this.gl)[method].apply(ref, [location].concat(args));
  };
});

var Loader = /*@__PURE__*/function () {
  function Loader() {}

  return Loader;
}();

var Target = /*@__PURE__*/function () {
  function Target() {}

  Target.prototype.createCanvas = function createCanvas(options) {
    return new this.factory(options);
  };

  return Target;
}();

var InteractionEvent = /*@__PURE__*/function () {
  function InteractionEvent(options) {
    this.target = null;
    this.currentTarget = null;
    this.stopped = false;
    var type = options.type;
    var nativeEvent = options.nativeEvent;
    var worldX = options.worldX;
    var worldY = options.worldY;
    this.type = type;
    this.nativeEvent = nativeEvent;
    this.worldX = worldX;
    this.worldY = worldY;
  }

  InteractionEvent.prototype.stopPropagation = function stopPropagation() {
    this.stopped = true;
  };

  return InteractionEvent;
}();

var BaseImageFill = /*@__PURE__*/function (Fill) {
  function BaseImageFill(resource) {
    Fill.call(this);
    this.imageTransform = new Transform();
    this.transform = new Transform();
    this._resource = resource;
    this.pattern = null;

    this._updateWhenLoaded();

    this.transform._addUpdateListener(this._updated);

    this.imageTransform._addUpdateListener(this._updated);
  }

  if (Fill) BaseImageFill.__proto__ = Fill;
  BaseImageFill.prototype = /*#__PURE__*/Object.create(Fill && Fill.prototype);
  BaseImageFill.prototype.constructor = BaseImageFill;
  var prototypeAccessors = {
    resource: {
      configurable: true
    }
  };

  prototypeAccessors.resource.get = function resource() {
    return this._resource;
  };

  prototypeAccessors.resource.set = function resource(value) {
    if (this._resource === value) {
      return;
    }

    this._resource = value;

    this._updated();

    this._updateWhenLoaded();

    this.pattern = null;
  };

  BaseImageFill.prototype.getNative = function getNative(canvas, bounds) {
    if (this.resource.isEmpty) {
      return Color.Transparent;
    }

    if (!this.pattern) {
      this.pattern = this.createPattern(canvas);
    }

    if (hasNativePatternTransform(this.pattern)) {
      var ref = this.calcTransform(bounds);
      var a = ref.a;
      var b = ref.b;
      var c = ref.c;
      var d = ref.d;
      var tx = ref.tx;
      var ty = ref.ty;
      _domMatrix.a = a;
      _domMatrix.b = b;
      _domMatrix.c = c;
      _domMatrix.d = d;
      _domMatrix.e = tx;
      _domMatrix.f = ty;
      this.pattern.setTransform(_domMatrix);
    }

    return this.pattern;
  };

  BaseImageFill.prototype._updateWhenLoaded = function _updateWhenLoaded() {
    if (this.resource.loading) {
      this.resource.onImageLoaded(this._updated);
    }
  };

  BaseImageFill.prototype.getTransform = function getTransform(canvas, bounds) {
    if (hasNativePatternTransform()) {
      return Matrix.Identity;
    }

    return this.calcTransform(bounds);
  };

  BaseImageFill.prototype.calcTransform = function calcTransform(bounds) {
    return _matrix$3.copyFrom(this.calcBaseTransform(bounds)).append(this.imageTransform.matrix).prepend(this.transform.matrix);
  };

  Object.defineProperties(BaseImageFill.prototype, prototypeAccessors);
  return BaseImageFill;
}(Fill);

function hasNativePatternTransform(pattern) {
  if (BaseImageFill.HasNativePatternTransform === void 0) {
    if (!pattern) {
      return false;
    }

    BaseImageFill.HasNativePatternTransform = Boolean(_domMatrix && pattern.setTransform);
  }

  return BaseImageFill.HasNativePatternTransform;
}

var _matrix$3 = /*#__PURE__*/new Matrix();

var _domMatrix = typeof DOMMatrix !== "undefined" ? /*#__PURE__*/new DOMMatrix() : void 0;

injectInspector(BaseImageFill, [["$", function () {
  return BaseImageFill.name;
}], "resource", "imageTransform", ["imageTransform (matrix)", function (obj) {
  return obj.imageTransform.matrix;
}], "transform", ["transform (matrix)", function (obj) {
  return obj.transform.matrix;
}]]);

var ColorStop = /*@__PURE__*/function (Versioning) {
  function ColorStop(offset, color) {
    Versioning.call(this);
    this._offset = offset;
    this._color = color;
  }

  if (Versioning) ColorStop.__proto__ = Versioning;
  ColorStop.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  ColorStop.prototype.constructor = ColorStop;
  var prototypeAccessors = {
    offset: {
      configurable: true
    },
    color: {
      configurable: true
    }
  };

  prototypeAccessors.offset.get = function offset() {
    return this._offset;
  };

  prototypeAccessors.offset.set = function offset(value) {
    if (this._offset === value) {
      return;
    }

    this._offset = value;

    this._updated();
  };

  prototypeAccessors.color.get = function color() {
    return this._color;
  };

  prototypeAccessors.color.set = function color(value) {
    if (this._color === value) {
      return;
    }

    this._color = value;

    this._updated();
  };

  Object.defineProperties(ColorStop.prototype, prototypeAccessors);
  return ColorStop;
}(Versioning);
var GradientFill = /*@__PURE__*/function (Fill) {
  function GradientFill() {
    Fill.call(this);
    this.colorStops = [];
    this.transform = new Transform();

    this.transform._addUpdateListener(this._updated);
  }

  if (Fill) GradientFill.__proto__ = Fill;
  GradientFill.prototype = /*#__PURE__*/Object.create(Fill && Fill.prototype);
  GradientFill.prototype.constructor = GradientFill;

  GradientFill.prototype.addColorStop = function addColorStop(offsetOrColorStop, color) {
    var colorStop = offsetOrColorStop instanceof ColorStop ? offsetOrColorStop : new ColorStop(offsetOrColorStop, color);

    colorStop._addUpdateListener(this._updated);

    this.colorStops.push(colorStop);

    this._updated();

    return this;
  };

  GradientFill.prototype.removeColorStop = function removeColorStop(colorStop) {
    var index = this.colorStops.indexOf(colorStop);

    if (index !== -1) {
      this.colorStops.splice(index, 1);

      colorStop._removeUpdateListener(this._updated);
    }

    this._updated();

    return this;
  };

  GradientFill.prototype.generateRawRenderData = function generateRawRenderData(bounds) {
    return Fill.prototype.generateRawRenderData.call(this, bounds);
  };

  GradientFill.prototype.getTransform = function getTransform(canvas, bounds) {
    return this.transform.matrix;
  };

  GradientFill.prototype.getNative = function getNative(canvas, bounds) {
    var gradient = this.getGradient(bounds, canvas);

    if (!gradient) {
      return Color.Transparent;
    }

    for (var i = 0, list = this.colorStops; i < list.length; i += 1) {
      var colorStop = list[i];
      var offset = colorStop.offset;
      var color = colorStop.color;
      gradient.addColorStop(offset, color.rgba);
    }

    return gradient;
  };

  GradientFill.prototype.getRenderColorStops = function getRenderColorStops() {
    var colorStops = [];

    for (var i = 0, list = this.colorStops; i < list.length; i += 1) {
      var colorStop = list[i];
      var offset = colorStop.offset;
      var color = colorStop.color;
      colorStops.push({
        offset: offset,
        color: color.hex
      });
    }

    return colorStops;
  };

  return GradientFill;
}(Fill);
injectInspector(GradientFill, [["$", function () {
  return GradientFill.name;
}]]);
injectInspector(ColorStop, ["offset", "color"]);

var CustomGradientFill = /*@__PURE__*/function (GradientFill) {
  function CustomGradientFill() {
    GradientFill.apply(this, arguments);
  }

  if (GradientFill) CustomGradientFill.__proto__ = GradientFill;
  CustomGradientFill.prototype = /*#__PURE__*/Object.create(GradientFill && GradientFill.prototype);
  CustomGradientFill.prototype.constructor = CustomGradientFill;
  return CustomGradientFill;
}(GradientFill);

var ImageFill = /*@__PURE__*/function (BaseImageFill) {
  function ImageFill() {
    BaseImageFill.apply(this, arguments);
  }

  if (BaseImageFill) ImageFill.__proto__ = BaseImageFill;
  ImageFill.prototype = /*#__PURE__*/Object.create(BaseImageFill && BaseImageFill.prototype);
  ImageFill.prototype.constructor = ImageFill;

  ImageFill.prototype.renderWithClip = function renderWithClip(canvas, bounds) {
    if (this.resource.isEmpty) {
      return;
    }

    var hasNativePatternTransform = BaseImageFill.HasNativePatternTransform;
    BaseImageFill.HasNativePatternTransform = false;
    canvas.save();
    canvas.clip();
    canvas.globalAlpha *= this.alpha;
    this.applyTransformTo(canvas, bounds);
    canvas.drawImage(this.resource, 0, 0, this.resource.width, this.resource.height);
    canvas.restore();
    BaseImageFill.HasNativePatternTransform = hasNativePatternTransform;
  };

  ImageFill.prototype.createPattern = function createPattern(canvas) {
    return canvas.createPattern(this.resource, "no-repeat");
  };

  ImageFill.prototype.calcBaseTransform = function calcBaseTransform(bounds) {
    var sx = bounds.width / this.resource.width;
    var sy = bounds.height / this.resource.height;
    return _matrix$2.set(sx, 0, 0, sy, bounds.x, bounds.y);
  };

  return ImageFill;
}(BaseImageFill);
var RawImageFill = /*@__PURE__*/function (ImageFill) {
  function RawImageFill() {
    ImageFill.apply(this, arguments);
  }

  if (ImageFill) RawImageFill.__proto__ = ImageFill;
  RawImageFill.prototype = /*#__PURE__*/Object.create(ImageFill && ImageFill.prototype);
  RawImageFill.prototype.constructor = RawImageFill;

  RawImageFill.prototype.calcBaseTransform = function calcBaseTransform() {
    return Matrix.Identity;
  };

  return RawImageFill;
}(ImageFill);

var _matrix$2 = /*#__PURE__*/new Matrix();

injectInspector(ImageFill, [["$", function () {
  return ImageFill.name;
}]]);
injectInspector(RawImageFill, [["$", function () {
  return RawImageFill.name;
}]]);

var RawLinearGradientFill = /*@__PURE__*/function (GradientFill) {
  function RawLinearGradientFill(x0, y0, x1, y1) {
    GradientFill.call(this);
    this._x0 = x0;
    this._y0 = y0;
    this._x1 = x1;
    this._y1 = y1;
  }

  if (GradientFill) RawLinearGradientFill.__proto__ = GradientFill;
  RawLinearGradientFill.prototype = /*#__PURE__*/Object.create(GradientFill && GradientFill.prototype);
  RawLinearGradientFill.prototype.constructor = RawLinearGradientFill;
  var prototypeAccessors = {
    x0: {
      configurable: true
    },
    y0: {
      configurable: true
    },
    x1: {
      configurable: true
    },
    y1: {
      configurable: true
    }
  };

  prototypeAccessors.x0.get = function x0() {
    return this._x0;
  };

  prototypeAccessors.x0.set = function x0(value) {
    if (this._x0 === value) {
      return;
    }

    this._x0 = value;

    this._updated();
  };

  prototypeAccessors.y0.get = function y0() {
    return this._y0;
  };

  prototypeAccessors.y0.set = function y0(value) {
    if (this._y0 === value) {
      return;
    }

    this._y0 = value;

    this._updated();
  };

  prototypeAccessors.x1.get = function x1() {
    return this._x1;
  };

  prototypeAccessors.x1.set = function x1(value) {
    if (this._x1 === value) {
      return;
    }

    this._x1 = value;

    this._updated();
  };

  prototypeAccessors.y1.get = function y1() {
    return this._y1;
  };

  prototypeAccessors.y1.set = function y1(value) {
    if (this._y1 === value) {
      return;
    }

    this._y1 = value;

    this._updated();
  };

  RawLinearGradientFill.prototype.generateRawRenderData = function generateRawRenderData() {
    return {
      linearGradFill: {
        alpha: this.alpha,
        colorStops: this.getRenderColorStops(),
        position: {
          x0: this.x0,
          x1: this.x1,
          y0: this.y0,
          y1: this.y1
        }
      }
    };
  };

  RawLinearGradientFill.prototype.getGradient = function getGradient(bounds, canvas) {
    return canvas.createLinearGradient(this.x0, this.y0, this.x1, this.y1);
  };

  Object.defineProperties(RawLinearGradientFill.prototype, prototypeAccessors);
  return RawLinearGradientFill;
}(GradientFill);
var LinearGradientFill = /*@__PURE__*/function (GradientFill) {
  function LinearGradientFill(angle) {
    if (angle === void 0) angle = 0;
    GradientFill.call(this);
    this._angle = angle;
  }

  if (GradientFill) LinearGradientFill.__proto__ = GradientFill;
  LinearGradientFill.prototype = /*#__PURE__*/Object.create(GradientFill && GradientFill.prototype);
  LinearGradientFill.prototype.constructor = LinearGradientFill;
  var prototypeAccessors$1 = {
    angle: {
      configurable: true
    }
  };

  prototypeAccessors$1.angle.get = function () {
    return this._angle;
  };

  prototypeAccessors$1.angle.set = function (value) {
    if (this._angle === value) {
      return;
    }

    this._angle = value;

    this._updated();
  };

  LinearGradientFill.prototype.generateRawRenderData = function generateRawRenderData(bounds) {
    var position = this.getPosition(bounds);

    if (!position) {
      return GradientFill.prototype.generateRawRenderData.call(this, bounds);
    }

    return {
      linearGradFill: {
        alpha: this.alpha,
        colorStops: this.getRenderColorStops(),
        position: position
      }
    };
  };

  LinearGradientFill.prototype.getGradient = function getGradient(bounds, canvas) {
    var position = this.getPosition(bounds);

    if (!position) {
      return null;
    }

    var x0 = position.x0;
    var y0 = position.y0;
    var x1 = position.x1;
    var y1 = position.y1;
    return canvas.createLinearGradient(x0, y0, x1, y1);
  };

  LinearGradientFill.prototype.getPosition = function getPosition(bounds) {
    var minX = bounds.minX;
    var minY = bounds.minY;
    var maxX = bounds.maxX;
    var maxY = bounds.maxY;
    var width = bounds.width;
    var height = bounds.height;

    if (bounds.isEmpty || width === 0 || height === 0) {
      return null;
    }

    var angle = (this.angle >= 0 ? this.angle : -360 * Math.floor(this.angle / 360) + this.angle) % 360;
    var a = Math.cos(angle * DEG_TO_RAD);
    var b = Math.sin(angle * DEG_TO_RAD);
    var u = (minX + maxX) / 2;
    var v = (minY + maxY) / 2;
    var p1x;
    var p1y;
    var p2x;
    var p2y;

    if (angle <= 90) {
      p1x = minX;
      p1y = minY;
      p2x = maxX;
      p2y = maxY;
    } else if (angle <= 180) {
      p1x = maxX;
      p1y = minY;
      p2x = minX;
      p2y = maxY;
    } else if (angle <= 270) {
      p1x = maxX;
      p1y = maxY;
      p2x = minX;
      p2y = minY;
    } else {
      p1x = minX;
      p1y = maxY;
      p2x = maxX;
      p2y = minY;
    }

    var a2 = a * a;
    var ab = a * b;
    var b2 = b * b;
    var x0 = a2 * p1x - ab * (u - p1y) + b2 * v;
    var y0 = a2 * u + ab * (p1x - v) + b2 * p1y;
    var x1 = a2 * p2x - ab * (u - p2y) + b2 * v;
    var y1 = a2 * u + ab * (p2x - v) + b2 * p2y;
    return {
      x0: x0,
      y0: y0,
      x1: x1,
      y1: y1
    };
  };

  Object.defineProperties(LinearGradientFill.prototype, prototypeAccessors$1);
  return LinearGradientFill;
}(GradientFill);
injectInspector(RawLinearGradientFill, [["$", function () {
  return RawLinearGradientFill.name;
}], "x0", "y0", "x1", "y1"]);
injectInspector(LinearGradientFill, [["$", function () {
  return LinearGradientFill.name;
}], "angle"]);

var RawRadialGradientFill = /*@__PURE__*/function (GradientFill) {
  function RawRadialGradientFill(x0, y0, r0, x1, y1, r1) {
    GradientFill.call(this);
    this._x0 = x0;
    this._y0 = y0;
    this._r0 = r0;
    this._x1 = x1;
    this._y1 = y1;
    this._r1 = r1;
  }

  if (GradientFill) RawRadialGradientFill.__proto__ = GradientFill;
  RawRadialGradientFill.prototype = /*#__PURE__*/Object.create(GradientFill && GradientFill.prototype);
  RawRadialGradientFill.prototype.constructor = RawRadialGradientFill;
  var prototypeAccessors = {
    x0: {
      configurable: true
    },
    y0: {
      configurable: true
    },
    r0: {
      configurable: true
    },
    x1: {
      configurable: true
    },
    y1: {
      configurable: true
    },
    r1: {
      configurable: true
    }
  };

  prototypeAccessors.x0.get = function x0() {
    return this._x0;
  };

  prototypeAccessors.x0.set = function x0(value) {
    if (this._x0 === value) {
      return;
    }

    this._x0 = value;

    this._updated();
  };

  prototypeAccessors.y0.get = function y0() {
    return this._y0;
  };

  prototypeAccessors.y0.set = function y0(value) {
    if (this._y0 === value) {
      return;
    }

    this._y0 = value;

    this._updated();
  };

  prototypeAccessors.r0.get = function r0() {
    return this._r0;
  };

  prototypeAccessors.r0.set = function r0(value) {
    if (this._r0 === value) {
      return;
    }

    this._r0 = value;

    this._updated();
  };

  prototypeAccessors.x1.get = function x1() {
    return this._x1;
  };

  prototypeAccessors.x1.set = function x1(value) {
    if (this._x1 === value) {
      return;
    }

    this._x1 = value;

    this._updated();
  };

  prototypeAccessors.y1.get = function y1() {
    return this._y1;
  };

  prototypeAccessors.y1.set = function y1(value) {
    if (this._y1 === value) {
      return;
    }

    this._y1 = value;

    this._updated();
  };

  prototypeAccessors.r1.get = function r1() {
    return this._r1;
  };

  prototypeAccessors.r1.set = function r1(value) {
    if (this._r1 === value) {
      return;
    }

    this._r1 = value;

    this._updated();
  };

  RawRadialGradientFill.prototype.generateRawRenderData = function generateRawRenderData() {
    return {
      radialGradFill: {
        alpha: this.alpha,
        colorStops: this.getRenderColorStops(),
        position: {
          x0: this.x0,
          x1: this.x1,
          y0: this.y0,
          y1: this.y1,
          r0: this.r0,
          r1: this.r1
        }
      }
    };
  };

  RawRadialGradientFill.prototype.getGradient = function getGradient(bounds, canvas) {
    return canvas.createRadialGradient(this.x0, this.y0, this.r0, this.x1, this.y1, this.r1);
  };

  Object.defineProperties(RawRadialGradientFill.prototype, prototypeAccessors);
  return RawRadialGradientFill;
}(GradientFill);
var RadialGradientFill = /*@__PURE__*/function (GradientFill) {
  function RadialGradientFill(anchorX, anchorY) {
    if (anchorX === void 0) anchorX = 0.5;
    if (anchorY === void 0) anchorY = 0.5;
    GradientFill.call(this);
    this._anchorX = anchorX;
    this._anchorY = anchorY;
  }

  if (GradientFill) RadialGradientFill.__proto__ = GradientFill;
  RadialGradientFill.prototype = /*#__PURE__*/Object.create(GradientFill && GradientFill.prototype);
  RadialGradientFill.prototype.constructor = RadialGradientFill;
  var prototypeAccessors$1 = {
    anchorX: {
      configurable: true
    },
    anchorY: {
      configurable: true
    }
  };

  prototypeAccessors$1.anchorX.get = function () {
    return this._anchorX;
  };

  prototypeAccessors$1.anchorX.set = function (value) {
    if (this._anchorX === value) {
      return;
    }

    this._anchorX = value;

    this._updated();
  };

  prototypeAccessors$1.anchorY.get = function () {
    return this._anchorY;
  };

  prototypeAccessors$1.anchorY.set = function (value) {
    if (this._anchorY === value) {
      return;
    }

    this._anchorY = value;

    this._updated();
  };

  RadialGradientFill.prototype.generateRawRenderData = function generateRawRenderData(bounds) {
    var position = this.getRadialPosition(bounds);

    if (!position) {
      return GradientFill.prototype.generateRawRenderData.call(this, bounds);
    }

    return {
      radialGradFill: {
        alpha: this.alpha,
        colorStops: this.getRenderColorStops(),
        position: position
      }
    };
  };

  RadialGradientFill.prototype.getGradient = function getGradient(bounds, canvas) {
    var position = this.getRadialPosition(bounds);

    if (!position) {
      return null;
    }

    var x0 = position.x0;
    var y0 = position.y0;
    var r0 = position.r0;
    var x1 = position.x1;
    var y1 = position.y1;
    var r1 = position.r1;
    return canvas.createRadialGradient(x0, y0, r0, x1, y1, r1);
  };

  RadialGradientFill.prototype.getRadialPosition = function getRadialPosition(bounds) {
    var minX = bounds.minX;
    var minY = bounds.minY;
    var maxX = bounds.maxX;
    var maxY = bounds.maxY;
    var width = bounds.width;
    var height = bounds.height;

    if (bounds.isEmpty || width === 0 || height === 0) {
      return null;
    }

    var cx = minX + width * this.anchorX;
    var cy = minY + height * this.anchorY;
    var u = (minX + maxX) / 2;
    var v = (minY + maxY) / 2;
    var x;
    var y;

    if (cx > u && cy > v) {
      x = minX;
      y = minY;
    } else if (cx <= u && cy > v) {
      x = maxX;
      y = minY;
    } else if (cx <= u && cy <= v) {
      x = maxX;
      y = maxY;
    } else {
      x = minX;
      y = maxY;
    }

    var r = Math.sqrt(Math.pow(cx - x, 2) + Math.pow(cy - y, 2));
    return {
      x0: cx,
      y0: cy,
      r0: 0,
      x1: cx,
      y1: cy,
      r1: r
    };
  };

  Object.defineProperties(RadialGradientFill.prototype, prototypeAccessors$1);
  return RadialGradientFill;
}(GradientFill);
injectInspector(RawRadialGradientFill, [["$", function () {
  return RawRadialGradientFill.name;
}], "x0", "y0", "r0", "x1", "y1", "r1"]);
injectInspector(RadialGradientFill, [["$", function () {
  return RadialGradientFill.name;
}], "anchorX", "anchorY"]);

var TilingRepeat = /* @__PURE__ */function (TilingRepeat2) {
  TilingRepeat2["Horizontal"] = "repeat-x";
  TilingRepeat2["Vertical"] = "repeat-y";
  TilingRepeat2["Both"] = "repeat";
  return TilingRepeat2;
}(TilingRepeat || {});
var TilingMirror = /* @__PURE__ */function (TilingMirror2) {
  TilingMirror2[TilingMirror2["None"] = 0] = "None";
  TilingMirror2[TilingMirror2["Horizontal"] = 1] = "Horizontal";
  TilingMirror2[TilingMirror2["Vertical"] = 2] = "Vertical";
  TilingMirror2[TilingMirror2["Both"] = 3] = "Both";
  return TilingMirror2;
}(TilingMirror || {});
var TilingImageFill = /*@__PURE__*/function (BaseImageFill) {
  function TilingImageFill(resource, repeat
  /* Both */
  , mirror
  /* None */
  , useResource) {
    if (repeat === void 0) repeat = "repeat";
    if (mirror === void 0) mirror = 0;
    if (useResource === void 0) useResource = false;
    BaseImageFill.call(this, resource);
    this._repeat = repeat;
    this._mirror = mirror;
    this.useResource = useResource;
  }

  if (BaseImageFill) TilingImageFill.__proto__ = BaseImageFill;
  TilingImageFill.prototype = /*#__PURE__*/Object.create(BaseImageFill && BaseImageFill.prototype);
  TilingImageFill.prototype.constructor = TilingImageFill;
  var prototypeAccessors = {
    repeat: {
      configurable: true
    },
    mirror: {
      configurable: true
    }
  };

  prototypeAccessors.repeat.get = function repeat() {
    return this._repeat;
  };

  prototypeAccessors.repeat.set = function repeat(value) {
    if (this._repeat === value) {
      return;
    }

    this._repeat = value;

    this._updated();
  };

  prototypeAccessors.mirror.get = function mirror() {
    return this._mirror;
  };

  prototypeAccessors.mirror.set = function mirror(value) {
    if (this._mirror === value) {
      return;
    }

    this._mirror = value;

    this._updated();
  };

  TilingImageFill.prototype.createPattern = function createPattern(canvas) {
    var buffer = this.getPatternSource(canvas);
    var pattern = canvas.createPattern(buffer, this.repeat);

    if (buffer instanceof Canvas) {
      CanvasPoolSingleton.getInstance().checkIn(buffer);
    }

    return pattern;
  };

  TilingImageFill.prototype.calcBaseTransform = function calcBaseTransform(bounds) {
    return _matrix$1.set(1, 0, 0, 1, bounds.x, bounds.y);
  };

  TilingImageFill.prototype.getPatternSource = function getPatternSource(canvas) {
    if (this.mirror === 0
    /* None */
    ) {
      if (this.useResource) {
        return this.resource;
      }

      var ref = this.resource;
      var width2 = ref.width;
      var height2 = ref.height;
      var buffer2 = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor);
      buffer2.resetBufferInfo(canvas, width2, height2, 1);
      buffer2.drawImage(this.resource, 0, 0, width2, height2);
      return buffer2;
    }

    var ref$1 = this.resource;
    var width = ref$1.width;
    var height = ref$1.height;
    var buffer;

    switch (this.mirror) {
      case 1
      /* Horizontal */
      :
        buffer = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor);
        buffer.resetBufferInfo(canvas, width * 2, height, 1);
        buffer.drawImage(this.resource, 0, 0, width, height);
        buffer.transform = _flipX;
        buffer.drawImage(this.resource, width * -2, 0, width, height);
        return buffer;

      case 2
      /* Vertical */
      :
        buffer = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor);
        buffer.resetBufferInfo(canvas, width, height * 2, 1);
        buffer.drawImage(this.resource, 0, 0, width, height);
        buffer.transform = _flipY;
        buffer.drawImage(this.resource, 0, height * -2, width, height);
        return buffer;

      case 3
      /* Both */
      :
        buffer = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor);
        buffer.resetBufferInfo(canvas, width * 2, height * 2, 1);
        buffer.drawImage(this.resource, 0, 0, width, height);
        buffer.transform = _flipX;
        buffer.drawImage(this.resource, width * -2, 0, width, height);
        buffer.transform = _flipY;
        buffer.drawCanvas(buffer, 0, height * -2, buffer.width, buffer.height);
        return buffer;
    }
  };

  Object.defineProperties(TilingImageFill.prototype, prototypeAccessors);
  return TilingImageFill;
}(BaseImageFill);

var _matrix$1 = /*#__PURE__*/new Matrix();

var _flipX = /*#__PURE__*/new Matrix(-1, 0, 0, 1, 0, 0);

var _flipY = /*#__PURE__*/new Matrix(1, 0, 0, -1, 0, 0);

injectInspector(TilingImageFill, [["$", function () {
  return TilingImageFill.name;
}], ["repeat", function (obj) {
  var obj$1;
  return (obj$1 = {}, obj$1["repeat-x"
  /* Horizontal */
  ] = "Horizontal", obj$1["repeat-y"
  /* Vertical */
  ] = "Vertical", obj$1["repeat"
  /* Both */
  ] = "Both", obj$1)[obj.repeat];
}], ["mirror", function (obj) {
  return TilingMirror[obj.mirror];
}]]);

function getConstructor(obj) {
  return Object.getPrototypeOf(obj).constructor;
}
function getDefaults(instance) {
  return getConstructor(instance).defaults;
}

var Filter = /*@__PURE__*/function (Versioning) {
  function Filter() {
    Versioning.apply(this, arguments);
    this.forbidWebgl = false;
  }

  if (Versioning) Filter.__proto__ = Versioning;
  Filter.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  Filter.prototype.constructor = Filter;

  Filter.prototype.apply = function apply(obj, canvas, sx, sy) {};

  Filter.prototype.getFilterMatrix = function getFilterMatrix(obj, initialBounds) {
    return _matrix.identity();
  };

  Filter.prototype.updateFilterBounds = function updateFilterBounds(obj, filterBounds, initialBounds) {};

  return Filter;
}(Versioning);

var _matrix = /*#__PURE__*/new Matrix();

injectInspector(Filter, [["$", function () {
  return Filter.name;
}], ["type", false]]);

var BlurFilter = /*@__PURE__*/function (Filter) {
  function BlurFilter(options) {
    if (options === void 0) options = {};
    Filter.call(this);
    this.type = "blur";

    var _a, _b, _c, _d;

    var defaults = getDefaults(this);
    this._radiusX = (_a = options.radiusX) != null ? _a : defaults.radiusX;
    this._radiusY = (_b = options.radiusY) != null ? _b : defaults.radiusY;
    this._maxPadX = (_c = options.maxPadX) != null ? _c : defaults.maxPadX;
    this._maxPadY = (_d = options.maxPadY) != null ? _d : defaults.maxPadY;
  }

  if (Filter) BlurFilter.__proto__ = Filter;
  BlurFilter.prototype = /*#__PURE__*/Object.create(Filter && Filter.prototype);
  BlurFilter.prototype.constructor = BlurFilter;
  var prototypeAccessors = {
    radiusX: {
      configurable: true
    },
    radiusY: {
      configurable: true
    },
    maxPadX: {
      configurable: true
    },
    maxPadY: {
      configurable: true
    }
  };

  prototypeAccessors.radiusX.get = function radiusX() {
    return this._radiusX;
  };

  prototypeAccessors.radiusX.set = function radiusX(value) {
    if (this._radiusX === value) {
      return;
    }

    this._radiusX = value;

    this._updated();
  };

  prototypeAccessors.radiusY.get = function radiusY() {
    return this._radiusY;
  };

  prototypeAccessors.radiusY.set = function radiusY(value) {
    if (this._radiusY === value) {
      return;
    }

    this._radiusY = value;

    this._updated();
  };

  prototypeAccessors.maxPadX.get = function maxPadX() {
    return this._maxPadX;
  };

  prototypeAccessors.maxPadX.set = function maxPadX(value) {
    if (this._maxPadX === value) {
      return;
    }

    this._maxPadX = value;

    this._updated();
  };

  prototypeAccessors.maxPadY.get = function maxPadY() {
    return this._maxPadY;
  };

  prototypeAccessors.maxPadY.set = function maxPadY(value) {
    if (this._maxPadY === value) {
      return;
    }

    this._maxPadY = value;

    this._updated();
  };

  BlurFilter.prototype.apply = function apply(obj, canvas, sx, sy) {
    var s = canvas.resolution * this.radiusX * sx / 2;
    var t = canvas.resolution * this.radiusY * sy / 2;

    if (s === 0 && t === 0) {
      return;
    }

    var maxPadX = clamp(this.maxPadX, 0, Infinity);
    var maxPadY = clamp(this.maxPadY, 0, Infinity);
    var padX = clamp(this.radiusX * sx * 2, 0, maxPadX);
    var padY = clamp(this.radiusY * sy * 2, 0, maxPadY);
    canvas.pad(_padding$1.set(padY, padX, padY, padX));
    var buffer;

    if (this.forbidWebgl) {
      buffer = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor);
      buffer.resetBufferInfo(canvas, canvas.width, canvas.height, canvas.resolution, true);
      buffer.filter = "blur(" + s + "px)";
      buffer.drawCanvas(canvas, canvas.padding.left, canvas.padding.top);
    } else {
      buffer = canvas.getGlBuffer(canvas.width, canvas.height);

      if (!buffer) {
        return;
      }

      glBlurFilter(buffer, canvas.native, s, t);
    }

    canvas.transform = Matrix.Identity;
    canvas.clear();
    canvas.drawCanvas(buffer, -canvas.padding.left, -canvas.padding.top, canvas.width, canvas.height);

    if (buffer instanceof Canvas) {
      CanvasPoolSingleton.getInstance().checkIn(buffer);
    }
  };

  BlurFilter.prototype.updateFilterBounds = function updateFilterBounds(obj, filterBounds) {
    var maxPadX = clamp(this.maxPadX, 0, Infinity);
    var maxPadY = clamp(this.maxPadY, 0, Infinity);
    var padX = clamp(this.radiusX, 0, maxPadX);
    var padY = clamp(this.radiusY, 0, maxPadY);
    filterBounds.pad(_padding$1.set(padY, padX, padY, padX));
  };

  Object.defineProperties(BlurFilter.prototype, prototypeAccessors);
  return BlurFilter;
}(Filter);
BlurFilter.defaults = {
  radiusX: 4,
  radiusY: 4,
  maxPadX: Infinity,
  maxPadY: Infinity
};
function glBlurFilter(glBuffer, src, s, t) {
  var gl = glBuffer.gl;
  var vertexShader = glBuffer.getDefaultVertexShader();
  var fragmentShader = glBuffer.getShader("blur", gl.FRAGMENT_SHADER, "\n    precision mediump float;\n\n    uniform sampler2D u_image;\n    uniform vec2 u_resolution;\n    uniform vec2 u_direction;\n    uniform int u_d;\n    uniform int u_dOffset;\n    uniform bool u_flip;\n\n    vec4 read(sampler2D image, vec2 uv) {\n      if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return vec4(0.0);\n      return texture2D(image, uv);\n    }\n\n    vec4 boxBlur(sampler2D image, vec2 uv, vec2 resolution, int d, int dOffset, vec2 direction) {\n      int start = int(float(d) / 2.0) * 2 == d ? d / 2 : (d + dOffset) / 2;\n      vec4 acc;\n      for (int i = 0; i < 1024; i++) {\n        if (i >= d) break;\n\n        vec2 dst = uv + direction * float(i - start + dOffset) / resolution;\n        acc += read(image, dst);\n      }\n\n      return acc / float(d);\n    }\n\n    void main() {\n      vec2 uv = vec2(gl_FragCoord.xy / u_resolution);\n      if (u_flip) uv.y = 1.0 - uv.y;\n\n      if (u_d == 0) {\n        gl_FragColor = texture2D(u_image, uv);\n      } else {\n        gl_FragColor = boxBlur(u_image, uv, u_resolution, u_d, u_dOffset, u_direction);\n      }\n    }\n  ");
  glBuffer.useProgram("blur", vertexShader, fragmentShader);
  glBuffer.useDefaultFillRect();
  glBuffer.uniform2f("u_resolution", gl.canvas.width, gl.canvas.height);
  var texture = glBuffer.useTexture(src);
  var tb1 = glBuffer.createAndBindBufferTexture();
  var fb1 = glBuffer.createAndBindFramebuffer(tb1);
  var tb2 = glBuffer.createAndBindBufferTexture();
  var fb2 = glBuffer.createAndBindFramebuffer(tb2);
  var currentTexture = texture;
  var currentFBO = fb1;

  var next = function (cb) {
    gl.bindTexture(gl.TEXTURE_2D, currentTexture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, currentFBO);
    cb == null ? void 0 : cb();
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    currentTexture = currentTexture === tb1 ? tb2 : tb1;
    currentFBO = currentFBO === fb1 ? fb2 : fb1;
  };

  var apply = function (d, dirX, dirY) {
    glBuffer.uniform2f("u_direction", dirX, dirY);
    glBuffer.uniform1i("u_d", d);

    if (d % 2 === 0) {
      next(function () {
        return glBuffer.uniform1i("u_dOffset", 0);
      });
      next(function () {
        return glBuffer.uniform1i("u_dOffset", 1);
      });
      next(function () {
        return glBuffer.uniform1i("u_d", d + 1);
      });
    } else {
      next();
      next();
      next();
    }
  };

  gl.clearColor(0, 0, 0, 0);
  var dx = Math.floor(s * 3 * Math.sqrt(2 * Math.PI) / 4 + 0.5);
  var dy = Math.floor(t * 3 * Math.sqrt(2 * Math.PI) / 4 + 0.5);
  glBuffer.uniform1i("u_flip", 0);
  apply(dx, 1, 0);
  apply(dy, 0, 1);
  gl.bindTexture(gl.TEXTURE_2D, currentTexture);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  glBuffer.uniform1i("u_d", 0);
  glBuffer.uniform1i("u_flip", 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.deleteTexture(tb1);
  gl.deleteTexture(tb2);
  gl.deleteFramebuffer(fb1);
  gl.deleteFramebuffer(fb2);
}

var _padding$1 = /*#__PURE__*/new Padding();

injectInspector(BlurFilter, [["$", function () {
  return BlurFilter.name;
}], "radiusX", "radiusY", "maxPadX", "maxPadY"]);

var ColorMatrixFilter = /*@__PURE__*/function (Filter) {
  function ColorMatrixFilter(options) {
    if (options === void 0) options = {};
    Filter.call(this);
    this.type = "color-matrix";

    var _a;

    var defaults = getDefaults(this);
    this._colorMatrix = (_a = options.colorMatrix) != null ? _a : defaults.colorMatrix;
  }

  if (Filter) ColorMatrixFilter.__proto__ = Filter;
  ColorMatrixFilter.prototype = /*#__PURE__*/Object.create(Filter && Filter.prototype);
  ColorMatrixFilter.prototype.constructor = ColorMatrixFilter;
  var prototypeAccessors = {
    colorMatrix: {
      configurable: true
    }
  };

  prototypeAccessors.colorMatrix.get = function colorMatrix() {
    return this._colorMatrix;
  };

  prototypeAccessors.colorMatrix.set = function colorMatrix(value) {
    if (this._colorMatrix === value) {
      return;
    }

    this._colorMatrix = value;

    this._updated();
  };

  ColorMatrixFilter.prototype.apply = function apply(obj, canvas) {
    var buffer = canvas.getGlBuffer(canvas.width, canvas.height);

    if (!buffer) {
      return;
    }

    glColorMatrixFilter(buffer, canvas.native, this.colorMatrix);
    canvas.transform = Matrix.Identity;
    canvas.clear();
    canvas.drawCanvas(buffer, 0, 0, canvas.width, canvas.height);
  };

  ColorMatrixFilter.prototype.applyTo = function applyTo(imageData, scaleX, scaleY) {
    throw new Error("Method not implemented");
  };

  Object.defineProperties(ColorMatrixFilter.prototype, prototypeAccessors);
  return ColorMatrixFilter;
}(Filter);
ColorMatrixFilter.defaults = {
  colorMatrix: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0]
};
function glColorMatrixFilter(glBuffer, src, colorMatrix) {
  var gl = glBuffer.gl;
  var vertexShader = glBuffer.getDefaultVertexShader();
  var fragmentShader = glBuffer.getShader("color-matrix", gl.FRAGMENT_SHADER, "\n    precision mediump float;\n\n    uniform sampler2D u_image;\n    uniform float m[20];\n    varying vec2 v_texCoord;\n\n    void main() {\n      vec4 c = texture2D(u_image, v_texCoord);\n      gl_FragColor.r = m[0] * c.r + m[1] * c.g + m[2] * c.b + m[3] * c.a + m[4];\n      gl_FragColor.g = m[5] * c.r + m[6] * c.g + m[7] * c.b + m[8] * c.a + m[9];\n      gl_FragColor.b = m[10] * c.r + m[11] * c.g + m[12] * c.b + m[13] * c.a + m[14];\n      gl_FragColor.a = m[15] * c.r + m[16] * c.g + m[17] * c.b + m[18] * c.a + m[19];\n    }\n  ");
  glBuffer.useProgram("color-matrix", vertexShader, fragmentShader);
  glBuffer.useTexture(src);
  glBuffer.uniform1fv("m", colorMatrix);
  glBuffer.drawRect();
}
injectInspector(ColorMatrixFilter, [["$", function () {
  return ColorMatrixFilter.name;
}], "colorMatrix"]);

var Convolution3x3Filter = /*@__PURE__*/function (Filter) {
  function Convolution3x3Filter(options) {
    if (options === void 0) options = {};
    Filter.call(this);
    this.type = "convolution-3x3";

    var _a;

    var defaults = getDefaults(this);
    this._kernel = (_a = options.kernel) != null ? _a : defaults.kernel;
  }

  if (Filter) Convolution3x3Filter.__proto__ = Filter;
  Convolution3x3Filter.prototype = /*#__PURE__*/Object.create(Filter && Filter.prototype);
  Convolution3x3Filter.prototype.constructor = Convolution3x3Filter;
  var prototypeAccessors = {
    kernel: {
      configurable: true
    }
  };

  prototypeAccessors.kernel.get = function kernel() {
    return this._kernel;
  };

  prototypeAccessors.kernel.set = function kernel(value) {
    if (this._kernel === value) {
      return;
    }

    this._kernel = value;

    this._updated();
  };

  Convolution3x3Filter.prototype.apply = function apply(obj, canvas) {
    var buffer = canvas.getGlBuffer(canvas.width, canvas.height);

    if (!buffer) {
      return;
    }

    glConvolution3x3Filter(buffer, canvas.native, this.kernel);
    canvas.transform = Matrix.Identity;
    canvas.clear();
    canvas.drawCanvas(buffer, 0, 0, canvas.width, canvas.height);
  };

  Object.defineProperties(Convolution3x3Filter.prototype, prototypeAccessors);
  return Convolution3x3Filter;
}(Filter);
Convolution3x3Filter.defaults = {
  kernel: [0, 0, 0, 0, 1, 0, 0, 0, 0]
};
function glConvolution3x3Filter(glBuffer, src, kernel) {
  var gl = glBuffer.gl;
  var vertexShader = glBuffer.getDefaultVertexShader();
  var fragmentShader = glBuffer.getShader("convolution-3x3", gl.FRAGMENT_SHADER, "\n    precision mediump float;\n\n    uniform sampler2D u_image;\n    uniform vec2 u_resolution;\n    uniform float u_kernel[9];\n\n    vec4 read(sampler2D image, vec2 uv) {\n      if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return vec4(0.0);\n      return texture2D(image, uv);\n    }\n\n    vec4 convolution3x3(sampler2D image, vec2 uv, vec2 resolution, float kernel[9]) {\n      vec2 pixel = vec2(1.0, 1.0) / u_resolution;\n      return read(image, uv + pixel * vec2(-1.0, -1.0)) * kernel[0]\n           + read(image, uv + pixel * vec2( 0.0, -1.0)) * kernel[1]\n           + read(image, uv + pixel * vec2( 1.0, -1.0)) * kernel[2]\n           + read(image, uv + pixel * vec2(-1.0,  0.0)) * kernel[3]\n           + read(image, uv + pixel * vec2( 0.0,  0.0)) * kernel[4]\n           + read(image, uv + pixel * vec2( 1.0,  0.0)) * kernel[5]\n           + read(image, uv + pixel * vec2(-1.0,  1.0)) * kernel[6]\n           + read(image, uv + pixel * vec2( 0.0,  1.0)) * kernel[7]\n           + read(image, uv + pixel * vec2( 1.0,  1.0)) * kernel[8];\n    }\n\n    void main() {\n      vec2 uv = vec2(gl_FragCoord.xy / u_resolution);\n      uv.y = 1.0 - uv.y;\n      gl_FragColor = convolution3x3(u_image, uv, u_resolution, u_kernel);\n    }\n  ");
  glBuffer.useProgram("convolution-3x3", vertexShader, fragmentShader);
  glBuffer.useTexture(src);
  glBuffer.uniform2f("u_resolution", gl.canvas.width, gl.canvas.height);
  glBuffer.uniform1fv("u_kernel", kernel);
  glBuffer.drawRect();
}
injectInspector(Convolution3x3Filter, [["$", function () {
  return Convolution3x3Filter.name;
}], "kernel"]);

var FillOverlayFilter = /*@__PURE__*/function (Filter) {
  function FillOverlayFilter(fill) {
    if (fill === void 0) fill = Fill.None;
    Filter.call(this);
    this.type = "fill-overlay";
    this._fill = fill;
  }

  if (Filter) FillOverlayFilter.__proto__ = Filter;
  FillOverlayFilter.prototype = /*#__PURE__*/Object.create(Filter && Filter.prototype);
  FillOverlayFilter.prototype.constructor = FillOverlayFilter;
  var prototypeAccessors = {
    fill: {
      configurable: true
    }
  };

  prototypeAccessors.fill.get = function fill() {
    return this._fill;
  };

  prototypeAccessors.fill.set = function fill(value) {
    if (this._fill === value) {
      return;
    }

    this._fill = value;

    this._updated();
  };

  FillOverlayFilter.prototype.apply = function apply(obj, canvas) {
    canvas.save();
    canvas.globalCompositeOperation = "source-in";
    canvas.beginPath();
    canvas.rect(canvas.bounds.x, canvas.bounds.y, canvas.bounds.width, canvas.bounds.height);
    this.fill.applyTo(canvas, canvas.bounds);
    this.fill.applyTransformTo(canvas, canvas.bounds);
    canvas.fill();
    canvas.restore();
  };

  Object.defineProperties(FillOverlayFilter.prototype, prototypeAccessors);
  return FillOverlayFilter;
}(Filter);
injectInspector(FillOverlayFilter, [["$", function () {
  return FillOverlayFilter.name;
}], "fill"]);

var namespaceURI = "http://www.w3.org/2000/svg";

function getFilterContainer(width, height) {
  var id = "filter-element";
  var element = document.getElementById(id);

  if (!element) {
    element = document.createElementNS(namespaceURI, "svg");
    element.setAttribute("xmlns", namespaceURI);
    element.setAttribute("version", "1.1");
    element.setAttribute("id", id);
    element.style.position = "absolute";
    element.style.display = "none";
    document.body.appendChild(element);
  }

  element.setAttribute("viewBox", "0 0 " + width + " " + height);
  return element;
}

function getFilterElement(id, width, height) {
  var element = document.getElementById(id);

  if (!element) {
    element = document.createElementNS(namespaceURI, "filter");
    element.setAttribute("id", id);
    var filterSVG = getFilterContainer(width, height);
    filterSVG.appendChild(element);
  }

  return element;
}

function getBlurFilter(width, height, radius) {
  var id = "blur-filter";
  var element = getFilterElement(id, width, height);
  var blurElement = element.childNodes[0];

  if (!blurElement) {
    blurElement = document.createElementNS(namespaceURI, "feGaussianBlur");
    element.appendChild(blurElement);
  }

  blurElement.setAttribute("stdDeviation", "" + radius);
  return id;
}
function getMorphologyFilter(width, height, radius, mode) {
  var id = "morphology-filter";
  var element = getFilterElement(id, width, height);
  var blurElement = element.childNodes[0];

  if (!blurElement) {
    blurElement = document.createElementNS(namespaceURI, "feMorphology");
    element.appendChild(blurElement);
  }

  blurElement.setAttribute("operator", mode);
  blurElement.setAttribute("radius", "" + radius);
  return id;
}
function getColorMatrixFilter(width, height, values) {
  var id = "color-matrix-filter";
  var element = getFilterElement(id, width, height);
  var blurElement = element.childNodes[0];

  if (!blurElement) {
    blurElement = document.createElementNS(namespaceURI, "feColorMatrix");
    blurElement.setAttribute("type", "matrix");
    element.appendChild(blurElement);
  }

  blurElement.setAttribute("values", values);
  return id;
}

var MorphologyMode = /* @__PURE__ */function (MorphologyMode2) {
  MorphologyMode2[MorphologyMode2["Erode"] = 0] = "Erode";
  MorphologyMode2[MorphologyMode2["Dilate"] = 1] = "Dilate";
  return MorphologyMode2;
}(MorphologyMode || {});
var shaderSource = "\nprecision mediump float;\n\nuniform sampler2D u_image;\nuniform vec2 u_resolution;\nuniform int u_mode;\nuniform int u_radiusX;\nuniform int u_radiusY;\nuniform int u_ignoreAlpha;\nuniform int u_picture;\n\nvec4 read(sampler2D image, vec2 uv) {\n  vec2 edge = vec2(float(u_radiusX / 2), float(u_radiusY / 2)) / u_resolution;\n  if (uv.x < edge.x || uv.x > 1.0 - edge.x || uv.y < edge.y || uv.y > 1.0 - edge.y) return vec4(0.0);\n  return texture2D(image, uv);\n}\n\nvoid minPerChannel(inout vec4 result, vec4 color) {\n  result.r = min(result.r, color.r);\n  result.g = min(result.g, color.g);\n  result.b = min(result.b, color.b);\n  result.a = min(result.a, color.a);\n}\n\nvoid maxPerChannel(inout vec4 result, vec4 color) {\n  result.r = max(result.r, color.r);\n  result.g = max(result.g, color.g);\n  result.b = max(result.b, color.b);\n  result.a = max(result.a, color.a);\n}\n\nvec4 morphology(sampler2D image, vec2 uv, vec2 resolution, int mode, int radiusX, int radiusY, int ignoreAlpha) {\n  vec2 pixel = vec2(1.0, 1.0) / u_resolution;\n  vec4 result = read(image, uv);\n\n  if (radiusX > 20 && u_picture > 0) {\n    for (int i = 1; i < 128; i++) {\n      if (i > radiusX) break;\n  \n      if (mode == 0) { // erode\n        minPerChannel(result, read(image, uv + pixel * vec2(i, 0)));\n        minPerChannel(result, read(image, uv + pixel * vec2(-i, 0)));\n        minPerChannel(result, read(image, uv + pixel * vec2(0, i)));\n        minPerChannel(result, read(image, uv + pixel * vec2(0, -i)));\n  \n        if (i < 50) {\n          minPerChannel(result, read(image, uv + pixel * vec2(i, i)));\n          minPerChannel(result, read(image, uv + pixel * vec2(-i, i)));\n          minPerChannel(result, read(image, uv + pixel * vec2(i, -i)));\n          minPerChannel(result, read(image, uv + pixel * vec2(-i, -i)));\n        }\n      }\n    }\n  } else {\n    for (int i = 1; i < 128; i++) {\n      if (i > radiusX) break;\n      for (int j = 1; j < 128; j++) {\n        if (j > radiusY) break;\n\n        if (mode == 0) { // erode\n          minPerChannel(result, read(image, uv + pixel * vec2(i, j)));\n          minPerChannel(result, read(image, uv + pixel * vec2(i, -j)));\n          minPerChannel(result, read(image, uv + pixel * vec2(-i, j)));\n          minPerChannel(result, read(image, uv + pixel * vec2(-i, -j)));\n        }\n\n        if (mode == 1) { // dilate\n          maxPerChannel(result, read(image, uv + pixel * vec2(i, j)));\n          maxPerChannel(result, read(image, uv + pixel * vec2(i, -j)));\n          maxPerChannel(result, read(image, uv + pixel * vec2(-i, j)));\n          maxPerChannel(result, read(image, uv + pixel * vec2(-i, -j)));\n        }\n      }\n    }\n  }\n\n  if (ignoreAlpha == 1 && result != vec4(0.0)) result.a = 1.0;\n  return result;\n}\n\nvoid main() {\n  vec2 uv = vec2(gl_FragCoord.xy / u_resolution);\n  uv.y = 1.0 - uv.y;\n  gl_FragColor = morphology(u_image, uv, u_resolution, u_mode, u_radiusX, u_radiusY, u_ignoreAlpha);\n}";
var MorphologyFilter = /*@__PURE__*/function (Filter) {
  function MorphologyFilter(options) {
    if (options === void 0) options = {};
    Filter.call(this);
    this.type = "morphology";

    var _a, _b, _c, _d, _e;

    var defaults = getDefaults(this);
    this._mode = (_a = options.mode) != null ? _a : defaults.mode;
    this._radiusX = (_b = options.radiusX) != null ? _b : defaults.radiusX;
    this._radiusY = (_c = options.radiusY) != null ? _c : defaults.radiusY;
    this._ignoreAlpha = (_d = options.ignoreAlpha) != null ? _d : defaults.ignoreAlpha;
    this._isPicture = (_e = options.isPicture) != null ? _e : defaults.isPicture;
  }

  if (Filter) MorphologyFilter.__proto__ = Filter;
  MorphologyFilter.prototype = /*#__PURE__*/Object.create(Filter && Filter.prototype);
  MorphologyFilter.prototype.constructor = MorphologyFilter;
  var prototypeAccessors = {
    mode: {
      configurable: true
    },
    radiusX: {
      configurable: true
    },
    radiusY: {
      configurable: true
    },
    ignoreAlpha: {
      configurable: true
    },
    isPicture: {
      configurable: true
    }
  };

  prototypeAccessors.mode.get = function mode() {
    return this._mode;
  };

  prototypeAccessors.mode.set = function mode(value) {
    if (this._mode === value) {
      return;
    }

    this._mode = value;

    this._updated();
  };

  prototypeAccessors.radiusX.get = function radiusX() {
    return this._radiusX;
  };

  prototypeAccessors.radiusX.set = function radiusX(value) {
    if (this._radiusX === value) {
      return;
    }

    this._radiusX = value;

    this._updated();
  };

  prototypeAccessors.radiusY.get = function radiusY() {
    return this._radiusY;
  };

  prototypeAccessors.radiusY.set = function radiusY(value) {
    if (this._radiusY === value) {
      return;
    }

    this._radiusY = value;

    this._updated();
  };

  prototypeAccessors.ignoreAlpha.get = function ignoreAlpha() {
    return this._ignoreAlpha;
  };

  prototypeAccessors.ignoreAlpha.set = function ignoreAlpha(value) {
    if (this._ignoreAlpha === value) {
      return;
    }

    this._ignoreAlpha = value;

    this._updated();
  };

  prototypeAccessors.isPicture.get = function isPicture() {
    return this._isPicture;
  };

  prototypeAccessors.isPicture.set = function isPicture(value) {
    if (this._isPicture === value) {
      return;
    }

    this._isPicture = value;

    this._updated();
  };

  MorphologyFilter.prototype.apply = function apply(obj, canvas, sx, sy) {
    var rx = canvas.resolution * sx * this.radiusX;
    var ry = canvas.resolution * sy * this.radiusY;

    if (this.mode === 1
    /* Dilate */
    ) {
      canvas.pad(_padding.set(ry, rx, ry, rx));
    }

    var buffer;

    if (this.forbidWebgl) {
      var filterMode = this.mode === 1
      /* Dilate */
      ? "dilate" : "erode";
      var filterId = getMorphologyFilter(canvas.width, canvas.height, Math.max(rx, ry), filterMode);
      buffer = CanvasPoolSingleton.getInstance().checkOut(canvas.constructor);
      buffer.resetBufferInfo(canvas, canvas.width, canvas.height, canvas.resolution, true);
      buffer.filter = "url(#" + filterId + ")";
      buffer.drawCanvas(canvas, canvas.padding.left, canvas.padding.top);
    } else {
      buffer = canvas.getGlBuffer(canvas.width, canvas.height);

      if (!buffer) {
        return;
      }

      glMorphologyFilter(buffer, canvas.native, this.mode, rx, ry, this.ignoreAlpha, this.isPicture);
    }

    canvas.transform = Matrix.Identity;
    canvas.clear();
    canvas.drawCanvas(buffer, -canvas.padding.left, -canvas.padding.top, canvas.width, canvas.height);

    if (buffer instanceof Canvas) {
      CanvasPoolSingleton.getInstance().checkIn(buffer);
    }
  };

  MorphologyFilter.prototype.updateFilterBounds = function updateFilterBounds(obj, filterBounds) {
    var rx = this.radiusX;
    var ry = this.radiusY;
    filterBounds.pad(_padding.set(ry, rx, ry, rx));
  };

  Object.defineProperties(MorphologyFilter.prototype, prototypeAccessors);
  return MorphologyFilter;
}(Filter);
MorphologyFilter.defaults = {
  mode: 0
  /* Erode */
  ,
  radiusX: 4,
  radiusY: 4,
  ignoreAlpha: false,
  isPicture: false
};
function glMorphologyFilter(glBuffer, src, mode, radiusX, radiusY, ignoreAlpha, isPicture) {
  var gl = glBuffer.gl;
  var vertexShader = glBuffer.getDefaultVertexShader();
  var fragmentShader = glBuffer.getShader("morphology", gl.FRAGMENT_SHADER, shaderSource);
  glBuffer.useProgram("morphology", vertexShader, fragmentShader);
  glBuffer.useTexture(src);
  glBuffer.uniform2f("u_resolution", gl.canvas.width, gl.canvas.height);
  glBuffer.uniform1i("u_mode", mode);
  glBuffer.uniform1i("u_radiusX", Math.round(radiusX));
  glBuffer.uniform1i("u_radiusY", Math.round(radiusY));
  glBuffer.uniform1i("u_ignoreAlpha", Number(ignoreAlpha));
  glBuffer.uniform1i("u_picture", Number(isPicture));
  glBuffer.drawRect();
}

var _padding = /*#__PURE__*/new Padding();

injectInspector(MorphologyFilter, [["$", function () {
  return MorphologyFilter.name;
}], "mode", "radiusX", "radiusY", "ignoreAlpha"]);

var Resource = /*@__PURE__*/function () {
  function Resource(data) {
    var this$1$1 = this;
    if (data === void 0) data = null;
    this.data = null;
    this.loadPromise = null;

    if (data instanceof Promise) {
      this.loadPromise = data;
      this.loadPromise.then(this.onLoaded.bind(this)).catch(function (err) {
        console.error("Failed to load resource:", err);
        this$1$1.loadPromise = null;
      });
    } else if (data !== null) {
      this.onLoaded(data);
    }
  }

  var prototypeAccessors = {
    loading: {
      configurable: true
    }
  };

  prototypeAccessors.loading.get = function loading() {
    return this.loadPromise !== null;
  };

  Resource.prototype.onLoaded = function onLoaded(data) {
    this.data = data;
    this.loadPromise = null;
  };

  Object.defineProperties(Resource.prototype, prototypeAccessors);
  return Resource;
}();
injectInspector(Resource, [["$", function () {
  return Resource.name;
}], "data", "loading"]);

var ImageResource = /*@__PURE__*/function (Resource) {
  function ImageResource(data) {
    if (data === void 0) data = null;
    Resource.call(this, data);

    var _a, _b;

    (_a = this.width) != null ? _a : this.width = 0;
    (_b = this.height) != null ? _b : this.height = 0;
  }

  if (Resource) ImageResource.__proto__ = Resource;
  ImageResource.prototype = /*#__PURE__*/Object.create(Resource && Resource.prototype);
  ImageResource.prototype.constructor = ImageResource;
  var prototypeAccessors = {
    isEmpty: {
      configurable: true
    }
  };

  prototypeAccessors.isEmpty.get = function isEmpty() {
    return this.loading || this.width === 0 || this.height === 0;
  };

  ImageResource.prototype.onImageLoaded = function onImageLoaded(cb) {
    if (this.loadPromise instanceof Promise) {
      return void this.loadPromise.then(cb);
    }

    return cb();
  };

  ImageResource.prototype.onImageErrored = function onImageErrored(cb) {
    if (this.loadPromise instanceof Promise) {
      return void this.loadPromise.catch(cb);
    }
  };

  ImageResource.prototype.onLoaded = function onLoaded(data) {
    Resource.prototype.onLoaded.call(this, data);
    var image = data;
    this.width = image.naturalWidth || image.codedWidth || image.width || 0;
    this.height = image.naturalHeight || image.codedHeight || image.height || 0;
  };

  Object.defineProperties(ImageResource.prototype, prototypeAccessors);
  return ImageResource;
}(Resource);
injectInspector(ImageResource, [["$", function () {
  return ImageResource.name;
}], "width", "height"]);

var Image = /*@__PURE__*/function (DisplayObject) {
  function Image(resource) {
    DisplayObject.call(this);
    this.resource = resource;
    this._width = void 0;
    this._height = void 0;
  }

  if (DisplayObject) Image.__proto__ = DisplayObject;
  Image.prototype = /*#__PURE__*/Object.create(DisplayObject && DisplayObject.prototype);
  Image.prototype.constructor = Image;
  var prototypeAccessors = {
    resource: {
      configurable: true
    },
    width: {
      configurable: true
    },
    height: {
      configurable: true
    },
    naturalWidth: {
      configurable: true
    },
    naturalHeight: {
      configurable: true
    },
    _isCompoundObject: {
      configurable: true
    }
  };

  prototypeAccessors.resource.get = function resource() {
    return this._resource;
  };

  prototypeAccessors.resource.set = function resource(value) {
    if (this._resource === value) {
      return;
    }

    this._resource = value;

    this._boundsUpdated();

    this._updateWhenLoaded();
  };

  prototypeAccessors.width.get = function width() {
    if (this._width !== void 0) {
      return this._width;
    }

    if (this.resource) {
      return this.resource.width;
    }

    return 0;
  };

  prototypeAccessors.width.set = function width(value) {
    if (this._width === value) {
      return;
    }

    this._width = value;

    this._boundsUpdated();
  };

  prototypeAccessors.height.get = function height() {
    if (this._height !== void 0) {
      return this._height;
    }

    if (this.resource) {
      return this.resource.height;
    }

    return 0;
  };

  prototypeAccessors.height.set = function height(value) {
    if (this._height === value) {
      return;
    }

    this._height = value;

    this._boundsUpdated();
  };

  prototypeAccessors.naturalWidth.get = function naturalWidth() {
    var _a, _b;

    return (_b = (_a = this.resource) == null ? void 0 : _a.width) != null ? _b : 0;
  };

  prototypeAccessors.naturalHeight.get = function naturalHeight() {
    var _a, _b;

    return (_b = (_a = this.resource) == null ? void 0 : _a.height) != null ? _b : 0;
  };

  prototypeAccessors._isCompoundObject.get = function _isCompoundObject() {
    return false;
  };

  Image.prototype._updateWhenLoaded = function _updateWhenLoaded() {
    var this$1$1 = this;

    var _a;

    if ((_a = this.resource) == null ? void 0 : _a.loading) {
      this.resource.onImageLoaded(function () {
        this$1$1._boundsUpdated({
          source: this$1$1
        });
      });
    }
  };

  Image.prototype.updateInitialBounds = function updateInitialBounds(initialBounds) {
    initialBounds.set(0, 0, this.width, this.height);
  };

  Image.prototype.draw = function draw(canvas) {
    if (!this.resource || this.resource.isEmpty) {
      return;
    }

    canvas.drawImage(this.resource, 0, 0, this.width, this.height);
  };

  Image.prototype.drawHit = function drawHit(canvas) {
    DisplayObject.prototype.drawHit.call(this, canvas);

    if (!this.colorKey) {
      return;
    }

    if (this.pointerEvents === PointerEvents.NONE) {
      return;
    }

    canvas.nativeFillStyle = Color.fromHex(this.colorKey).rgba;
    canvas.fillRect(0, 0, this.width, this.height);
  };

  Object.defineProperties(Image.prototype, prototypeAccessors);
  return Image;
}(DisplayObject);
injectInspector(Image, ["resource", "width", "height"]);

function generatePath(segments) {
  var path = "";
  segments.forEach(function (segment) {
    if (segment.cmd === Command.MoveTo || segment.cmd === Command.LineTo) {
      path += segment.cmd + " " + segment.x + " " + segment.y + " ";
    } else if (segment.cmd === Command.CurveTo) {
      path += segment.cmd + " " + segment.x1 + " " + segment.y1 + ", " + segment.x2 + " " + segment.y2 + ", " + segment.x + " " + segment.y + " ";
    } else if (segment.cmd === Command.QuadraticCurveTo) {
      path += segment.cmd + " " + segment.x1 + " " + segment.y1 + ", " + segment.x + " " + segment.y + " ";
    } else {
      path += segment.cmd;
    }
  });
  return path;
}

var SvgGraphics = /*@__PURE__*/function (FillStrokeAble) {
  function SvgGraphics(path) {
    if (path === void 0) path = "";
    FillStrokeAble.call(this);
    this._segments = [];
    this._pathDirty = false;
    this._hitStrokeWidth = null;
    this._path = path;
    this._segmentsDirty = !!path;
  }

  if (FillStrokeAble) SvgGraphics.__proto__ = FillStrokeAble;
  SvgGraphics.prototype = /*#__PURE__*/Object.create(FillStrokeAble && FillStrokeAble.prototype);
  SvgGraphics.prototype.constructor = SvgGraphics;
  var prototypeAccessors = {
    path: {
      configurable: true
    },
    segments: {
      configurable: true
    },
    hitStrokeWidth: {
      configurable: true
    }
  };

  prototypeAccessors.path.get = function path() {
    if (this._pathDirty) {
      this._pathDirty = false;
      this._path = generatePath(this._segments);
    }

    return this._path;
  };

  prototypeAccessors.path.set = function path(value) {
    if (this._path === value && !this._pathDirty) {
      return;
    }

    this._path = value;
    this._segmentsDirty = true;
    this._pathDirty = false;

    this._boundsUpdated();
  };

  prototypeAccessors.segments.get = function segments() {
    if (this._segmentsDirty) {
      this._segmentsDirty = false;
      this._segments = PathParser.parse(this._path);
    }

    return this._segments;
  };

  prototypeAccessors.segments.set = function segments(value) {
    this._segments = value;
    this._segmentsDirty = false;
    this._pathDirty = true;

    this._boundsUpdated();
  };

  prototypeAccessors.hitStrokeWidth.get = function hitStrokeWidth() {
    return this._hitStrokeWidth;
  };

  prototypeAccessors.hitStrokeWidth.set = function hitStrokeWidth(value) {
    if (this._hitStrokeWidth === value) {
      return;
    }

    this._hitStrokeWidth = value;

    this._visualUnrelatedUpdate();
  };

  SvgGraphics.prototype.generateRawRenderData = function generateRawRenderData(canvas) {
    FillStrokeAble.prototype.generateRawRenderData.call(this, canvas);
    var ref = this.worldTransform;
    var a = ref.a;
    var b = ref.b;
    var c = ref.c;
    var d = ref.d;
    var tx = ref.tx;
    var ty = ref.ty;
    return {
      type: "SvgGraphics",
      transform: {
        a: a,
        b: b,
        c: c,
        d: d,
        tx: tx,
        ty: ty
      },
      segments: this.segments,
      fillStyle: this.fill.generateRawRenderData(this.initialBounds),
      strokeStyle: this.strokeStyle.fill.generateRawRenderData(this.strokeBounds),
      lineWidth: this.strokeStyle.width,
      lineCap: this.strokeStyle.lineCap,
      lineJoin: this.strokeStyle.lineJoin,
      lineDash: this.strokeStyle.dash,
      lineDashOffset: this.strokeStyle.dashOffset,
      miterLimit: this.strokeStyle.miterLimit
    };
  };

  SvgGraphics.prototype.draw = function draw(canvas) {
    this.applyPath(canvas);

    if (!this.fill.isEmpty()) {
      if (this.fill instanceof ImageFill) {
        this.fill.renderWithClip(canvas, this.initialBounds);
      } else if (this.fill instanceof CustomGradientFill) {
        this.fill.renderWithClip(canvas, this);
      } else {
        canvas.save();
        this.fill.applyTo(canvas, this.initialBounds);
        this.fill.applyTransformTo(canvas, this.initialBounds);
        canvas.fill();
        canvas.restore();
      }

      if (this.fillFilters.length > 0) {
        var canvasTranslateX = canvas.contextTranslate.x;
        var canvasTranslateY = canvas.contextTranslate.y;
        canvas.contextTranslate.x = 0;
        canvas.contextTranslate.y = 0;
        this.applyFilters(canvas, this.fillFilters);
        canvas.contextTranslate.x = canvasTranslateX;
        canvas.contextTranslate.y = canvasTranslateY;
      }
    }

    if (!this.strokeStyle.isEmpty()) {
      canvas.save();
      this.strokeStyle.applyTo(canvas);
      this.stroke.applyToStroke(canvas, this.strokeBounds);
      this.stroke.applyTransformTo(canvas, this.strokeBounds);
      canvas.stroke();
      canvas.restore();
    }
  };

  SvgGraphics.prototype.drawHit = function drawHit(canvas) {
    FillStrokeAble.prototype.drawHit.call(this, canvas);

    if (!this.colorKey) {
      return;
    }

    if (this.pointerEvents === PointerEvents.NONE) {
      return;
    }

    this.applyPath(canvas);

    if (this.fill !== Fill.None && this.pointerEvents === PointerEvents.AUTO) {
      canvas.nativeFillStyle = Color.fromHex(this.colorKey).rgba;
      canvas.fill();
    }

    var hitStrokeWidth = this.hitStrokeWidth !== null ? this.hitStrokeWidth : this.stroke !== Fill.None ? this.strokeWidth : 0;

    if (hitStrokeWidth > 0 && this.pointerEvents === PointerEvents.AUTO) {
      this.strokeStyle.applyTo(canvas);
      canvas.lineWidth = hitStrokeWidth;
      canvas.nativeStrokeStyle = Color.fromHex(this.colorKey).rgba;
      canvas.stroke();
    }
  };

  SvgGraphics.prototype.applyPath = function applyPath(canvas) {
    drawPath(canvas, this.segments);
  };

  SvgGraphics.prototype.updateInitialBounds = function updateInitialBounds(initialBounds) {
    PathBoundsCalculator.calcBounds(this.segments, initialBounds);
  };

  SvgGraphics.prototype.updateStrokeBounds = function updateStrokeBounds(strokeBounds) {
    PathBoundsCalculator.calcStrokeBounds(this.segments, this.strokeStyle, strokeBounds);
  };

  Object.defineProperties(SvgGraphics.prototype, prototypeAccessors);
  return SvgGraphics;
}(FillStrokeAble);
injectInspector(SvgGraphics, ["path", "hitStrokeWidth"]);

var TextStyle = /*@__PURE__*/function (Versioning) {
  function TextStyle() {
    Versioning.call(this);
    this.underline = new StrokeStyle();
    this.lineThrough = new StrokeStyle();
    this._fontFamily = "Arial";
    this._fontSize = 16;
    this._fontWeight = FontWeight.Normal;
    this._fontStyle = FontStyle.Normal;
    this._textBaseline = TextBaseline.Alphabetic;

    this.underline._addUpdateListener(this._updated);

    this.lineThrough._addUpdateListener(this._updated);
  }

  if (Versioning) TextStyle.__proto__ = Versioning;
  TextStyle.prototype = /*#__PURE__*/Object.create(Versioning && Versioning.prototype);
  TextStyle.prototype.constructor = TextStyle;
  var prototypeAccessors = {
    fontFamily: {
      configurable: true
    },
    fontSize: {
      configurable: true
    },
    fontWeight: {
      configurable: true
    },
    fontStyle: {
      configurable: true
    },
    textBaseline: {
      configurable: true
    },
    font: {
      configurable: true
    }
  };

  prototypeAccessors.fontFamily.get = function fontFamily() {
    return this._fontFamily;
  };

  prototypeAccessors.fontFamily.set = function fontFamily(value) {
    if (this._fontFamily === value) {
      return;
    }

    this._fontFamily = value;

    this._updated();
  };

  prototypeAccessors.fontSize.get = function fontSize() {
    return this._fontSize;
  };

  prototypeAccessors.fontSize.set = function fontSize(value) {
    if (this._fontSize === value) {
      return;
    }

    this._fontSize = value;

    this._updated();
  };

  prototypeAccessors.fontWeight.get = function fontWeight() {
    return this._fontWeight;
  };

  prototypeAccessors.fontWeight.set = function fontWeight(value) {
    if (this._fontWeight === value) {
      return;
    }

    this._fontWeight = value;

    this._updated();
  };

  prototypeAccessors.fontStyle.get = function fontStyle() {
    return this._fontStyle;
  };

  prototypeAccessors.fontStyle.set = function fontStyle(value) {
    if (this._fontStyle === value) {
      return;
    }

    this._fontStyle = value;

    this._updated();
  };

  prototypeAccessors.textBaseline.get = function textBaseline() {
    return this._textBaseline;
  };

  prototypeAccessors.textBaseline.set = function textBaseline(value) {
    if (this._textBaseline === value) {
      return;
    }

    this._textBaseline = value;

    this._updated();
  };

  prototypeAccessors.font.get = function font() {
    return this.fontStyle + " " + this.fontWeight + " " + this.fontSize + "px " + this.fontFamily;
  };

  Object.defineProperties(TextStyle.prototype, prototypeAccessors);
  return TextStyle;
}(Versioning);
var FontWeight = /* @__PURE__ */function (FontWeight2) {
  FontWeight2["Normal"] = "normal";
  FontWeight2["Bold"] = "bold";
  FontWeight2["Bolder"] = "bolder";
  FontWeight2["Lighter"] = "lighter";
  FontWeight2["W100"] = "100";
  FontWeight2["W200"] = "200";
  FontWeight2["W300"] = "300";
  FontWeight2["W400"] = "400";
  FontWeight2["W500"] = "500";
  FontWeight2["W600"] = "600";
  FontWeight2["W700"] = "700";
  FontWeight2["W800"] = "800";
  FontWeight2["W900"] = "900";
  return FontWeight2;
}(FontWeight || {});
var FontStyle = /* @__PURE__ */function (FontStyle2) {
  FontStyle2["Normal"] = "normal";
  FontStyle2["Italic"] = "italic";
  FontStyle2["Oblique"] = "oblique";
  return FontStyle2;
}(FontStyle || {});
var TextBaseline = /* @__PURE__ */function (TextBaseline2) {
  TextBaseline2["Alphabetic"] = "alphabetic";
  TextBaseline2["Top"] = "top";
  TextBaseline2["Hanging"] = "hanging";
  TextBaseline2["Middle"] = "middle";
  TextBaseline2["Ideographic"] = "ideographic";
  TextBaseline2["Bottom"] = "bottom";
  return TextBaseline2;
}(TextBaseline || {});
injectInspector(TextStyle, ["fontFamily", "fontSize", "fontWeight", "fontStyle", "textBaseline"]);

var Text = /*@__PURE__*/function (FillStrokeAble) {
  function Text(content) {
    var this$1$1 = this;
    if (content === void 0) content = "";
    FillStrokeAble.call(this);
    this.textStyle = new TextStyle();
    this.style = new TextStyleProxy(this.textStyle);
    this._content = content;
    this.fill = new ColorFill(Color.Black);

    this.textStyle._addUpdateListener(function () {
      return this$1$1._boundsUpdated();
    });
  }

  if (FillStrokeAble) Text.__proto__ = FillStrokeAble;
  Text.prototype = /*#__PURE__*/Object.create(FillStrokeAble && FillStrokeAble.prototype);
  Text.prototype.constructor = Text;
  var prototypeAccessors = {
    content: {
      configurable: true
    },
    fontFamily: {
      configurable: true
    },
    fontSize: {
      configurable: true
    },
    fontWeight: {
      configurable: true
    },
    fontStyle: {
      configurable: true
    },
    textBaseline: {
      configurable: true
    },
    underline: {
      configurable: true
    },
    lineThrough: {
      configurable: true
    }
  };

  prototypeAccessors.content.get = function content() {
    return this._content;
  };

  prototypeAccessors.content.set = function content(value) {
    if (this._content === value) {
      return;
    }

    this._content = value;

    this._boundsUpdated();
  };

  prototypeAccessors.fontFamily.get = function fontFamily() {
    return this.textStyle.fontFamily;
  };

  prototypeAccessors.fontFamily.set = function fontFamily(value) {
    this.textStyle.fontFamily = value;
  };

  prototypeAccessors.fontSize.get = function fontSize() {
    return this.textStyle.fontSize;
  };

  prototypeAccessors.fontSize.set = function fontSize(value) {
    this.textStyle.fontSize = value;
  };

  prototypeAccessors.fontWeight.get = function fontWeight() {
    return this.textStyle.fontWeight;
  };

  prototypeAccessors.fontWeight.set = function fontWeight(value) {
    this.textStyle.fontWeight = value;
  };

  prototypeAccessors.fontStyle.get = function fontStyle() {
    return this.textStyle.fontStyle;
  };

  prototypeAccessors.fontStyle.set = function fontStyle(value) {
    this.textStyle.fontStyle = value;
  };

  prototypeAccessors.textBaseline.get = function textBaseline() {
    return this.textStyle.textBaseline;
  };

  prototypeAccessors.textBaseline.set = function textBaseline(value) {
    this.textStyle.textBaseline = value;
  };

  prototypeAccessors.underline.get = function underline() {
    return this.textStyle.underline;
  };

  prototypeAccessors.lineThrough.get = function lineThrough() {
    return this.textStyle.lineThrough;
  };

  Text.prototype.generateRawRenderData = function generateRawRenderData(canvas) {
    FillStrokeAble.prototype.generateRawRenderData.call(this, canvas);
    var ref = this.worldTransform;
    var a = ref.a;
    var b = ref.b;
    var c = ref.c;
    var d = ref.d;
    var tx = ref.tx;
    var ty = ref.ty;
    var data = {
      type: "Text",
      transform: {
        a: a,
        b: b,
        c: c,
        d: d,
        tx: tx,
        ty: ty
      },
      fillStyle: this.fill.generateRawRenderData(this.initialBounds),
      strokeStyle: this.strokeStyle.fill.generateRawRenderData(this.strokeBounds),
      content: this.content,
      font: this.textStyle.font,
      textBaseline: this.textBaseline,
      lineWidth: this.strokeStyle.width,
      lineCap: this.strokeStyle.lineCap,
      lineJoin: this.strokeStyle.lineJoin,
      lineDash: this.strokeStyle.dash,
      lineDashOffset: this.strokeStyle.dashOffset,
      miterLimit: this.strokeStyle.miterLimit
    };
    return data;
  };

  Text.prototype.draw = function draw(canvas) {
    canvas.font = this.textStyle.font;
    canvas.textBaseline = this.textBaseline;

    if (!this.fill.isEmpty()) {
      this.fill.applyTo(canvas, this.initialBounds);
      canvas.fillText(this.content, 0, 0);

      if (this.fillFilters.length > 0) {
        var canvasTranslateX = canvas.contextTranslate.x;
        var canvasTranslateY = canvas.contextTranslate.y;
        canvas.contextTranslate.x = 0;
        canvas.contextTranslate.y = 0;
        this.applyFilters(canvas, this.fillFilters);
        canvas.contextTranslate.x = canvasTranslateX;
        canvas.contextTranslate.y = canvasTranslateY;
      }
    }

    if (!this.strokeStyle.isEmpty()) {
      this.strokeStyle.applyTo(canvas);
      this.stroke.applyToStroke(canvas, this.strokeBounds);
      canvas.strokeText(this.content, 0, 0);
    }

    this.drawUnderline(canvas);
    this.drawLineThrough(canvas);
  };

  Text.prototype._drawToBuffer = function _drawToBuffer(canvas, bounds) {
    var textBounds = new Bounds().addBounds(bounds);

    if (this.fontStyle !== FontStyle.Normal) {
      textBounds.pad(new Padding(0, this.fontSize, 0, 0));
    }

    return FillStrokeAble.prototype._drawToBuffer.call(this, canvas, textBounds);
  };

  Text.prototype.drawHit = function drawHit(canvas) {
    FillStrokeAble.prototype.drawHit.call(this, canvas);

    if (!this.colorKey) {
      return;
    }

    if (this.pointerEvents === PointerEvents.NONE) {
      return;
    }

    if (!(this.fill.isEmpty() && this.pointerEvents === PointerEvents.AUTO)) {
      canvas.nativeFillStyle = Color.fromHex(this.colorKey).rgba;
    }

    var textDecorationDim = 0;
    var y = this.textBaseline === TextBaseline.Alphabetic ? this.fontSize / 3 - this.initialBounds.height : this.fontSize * (textDecorationDim + TextBaselineDim[this.textBaseline]) * -0.5 - this.fontSize / 4;
    canvas.beginPath();
    canvas.rect(0, y, this.initialBounds.width, this.initialBounds.height);
    canvas.fill();
  };

  Text.prototype.updateInitialBounds = function updateInitialBounds(initialBounds) {
    var ref = this.app;
    var view = ref.view;
    view.font = this.textStyle.font;
    var ref$1 = view.measureText(this.content);
    var width = ref$1.width;
    var height = this.fontSize * 1.4;
    initialBounds.clear().addFrame(0, 0, width, height);
  };

  Text.prototype.drawUnderline = function drawUnderline(canvas) {
    if (this.underline.width === 0) {
      return;
    }

    var fill = this.underline.fill === Fill.None ? this.fill : this.underline.fill;
    fill.applyToStroke(canvas, this.initialBounds);
    var textDecorationDim = -2;
    var y = this.textBaseline === TextBaseline.Alphabetic ? this.initialBounds.height / 4 : this.fontSize * (textDecorationDim + TextBaselineDim[this.textBaseline]) * -0.5;
    canvas.drawLine(this.initialBounds.width, y, this.underline.width);
  };

  Text.prototype.drawLineThrough = function drawLineThrough(canvas) {
    if (this.textStyle.lineThrough.width === 0) {
      return;
    }

    var fill = this.lineThrough.fill === Fill.None ? this.fill : this.lineThrough.fill;
    fill.applyToStroke(canvas, this.initialBounds);
    var textDecorationDim = -1;
    var y = this.textBaseline === TextBaseline.Alphabetic ? -this.fontSize / 3 : this.fontSize * (textDecorationDim + TextBaselineDim[this.textBaseline]) * -0.5;
    canvas.drawLine(this.initialBounds.width, y, this.lineThrough.width);
  };

  Object.defineProperties(Text.prototype, prototypeAccessors);
  return Text;
}(FillStrokeAble);
var PointText = /*@__PURE__*/function (Text) {
  function PointText() {
    Text.apply(this, arguments);
  }

  if (Text) PointText.__proto__ = Text;
  PointText.prototype = /*#__PURE__*/Object.create(Text && Text.prototype);
  PointText.prototype.constructor = PointText;
  return PointText;
}(Text);
var TextBaselineDim = {};
TextBaselineDim[TextBaseline.Top] = 0;
TextBaselineDim[TextBaseline.Hanging] = 0;
TextBaselineDim[TextBaseline.Middle] = 1;
TextBaselineDim[TextBaseline.Ideographic] = 2;
TextBaselineDim[TextBaseline.Bottom] = 2;
injectInspector(Text, [["style", false], "content", "textStyle"]);

export { AlphaRendering, Application, BaseImageFill, BlurFilter, Bounds, BoundsCalculation, CURVETIME_EPSILON, Cache, CacheRendering, Canvas, CanvasPoolSingleton, Color, ColorFill, ColorMatrixFilter, ColorStop, Component, Container, Convolution3x3Filter, CustomGradientFill, DEG_TO_RAD, DisplayObject, EPSILON, E_OPERATION_TYPE, EmptyFill, Fill, FillOverlayFilter, FillStrokeAble, Filter, FontStyle, FontWeight, GlBuffer, GradientFill, Image, ImageFill, ImageResource, InteractionEvent, InteractionManager, LineCap, LineJoin, LinearGradientFill, Loader, MACHINE_EPSILON, Matrix, MorphologyFilter, MorphologyMode, PI, PI_2, Padding, PathParser, Point, PointText, PointerEvents, RAD_TO_DEG, RadialGradientFill, RawImageFill, RawLinearGradientFill, RawRadialGradientFill, Resource, StrokeStyle, SvgGraphics, Target, Text, TextBaseline, TextStyle, TilingImageFill, TilingMirror, TilingRepeat, Transform, Versioning, WorldTransform, clamp, dep, drawPath, getBlurFilter, getColorMatrixFilter, getConstructor, getDefaults, getMorphologyFilter, glBlurFilter, glColorMatrixFilter, glConvolution3x3Filter, glMorphologyFilter, injectInspector, inspect, isMachineZero, noop };
//# sourceMappingURL=alloy-renderer.esm.js.map
